import { DOM } from './state.js';
import {
    signInStaff,
    signOut,
    getCurrentSession,
    getStaffRole,
    isStaff,
    onAuthChange,
} from './services/auth.service.js';
import {
    loadUpcoming,
    loadArchived,
    transition,
    createReservation,
    editReservation,
} from './services/admin-reservations.service.js';
import { loadBlocks, createBlock, deleteBlock } from './services/admin-blocks.service.js';
import { loadPubli, togglePubli } from './services/admin-publi.service.js';
import {
    loadEmpleados,
    createEmpleado,
    updateEmpleado,
    setEmpleadoPin,
    loadFudoDirectory,
    invalidateFudoDirectoryCache,
} from './services/admin-empleados.service.js';
import {
    loadFichadas,
    crearFichada,
    editarFichada,
    cerrarFichada,
    anularFichada,
    loadJornadasAbiertasOlvidadas,
} from './services/admin-fichadas.service.js';
import { previewLiquidacion, confirmLiquidacion, anularLiquidacion, loadLiquidacionesHistorial } from './services/admin-liquidacion.service.js';
import { loadPayrollConfig, savePayrollConfig, savePeriodoPorDefecto } from './services/admin-payroll-config.service.js';
import {
    crearAdelanto,
    anularAdelanto,
    loadAdelantos,
    crearOtroDescuento,
    anularOtroDescuento,
    loadOtrosDescuentos,
} from './services/admin-adelantos.service.js';
import { registrarPago, anularPago, loadPagos } from './services/admin-pagos.service.js';
import {
    loadFeriados,
    crearFeriado,
    eliminarFeriado,
    GENERIC_ERROR_MESSAGE as TABLERO_CONFIG_GENERIC_ERROR_MSG,
} from './services/admin-feriados.service.js';
import { mesARango, mesLiquidacionPorDefecto } from './shared/liquidacion-ui-policy.js';
import {
    PERIODO_KINDS,
    rangoDelPeriodo,
    rangoPersonalizado,
    periodoPorDefecto,
    periodoSiguiente,
    periodoAnterior,
    esMesCompleto,
    diasDelPeriodo,
} from './shared/liquidacion-periodo-policy.js';
import { mesSiguiente as feriadoMesSiguiente, mesAnterior as feriadoMesAnterior } from './shared/feriados-ui-policy.js';
import { artDateKey, artMidnightUtcIso, periodoTerminado } from './shared/timesheet-policy.js';
import { panelForPath } from './shared/panel-routes.js';
// S2b follow-up c (review-5a2ac077e1b2c3d4): the pure roster/worked-time
// functions moved to `js/shared/liquidacion-tablero-policy.js` — this file
// no longer imports `detectAnomalies`/`splitMinutesByDate` directly.
import {
    TABLERO_ERROR_KINDS,
    ventanaLecturaFichadas,
    splitTableroRoster,
    applyPreviewToFilas,
    computeTableroTotales,
    anomaliasDeEmpleado,
    loteLiquidable,
    adelantosActivosPorEmpleado,
} from './shared/liquidacion-tablero-policy.js';
import { construirComprobante } from './shared/liquidacion-comprobante-policy.js';
import { estadoPago, pagosPreviosDeAnuladas, textoAvisoPagoPrevio } from './shared/liquidacion-pagos-policy.js';
import { construirFilas, serializarCsv, conBom, nombreArchivoExport } from './shared/liquidacion-export-policy.js';
import { downloadTextFile } from './infrastructure/browser-download.js';
import { LoginForm } from './components/admin/LoginForm.js';
import { AdminAccessDenied } from './components/admin/AdminAccessDenied.js';
import { AdminShell, TAB_ACTIVE_CLASSES, TAB_INACTIVE_CLASSES } from './components/admin/AdminShell.js';
import { ReservationsView } from './components/admin/ReservationsView.js';
import { EmpleadosView } from './components/admin/EmpleadosView.js';
import { FichadasView } from './components/admin/FichadasView.js';
import { JornadasAbiertasBanner } from './components/admin/JornadasAbiertasBanner.js';
import { LiquidacionView } from './components/admin/LiquidacionView.js';
import { LiquidacionTableroView } from './components/admin/LiquidacionTableroView.js';
import { DrawerShell } from './components/admin/DrawerShell.js';
import { AdelantoDrawerView } from './components/admin/AdelantoDrawerView.js';
import { OtrosDescuentosDrawerView } from './components/admin/OtrosDescuentosDrawerView.js';
import { AnomaliasDrawerView } from './components/admin/AnomaliasDrawerView.js';
import { PagoDrawerView } from './components/admin/PagoDrawerView.js';
import { ConfiguracionLiquidacionView } from './components/admin/ConfiguracionLiquidacionView.js';
import { AnularLiquidacionDialog } from './components/admin/AnularLiquidacionDialog.js';
import { ComprobanteLiquidacionView } from './components/admin/ComprobanteLiquidacionView.js';
import { NuevaReservaModal } from './components/admin/NuevaReservaModal.js';
import { escapeHTML } from './utils/escape.js';
import { formatMonto, formatPrice } from './utils/formatters.js';
import { MesaMapa } from './components/admin/MesaMapa.js';
import { applySeleccion } from './shared/mesas-policy.js';
import {
    loadFloorPlan,
    guardarAsignacion,
    sincronizarMesas,
} from './services/admin-mesas.service.js';
import { horaOptions, momentoOptions } from './components/admin/NuevaReservaForm.js';
import { mountFichajeSection, stopFichaje } from './kiosco.js';
import {
    mountFaqStaffSection,
    stopFaqStaff,
    mountFaqAdminSection,
    stopFaqAdmin,
    mountFaqBusquedasSection,
    stopFaqBusquedas,
} from './faq.js';
import {
    mountReportesStaffSection,
    stopReportesStaff,
    mountReportesAdminSection,
    stopReportesAdmin,
    refreshReportesBadge,
} from './reportes.js';
import { mountGiftcardsStaffSection, stopGiftcardsStaff, mountGiftcardsAdminSection, stopGiftcardsAdmin } from './giftcards.js';
import { BlocksSection } from './components/admin/BlocksSection.js';
import { PubliToggle } from './components/admin/PubliToggle.js';
import { CategoriesView } from './components/admin/CategoriesView.js';
import { ConfirmDialog } from './components/admin/ConfirmDialog.js';
import { showToast } from './components/shared/Toast.js';
import { hideDecorativeIcons } from './utils/aria.js';
import { FudoGpRepository } from './infrastructure/fudo-gp.repository.js';
import { fetchCategoryRules, upsertCategoryRule } from './infrastructure/category-rules.repository.js';

/**
 * Módulo de los paneles de staff (/staff) y admin (/admin). Gatea el acceso: solo staff logueado ve el
 * shell; el resto (no-staff o sin sesión) ve el formulario de login.
 *
 * Sigue el patrón de reservas.js/giftcards.js: render hacia un contenedor +
 * listeners cableados por delegación local. La vista de reservas (Fase 1,
 * slice 2) se monta dentro del shell y se re-renderiza de forma parcial
 * (solo su mount node) en cada carga/mutación, sin tocar el resto del shell.
 */

/** Copy reutilizada cuando no pudimos resolver el rol (fallo transitorio). */
const VERIFY_ERROR_MSG = 'No pudimos verificar tu acceso. Probá de nuevo.';

/** Copy Spanish de la vista de reservas (Req: Staff Status Transition Actions, Loading/Empty/Error States). */
const RESERVA_MUTATION_ERROR_MSG = 'No pudimos actualizar la reserva. Probá de nuevo.';
const RESERVA_STALE_MSG = 'La reserva ya fue modificada.';

/** Copy Spanish del alta manual de reservas ("Nueva reserva"). */
/** Copy Spanish del plano de mesas (asignación de mesas a una reserva). */
const MESAS_LOAD_ERROR_MSG = 'No pudimos cargar el plano de mesas.';
const RESERVA_EDIT_SUCCESS_MSG = 'Reserva actualizada.';
const RESERVA_EDIT_ERROR_MSG = 'No pudimos guardar los cambios. Probá de nuevo.';

const RESERVA_CREATE_SUCCESS_MSG = 'Reserva creada.';
const RESERVA_CREATE_ERROR_MSG = 'No pudimos crear la reserva. Probá de nuevo.';
/** Teléfono es obligatorio SOLO en el alta manual de staff (el flujo público sigue sin exigirlo). */
const RESERVA_TELEFONO_REQUIRED_MSG = 'El teléfono es obligatorio.';
/** En el alta manual TODOS los campos son obligatorios (guard presentacional). */
const RESERVA_ALL_FIELDS_REQUIRED_MSG = 'Completá todos los campos.';

/**
 * Mapea el `data-estado` de un botón segmentado a la acción de transición del
 * service (`ACTIONS`). Los 3 botones de estado + el botón archivar son las
 * únicas transiciones ofrecidas por fila.
 */
const RESERVA_ESTADO_ACTIONS = {
    confirmada: 'confirmar',
    cancelada: 'cancelar',
    terminada: 'terminar',
};

/** Toast de éxito per-acción, mostrado cuando `transition` resuelve `{ changed: true }`. */
const RESERVA_SUCCESS_MESSAGES = {
    confirmar: 'Reserva confirmada.',
    cancelar: 'Reserva cancelada.',
    terminar: 'Reserva marcada como terminada.',
    archivar: 'Reserva archivada.',
};

/** Copy Spanish de la sección de bloqueos. */
const BLOCK_CREATE_SUCCESS_MSG = 'Bloqueo creado.';
const BLOCK_CREATE_ERROR_MSG = 'No pudimos crear el bloqueo. Probá de nuevo.';
const BLOCK_DELETE_SUCCESS_MSG = 'Bloqueo eliminado.';
const BLOCK_DELETE_ERROR_MSG = 'No pudimos eliminar el bloqueo. Probá de nuevo.';
const BLOCKS_LOAD_ERROR_MSG = 'No pudimos cargar los bloqueos. Probá de nuevo.';

/** Copy Spanish de la sección de configuración (publi). */
const PUBLI_LOAD_ERROR_MSG = 'No pudimos cargar la configuración. Probá de nuevo.';
const PUBLI_TOGGLE_ERROR_MSG = 'No pudimos actualizar la publicidad. Probá de nuevo.';

/** Copy Spanish de la sección de Empleados (Control de Horarios, slice 1). */
const EMPLEADO_SAVE_SUCCESS_MSG = 'Empleado guardado.';
const EMPLEADO_SAVE_ERROR_MSG = 'No pudimos guardar el empleado. Probá de nuevo.';
const EMPLEADO_PIN_SUCCESS_MSG = 'PIN actualizado.';
const EMPLEADO_PIN_ERROR_MSG = 'No pudimos guardar el PIN. Probá de nuevo.';

/** Copy Spanish de la sección de Fichadas (Control de Horarios, slice 3 — Auditoría). */
const FICHADA_CREAR_SUCCESS_MSG = 'Fichada creada.';
const FICHADA_GUARDAR_SUCCESS_MSG = 'Fichada actualizada.';
const FICHADA_ANULAR_SUCCESS_MSG = 'Fichada anulada.';
/** Fallback SOLO para un fallo de transporte (excepción); el rechazo de negocio ya trae su propio `result.error` en español. */
const FICHADA_GENERIC_ERROR_MSG = 'No pudimos guardar la fichada. Probá de nuevo.';

/** Copy Spanish de la sección de Liquidación (Control de Horarios, slice 5). */
const LIQUIDACION_GENERIC_ERROR_MSG = 'No pudimos calcular la liquidación. Probá de nuevo.';

/**
 * Una excepción lanzada mientras se confirma o se anula (no un resultado del
 * servicio) es tan ambigua como un corte de transporte: la escritura pudo
 * haberse aplicado. Se trata como `incierto`, nunca como "Probá de nuevo".
 */
const LIQUIDACION_INCIERTO_EXCEPCION_MSG = 'Ocurrió un error inesperado. No sabemos si se aplicó — revisá el historial antes de reintentar.';
/** A drawer monto the browser could not parse (`validity.badInput`, e.g. "5.000,50"), see `leerBorradorForm`. */
const MONTO_FORMATO_INVALIDO_MSG = 'El monto no tiene un formato válido: usá solo números y punto para los decimales (por ejemplo 5000.50). Volvé a cargarlo.';
/**
 * S8: reusado por `handleTableroExportarClick` ("Exportar CSV", mudado desde
 * el toolbar legacy) — un mes vacío/malformado nunca llega a
 * `loadLiquidacionesHistorial`. `mesARango` (js/shared/liquidacion-ui-policy.js)
 * returns `null` for anything malformed.
 */
const LIQUIDACION_MES_INVALIDO_MSG = 'Elegí un mes válido.';
/**
 * Unit B2 (B2.7): antes de este change esto decía "no existe un flujo para
 * anularla ni editarla después" — ya NO es cierto (Unit B1/B2 agregan
 * exactamente ese flujo). El copy actualizado no promete que la liquidación
 * es indeshacible, pero SIGUE siendo honesto sobre lo que de verdad no
 * cambia: los montos nunca se pueden editar (AD2, la CHECK/trigger de
 * migración 27 sólo permite `estado` `confirmada -> anulada` con todo lo
 * demás byte-idéntico) y una anulación queda registrada de forma
 * PERMANENTE (terminal, design.md Q5 — no existe `anulada -> confirmada`).
 */
const LIQUIDACION_CONFIRM_IRREVERSIBLE_MSG =
    'Los montos de esta liquidación nunca se pueden editar después de confirmar. Sólo se puede anular con un motivo, y esa anulación queda registrada de forma permanente. ¿Confirmar la liquidación';
/** Unit B2 — anulación auditada de una liquidación confirmada (design.md AD3/AD12). */
const LIQUIDACION_ANULAR_SUCCESS_MSG = 'Liquidación anulada.';

/** Copy Spanish del editor del tope mensual de descuento (E1, `payroll_config`). */
// F3 (review-050ef9b16341fee9): el mensaje de éxito ahora dice explícitamente
// el efecto retroactivo — bajar el tope cambia liquidaciones del mes en
// curso todavía no confirmadas, no solo las futuras.
const LIQUIDACION_CAP_SAVE_SUCCESS_MSG = 'Tope mensual guardado. Ya afecta las liquidaciones no confirmadas del mes en curso.';
const LIQUIDACION_CAP_SAVE_ERROR_MSG = 'No pudimos guardar el tope. Probá de nuevo.';

/**
 * F1 (review-13e17d2352297ac9): sin acotar, `listLiquidaciones` traía la
 * tabla `liquidaciones` COMPLETA en cada carga del tab y después de cada
 * confirm — crece sin límite a medida que se acumulan liquidaciones. La
 * carga del historial se acota a los períodos que ARRANCAN en los últimos
 * `LIQUIDACION_HISTORIAL_MESES` meses (`liquidacionHistorialRangoDefault`).
 * El panel muestra de a `LIQUIDACION_HISTORIAL_PAGINA` filas; las más
 * viejas que la ventana se leen a pedido, de a una página, con "Ver más"
 * (L8, `handleLiquidacionHistorialVerMas`).
 */
const LIQUIDACION_HISTORIAL_MESES = 6;

/**
 * L8 — tamaño de página del historial: filas que se muestran de entrada, y
 * cuántas más suma cada "Ver más" (primero de lo ya cargado; agotado eso, una
 * página de liquidaciones más viejas que la ventana, leída con
 * `offset`/`limit`). ÚNICO dueño de este número: `LiquidacionView` lo recibe
 * (`historialFilasMostradas`), no tiene uno propio.
 */
const LIQUIDACION_HISTORIAL_PAGINA = 20;

/** L8 — filas del historial que se muestran hoy (crece de a `LIQUIDACION_HISTORIAL_PAGINA`). */
let liquidacionHistorialFilasMostradas = LIQUIDACION_HISTORIAL_PAGINA;

/**
 * L8 — estado de la paginación de liquidaciones MÁS VIEJAS que la ventana:
 * `inicioAntesDe` (R3: el inicio de la ventana, CONGELADO cuando se cargó
 * el historial — nunca recalculado por click, así un cambio de mes con el
 * panel abierto no corre la frontera), cuántas se leyeron (`offset` de la
 * próxima página), si puede haber más, si hay una lectura en vuelo y el
 * aviso de la última falla.
 */
let liquidacionHistorialViejas = { inicioAntesDe: null, cargadas: 0, hayMas: true, cargando: false, error: null };

/**
 * R3 — `true` mientras `refreshLiquidacionHistorial` está en vuelo: "Ver más"
 * no arranca (y se muestra deshabilitado) hasta que la recarga termine, así
 * una página vieja nunca se cuelga de filas que la recarga va a reemplazar.
 */
let liquidacionHistorialRecargando = false;

/**
 * R1 — ids de filas del historial cuyos pagos NO se pudieron leer (la
 * lectura de pagos de una página de "Ver más" falló o no respondió). Esas
 * filas no muestran la pill — "Pendiente" sobre una liquidación quizás ya
 * pagada invita a pagarla dos veces — sino "No pudimos cargar los pagos.".
 */
let liquidacionHistorialPagosDesconocidos = new Set();

/** L8 — descarta una página de "Ver más" que llega después de un remonte o de una recarga del historial. */
let liquidacionHistorialVerMasSeq = 0;

/** Guard de re-entrancy: evita dos handleLogin concurrentes (doble submit). */
let loginInFlight = false;

/** Suscripción activa a cambios de auth; se limpia antes de re-suscribir. */
let authSubscription = null;

/**
 * Contador de secuencia de `renderAdmin` (D1 follow-up, review-ea4f2a93a19e6939
 * finding 5): dos llamados solapados con panel distinto (ej. `renderAdmin('admin')`
 * seguido de `renderAdmin('staff')` antes de que el primero resuelva
 * sesión/rol) pueden resolver fuera de orden. Cada llamado captura el valor
 * vigente al entrar; si al volver de un `await` el contador ya avanzó (otro
 * llamado más nuevo empezó), el llamado viejo corta ANTES de escribir DOM o
 * estado — mismo patrón que `refreshEmpleadosSeq`/`refreshFichadasSeq` para
 * cargas de sección, aplicado acá al render del shell entero.
 */
let renderSeq = 0;

/**
 * S5 (acceptance run): a section list load that never settles (a hung
 * Supabase request) used to leave the section on "Cargando..." forever. The
 * section loaders race their read against this limit and fall into their
 * existing error state, which already offers "Reintentar". Same value as
 * `TOPE_RESPUESTA_MS` in `js/services/admin-fichadas.service.js` (the fichada
 * mutations' limit), which cannot import from this entry module.
 */
const SECTION_LOAD_TIMEOUT_MS = 15_000;

/**
 * F2: limit for the Fudo GP categories read. Above `safeFetchClient`'s
 * header-phase budget (8 s per attempt, up to 4 attempts plus backoff, about
 * 33 s) so it never cuts those retries short, but finite: that budget ends
 * when the headers arrive, and a body that stalls afterwards
 * (`response.json()` never settles) would otherwise hang the section.
 */
const FUDO_LOAD_TIMEOUT_MS = 40_000;

/**
 * Rejects with a `SectionLoadTimeoutError` if `promise` has not settled after
 * `ms`. The underlying request is not aborted (the Supabase reads take no
 * signal here); its late answer is simply dropped, the race already settled.
 *
 * With the default limit it wraps the Supabase-backed section reads, which
 * have no timeout of their own: Reservas (`loadUpcoming`/`loadArchived`),
 * Bloqueos (`loadBlocks`), Empleados (`loadEmpleados`), Fichadas
 * (`loadFichadas` + `loadEmpleados`), the open-shift banner
 * (`loadJornadasAbiertasOlvidadas`, also awaited by the Liquidación
 * tablero), the Liquidación historial "Ver más" page and its payments
 * (`loadLiquidacionesHistorial` + `loadPagos`), Publi (`loadPubli`) and the
 * Categorías rules (`fetchCategoryRules`). The Fudo-backed Categorías list
 * (`FudoGpRepository.getCategories`) is wrapped with the longer
 * `FUDO_LOAD_TIMEOUT_MS` (40 s) instead: `safeFetchClient`'s own timeout +
 * retry budget (8 s per attempt, up to 4 attempts plus backoff, about 33 s)
 * is longer than the default limit, so the default would cut it short and
 * leave its retries running behind a "Reintentar" that starts yet another
 * request; but that budget stops at the headers, so a stalled body still
 * needs this outer limit.
 * @template T
 * @param {Promise<T>} promise
 * @param {number} [ms]
 * @returns {Promise<T>}
 */
function conTimeoutDeCarga(promise, ms = SECTION_LOAD_TIMEOUT_MS) {
    let timer;
    const limite = new Promise((_, reject) => {
        timer = setTimeout(() => {
            const err = new Error(`La carga no respondió en ${ms} ms.`);
            err.name = 'SectionLoadTimeoutError';
            reject(err);
        }, ms);
    });
    return Promise.race([promise, limite]).finally(() => clearTimeout(timer));
}

/**
 * Sección activa del shell (`reservas` | `config`). Cada render fresco del
 * shell (login exitoso, re-render por SIGNED_OUT que vuelve a shell) resetea a
 * `reservas` para no arrastrar la sección de una sesión anterior. Bloqueos ya
 * no es una sección propia: vive dentro de Reservas.
 */
let activeTab = 'reservas';

/**
 * Panel activo del shell (`'staff'` | `'admin'`, D1). Gatea qué entradas de
 * `SECTION_REGISTRY` son montables desde `handleTabClick` (D1.3 — un click
 * forjado con `data-tab` de una sección del otro panel no debe montar nada).
 * NO es el default de `renderAdmin`: ese sale siempre de la URL actual
 * (`currentPanel()`), porque este estado puede quedar viejo (p. ej. la rama
 * de error de sesión/rol no lo actualiza).
 */
let activePanel = 'staff';

/**
 * Panel que corresponde a la URL actual (`js/shared/panel-routes.js`, misma
 * fuente que las rutas de `js/main.js`). Fuera de una ruta de panel cae a
 * `'staff'` (mínimo privilegio).
 * @returns {'staff'|'admin'}
 */
function currentPanel() {
    return panelForPath(window.location.pathname) ?? 'staff';
}

/** Copy Spanish del acceso denegado en el lugar (D1 — "Counter Role Never Reaches Admin Sections"; sin redirect desde el route rename). */
const ADMIN_PANEL_DENIED_MSG = 'No tenés acceso al panel de administración.';

/** Último listado de bloqueos resuelto (null hasta la primera carga exitosa). */
let blocksViewModel = null;

/** Estado ui-only de la sección de bloqueos (loading/error/ready). */
let blocksUiState = { status: 'loading' };

/** Bloqueo pendiente de confirmación de borrado (`{ id, message }`) mientras el ConfirmDialog está abierto. */
let pendingBlockDelete = null;

/** Guard de re-entrancy de las mutaciones de bloqueos (crear/eliminar): evita dobles envíos. */
let blockMutationInFlight = false;

/** Último estado de publi resuelto (`{ enabled }`, null hasta la primera carga exitosa). */
let publiViewModel = null;

/** Estado ui-only de la sección de configuración (loading/error/ready). */
let publiUiState = { status: 'loading' };

/** Guard de re-entrancy del toggle de publi: evita dobles clicks. */
let publiToggleInFlight = false;

/** Último listado de empleados resuelto (null hasta la primera carga exitosa). */
let empleadosViewModel = null;

/** Estado ui-only de la sección de empleados (loading/error/ready). */
let empleadosUiState = { status: 'loading' };

/** Empleado en edición (`null` en alta) — repuebla el form de `EmpleadosView`. */
let empleadoEditing = null;

/** Diálogo de PIN pendiente (`{ empleadoId, error? }`, `null` = cerrado). */
let empleadoPinDialog = null;

/**
 * Lo tipeado en el form de empleados y todavía sin guardar. Sobrevive a los
 * repintados del mount (ver `renderEmpleadosMount`); `null` = nada pendiente.
 */
let empleadoFormDraft = null;

/** Guard de re-entrancy de las mutaciones de empleados (guardar/toggle/PIN). */
let empleadoMutationInFlight = false;

/** Generación de carga de empleados: descarta respuestas viejas (ver `refreshEmpleados`). */
let refreshEmpleadosSeq = 0;

/**
 * "Mostrar todos los clientes" (picker de Fudo, PR A2b) — toggle ui-only,
 * mismo criterio que `empleadoEditing`: vive en estado, no en el DOM, para
 * que un repintado del mount lo respete en vez de perderlo.
 */
let empleadoMostrarTodosClientes = false;

/**
 * Generación de carga del directorio Fudo (clientes/proveedores del picker,
 * PR A2b): descarta una respuesta vieja que resuelve después de que el admin
 * dejó la sección — MISMA trampa y misma solución que `refreshEmpleadosSeq`.
 */
let refreshFudoDirectorySeq = 0;

/**
 * ¿La ÚLTIMA carga del directorio Fudo falló (red/401/rate-limit)? (review
 * follow-up P2, 2026-08-08). Distingue "no pudimos consultar Fudo" de
 * "genuinely absent": sin esto, una caída de Fudo dejaba `fudoCustomers`/
 * `fudoProviders` sin setear (`[]` por defecto en `EmpleadosView`) y
 * `missingSelectionEntry` disparaba "ya no aparece en Fudo" para TODO
 * empleado con id asignado — una alarma falsa masiva, no un hallazgo real.
 * El id asignado se sigue mostrando igual (vía el snapshot `*_nombre`): esto
 * solo cambia el TEXTO del aviso, nunca esconde el vínculo.
 */
let empleadoFudoDirectoryUnavailable = false;

/** Último modelo de vista de Fichadas resuelto (`{ anomalies, empleados, fichadas }`, null hasta la primera carga exitosa). `fichadas` es el rango del filtro «Desde»/«Hasta» (`fichadaFiltro`; por defecto los últimos 30 días ART, `fichadasFechasDefault`), TODOS los empleados. */
let fichadasViewModel = null;

/** Estado ui-only de la sección de fichadas (loading/error/ready). */
let fichadasUiState = { status: 'loading' };

/**
 * Diálogo de edición/cierre de fichada pendiente
 * (`{ fichadaId, accion, empleadoNombre, entrada_at, salida_at, error? }`,
 * `null` = cerrado). Mismo criterio que `empleadoPinDialog`.
 */
let fichadaDialog = null;

/**
 * Lo tipeado en el form de alta manual de fichadas y todavía sin guardar.
 * Sobrevive a los repintados del mount (abrir el diálogo de corrección
 * repinta todo el mount, igual que `empleadoFormDraft` en Empleados — mismo
 * defecto que esa nota de cabecera documenta, la misma solución acá).
 * `null` = nada pendiente.
 */
let fichadaFormDraft = null;

/** Guard de re-entrancy de las mutaciones de fichadas (crear/corregir). */
let fichadaMutationInFlight = false;

/** Generación de carga de fichadas: descarta respuestas viejas (ver `refreshFichadas`). */
let refreshFichadasSeq = 0;

/**
 * Filtro por empleado de la sección Fichadas (owner request, 2026-09-02:
 * "ver TODAS las fichadas de un empleado"). `empleadoId: ''` = "Todos los
 * empleados" (default). No es un draft capturado del DOM como
 * `fichadaFormDraft` — es estado que la vista REFLEJA directamente
 * (`FichadasView`'s `uiState.filtro`), así que sobrevive a un repintado del
 * mount sin necesitar captura/re-poblado.
 */
let fichadaFiltro = { empleadoId: '', todoHistorial: false, desde: '', hasta: '', error: null };

/**
 * Última respuesta de `loadFichadas({ empleadoId, sinRango: true })`
 * ("Todo el historial"), cacheada por empleado — `null` si nunca se pidió o
 * si el filtro cambió de empleado desde entonces. Separado de
 * `fichadasViewModel.fichadas` (el rango por defecto) a propósito: tildar
 * "Todo el historial" NO debe tocar la lista de anomalías, que sigue
 * calculada sobre el rango por defecto para TODOS los empleados.
 */
let fichadaHistorialCompleto = null;

/** Generación de carga del historial completo de un empleado: descarta una respuesta vieja (mismo patrón que `refreshFichadasSeq`). */
let refreshFichadaHistorialSeq = 0;

/** Generación de lectura del banner de jornadas abiertas (F-B): descarta una respuesta vieja (mismo patrón que `refreshFichadasSeq`). */
let refreshJornadasBannerSeq = 0;

/** Último listado de empleados resuelto para el selector de Liquidación (null hasta la primera carga exitosa). */
let liquidacionViewModel = null;

/** Estado ui-only de la sección de liquidación (loading/error/ready — carga del listado de empleados). */
let liquidacionUiState = { status: 'loading' };

/**
 * Resultado del último preview (`null` = todavía no se pidió uno):
 *   { status: 'loading' }
 *   { status: 'anomalias', anomalias: [...] }   — RF-11, gate ANTES de Fudo
 *   { status: 'bloqueado', mensaje }            — Fudo 503/502, NUNCA "$0"
 *   { status: 'error', mensaje }
 *   { status: 'ready', liquidaciones: [...] }
 */
let liquidacionPreview = null;

/**
 * Resultado del último confirm (`null` = todavía no se confirmó nada):
 *   { status: 'ok', liquidaciones: [...] }
 *   { status: 'parcial', persistidas: [...], empleadoId, mensaje } — confirm
 *     de varios empleados, uno rechazado: lo que YA se escribió + lo que no.
 *   { status: 'incierto', mensaje } — falla de transporte en confirm: no se
 *     sabe si escribió o no, nunca se muestra como un error limpio.
 *   { status: 'error', mensaje }
 */
let liquidacionConfirmResult = null;

/** Últimos parámetros usados para el preview vigente (`{ empleadoIds, desde, hasta }`) — el confirm los reusa tal cual. */
let liquidacionUltimoRango = null;

/** Confirmación de liquidación pendiente (`{ empleadoIds, desde, hasta, message }`) mientras el ConfirmDialog está abierto. */
let pendingLiquidacionConfirm = null;

/** Guard de re-entrancy de las mutaciones de liquidación (preview/confirm): evita dobles envíos. */
let liquidacionMutationInFlight = false;

/**
 * Generación de preview de liquidación: descarta una respuesta vieja (mismo
 * patrón que `refreshFichadasSeq`). Se sube también al MONTAR la sección
 * (`mountLiquidacionPanel`), no solo al pedir un preview nuevo: sin eso, un
 * preview dejado en vuelo al abandonar la pestaña y que resuelve recién al
 * volver a entrar pasaría el chequeo de secuencia (nadie pidió uno nuevo
 * todavía) y pintaría una respuesta vieja sobre una sesión de la pestaña
 * que ya es otra — mismo criterio que `sessionSeq` en kiosco.js.
 */
let liquidacionPreviewSeq = 0;

/** Generación de carga del listado de empleados: descarta una respuesta vieja (mismo patrón que `refreshEmpleadosSeq`/`refreshFichadasSeq`). */
let liquidacionEmpleadosSeq = 0;

/**
 * R4-002: mismo patrón que `liquidacionPreviewSeq` — un confirm puede tardar
 * hasta 55s; si el admin abandona la pestaña y vuelve antes de que resuelva,
 * `mountLiquidacionPanel` recrea los mounts y resetea `liquidacionMutationInFlight`,
 * habilitando un confirm nuevo. Sin esta secuencia, la respuesta vieja solo
 * pasaba chequeos de "el nodo existe" (que valen igual contra los nodos
 * NUEVOS) y pisaba el resultado de la sesión fresca. Se sube también al
 * MONTAR la sección, no solo al confirmar, por la misma razón documentada
 * arriba para `liquidacionPreviewSeq`.
 */
let liquidacionConfirmSeq = 0;

/**
 * Unit B2 (design.md AD12) — anulación pendiente mientras el
 * `AnularLiquidacionDialog` está abierto: `{ liquidacionId, empleadoNombre,
 * periodo, error? }`, o `null` cuando está cerrado. Mismo patrón que
 * `pendingLiquidacionConfirm`, pero en su PROPIO mount (`admin-liquidacion-anular-mount`)
 * — así abrir/cerrar este diálogo, o mostrar un error de submit, nunca
 * repinta el selector ni el historial de abajo.
 */
let pendingLiquidacionAnular = null;

/** Guard de re-entrancy del submit de anulación: evita dobles envíos. */
let liquidacionAnularInFlight = false;

/**
 * Mensaje de una anulación `incierto` (o de una excepción al anular) que
 * todavía no se pudo contrastar con el historial: se muestra como aviso fijo
 * en el mount de Liquidación (no sólo un toast) hasta que
 * `refreshLiquidacionHistorial` recarga el historial CON ÉXITO — si esa
 * recarga también falla, el aviso sigue diciendo que la anulación pudo
 * haberse aplicado. `null` = no hay nada pendiente de verificar.
 */
let liquidacionAnularIncierto = null;

/**
 * Unit B2 — mismo patrón que `liquidacionConfirmSeq`: una anulación dejada
 * en vuelo al abandonar la pestaña y que resuelve recién al volver a entrar
 * no debe pisar el mount de una sesión fresca. Se sube también al MONTAR la
 * sección (`mountLiquidacionPanel`), no sólo al enviar una anulación nueva.
 */
let liquidacionAnularSeq = 0;

/**
 * Owner UX batch (2026-09-02): abre/cierra el área colapsable
 * "Configuración" (editor de tope + panel de consumo del tope por mes,
 * `LiquidacionView#renderConfigArea`) — `false` al montar la sección (Req 1:
 * "Closed by default"). `refreshLiquidacionCapConfig` la fuerza a `true`
 * cuando el tope todavía NO está configurado, para que el admin vea de
 * entrada qué hay que hacer (mismo criterio que el gate
 * `renderTopeNoConfigurado` ya usa en preview/confirm) — el toggle manual
 * del botón engranaje SIEMPRE puede volver a cerrarla una vez que el tope
 * está configurado.
 */
let liquidacionConfigAbierta = false;

/**
 * WARNING resilience fix (review-c978b1e13b357306): `refreshLiquidacionCapConfig`
 * used to force `liquidacionConfigAbierta = true` unconditionally whenever
 * the tope resolved as "not configured" — including the catch branch — so
 * an admin who opened AND closed the area by hand while that initial fetch
 * was still in flight got their explicit close silently reverted the moment
 * the fetch settled. Tracks whether the gear was touched during THIS mount;
 * reset to `false` in `mountLiquidacionPanel`, set to `true` in the gear's
 * click handler. `refreshLiquidacionCapConfig` only auto-opens when this is
 * still `false` — once the admin has touched the toggle, their choice wins
 * over the auto-open heuristic for the rest of the mount.
 */
let liquidacionConfigTocadoPorAdmin = false;

/**
 * Owner UX batch (2026-09-02): `true` muestra las filas `anulada` del
 * historial, `false` (default al montar la sección) las oculta detrás de un
 * toggle "Mostrar anuladas (N)" (`LiquidacionView#renderHistorialPanel`) —
 * AD13 sigue intacta: una fila anulada nunca se BORRA ni deja de cargarse,
 * sólo se filtra en el render, y sigue siendo alcanzable con un click. El
 * panel de consumo del tope (`renderMesConsumoPanel`) NO usa este flag —
 * excluye las filas anuladas SIEMPRE, por su propia regla AD13/AD7/AD8.
 */
let liquidacionMostrarAnuladas = false;

/**
 * Unit A2 (design.md AD5/AD6, spec "Comprobante Availability") — la fila
 * cuyo comprobante está abierto (el OBJETO completo, no el id): `null` cuando
 * el panel está cerrado. Guardar la fila entera, no re-buscarla, es lo que
 * hace que un `refreshLiquidacionHistorial()` de fondo (p.ej. disparado por
 * anular OTRA fila) nunca pueda cambiarle el documento debajo al admin
 * mientras lo tiene abierto o en vista previa de impresión.
 */
let liquidacionComprobanteAbierto = null;

/**
 * El `id` de la fila cuyo botón "Comprobante" abrió el panel — NO el nodo
 * `<button>` en sí. `renderLiquidacionMount` reemplaza el HTML del historial
 * en cada repintado (un refresh de fondo, un toggle "Mostrar anuladas",
 * etc.), así que un nodo guardado en `open` quedaría desconectado del DOM;
 * buscar por `id` en el mount principal AL CERRAR siempre encuentra el botón
 * vigente si la fila sigue visible (design.md AD6).
 */
let liquidacionComprobanteOrigenId = null;

/**
 * `document.title` de antes de abrir el comprobante, restaurado al cerrar
 * (Volver/Escape) o al re-montar el panel (AD8/AD9). Sólo se captura en la
 * PRIMERA apertura de una sesión de comprobante — si el admin abre un
 * segundo comprobante mientras el primero sigue abierto (reemplazo, no
 * apilado), esto evita pisar el título original con el del primer
 * comprobante.
 */
let liquidacionComprobantePreviousTitle = null;

/**
 * Estado del editor del tope mensual de descuento (E1, `payroll_config`):
 * `{ cargando, configurado, tope, error }`. `configurado: false` cubre
 * TANTO "sin fila todavía" (AD11) COMO "Supabase no disponible" — ver el
 * comentario de `admin-payroll-config.service.js#loadPayrollConfig`, que
 * documenta por qué el servicio colapsa ambos casos a propósito en vez de
 * inventar una distinción que el repositorio no expone.
 */
let liquidacionCapConfig = null;

/**
 * Lo tipeado en el input del tope y todavía sin guardar. Sobrevive a los
 * repintados del mount — MISMA TRAMPA (captura antes de reemplazar el HTML,
 * re-puebla después) que el resto de esta sección.
 */
let liquidacionCapDraft = null;

/** Guard de re-entrancy del guardado del tope: evita dobles envíos. */
let liquidacionCapSaveInFlight = false;

/** Generación de carga del tope: descarta una respuesta vieja (mismo patrón que `liquidacionEmpleadosSeq`). Corre en paralelo con `refreshLiquidacionEmpleados`, ninguno bloquea al otro. */
let liquidacionCapConfigSeq = 0;

/**
 * F1 (review-050ef9b16341fee9): generación del GUARDADO del tope — mismo
 * patrón que `liquidacionPreviewSeq`/`liquidacionConfirmSeq`/
 * `liquidacionCapConfigSeq`. Antes de este fix, `handleLiquidacionCapSaveSubmit`
 * gateaba sus escrituras post-`await` solo con "el mount existe", que vuelve
 * a ser cierto al reentrar a la pestaña: un guardado dejado en vuelo en una
 * visita abandonada podía pisar el tope de la sesión fresca. Se sube también
 * al MONTAR la sección, no solo al guardar, por la misma razón documentada
 * arriba para `liquidacionPreviewSeq`.
 */
let liquidacionCapSaveSeq = 0;

/**
 * E2 — historial de liquidaciones confirmadas + panel de consumo del tope
 * (AD9: `{ cargando, error, liquidaciones }`, SOLO de pantalla — nunca
 * autoritativo para el cálculo de sueldos). `null` hasta el primer render.
 */
let liquidacionHistorial = null;

/** Generación de carga del historial — mismo patrón que `liquidacionCapConfigSeq`; corre en paralelo, sin bloquear preview/confirm. */
let liquidacionHistorialSeq = 0;

/** review-982449c9e9c27e08 (finding 2): promesa del `refreshLiquidacionHistorial` en curso — `recomputeTablero` la espera antes de clasificar. Nunca rechaza. */
let liquidacionHistorialPromise = null;

/**
 * S8 (spec `payroll-payments` "Derived Payment State", reusa `estadoPago` de
 * S5b) — pagos de las filas `confirmada` del historial, `Map<liquidacionId,
 * pago[]>`, para la pill de pago + "Registrar pago" de cada fila. Lectura
 * NUEVA (no cubierta por el batch AD18 del tablero, que sólo cubre el
 * período vigente ahí): una falla acá SOLO degrada la pill a ausente, MISMO
 * criterio AD9 que el resto del historial (informativo, nunca autoritativo).
 * `null` hasta la primera carga.
 */
let liquidacionHistorialPagos = null;

// ---------------------------------------------------------------------------
// Tablero de período de Liquidación (Unit S2a, design.md AD1) — namespace de
// estado PROPIO ("tablero*"), independiente del namespace legacy
// "liquidacion*" de arriba (AD1: "existing liquidacion* variables
// untouched"). Reemplazó al selector mes/empleados como la ÚNICA forma de
// liquidar (S8 lo retiró) — el namespace "liquidacion*" de arriba sigue vivo
// para el historial/configuración/confirm, que siguen viviendo ahí.
// ---------------------------------------------------------------------------

/**
 * Selección de período vigente del tablero (`{kind, mes?, mitad?, fecha?,
 * desde?, hasta?}`, consumible por `rangoDelPeriodo`) — `null` hasta que se
 * resuelve el default (AD17) en el primer montaje de la pestaña.
 */
let tableroSeleccion = null;

/** `true` mientras se resuelve el período por defecto (AD17) + se completa la primera carga del tablero — la vista pinta un spinner de página completa mientras esto es `true` (no hay selector que preservar todavía). */
let tableroCargando = true;

/** `true` mientras un recompute (roster split + preview) está en vuelo — deshabilita "Actualizar"/navegación/presets en la vista, evitando un doble disparo. */
let tableroRefrescando = false;

/** Toggle "Mostrar inactivos con horas en el período" (Req: Inactive Employees Toggle) — destildado por defecto en cada montaje. */
let tableroMostrarInactivos = false;

/**
 * Cache de cifras del tablero (design.md AD5): `{ desde, hasta, calculadoAt,
 * estado: 'ok'|'error', error, filas: Map<empleadoId, fila> }`, `null` hasta
 * que el primer recompute resuelve. `estado`/`error` (AD19) ya forman parte
 * de la forma desde esta unidad aunque su render degradado recién llega en
 * S2b — evita repintar esta forma cuando esa unidad aterrice.
 */
let tableroCache = null;

/**
 * Generación de MONTAJE del tablero — se sube SOLO al MONTAR la sección
 * (`mountLiquidacionPanel`), a propósito NUNCA en cada recompute
 * (design.md AD5: "the sequence guard catches a re-mount"). El cambio de
 * período MIENTRAS un preview sigue en vuelo lo cubre el re-chequeo de
 * rango POST-AWAIT dentro de `recomputeTablero` — un guard independiente,
 * no este contador (a diferencia de `liquidacionPreviewSeq`, que SÍ se sube
 * en cada request; ver el comentario de `recomputeTablero` para por qué acá
 * se separan los dos guards).
 */
let tableroSeq = 0;

/**
 * Borrador del rango personalizado del toolbar ("Otro"), sobrevive a un
 * repintado de fondo — MISMA TRAMPA (captura antes de reemplazar el HTML,
 * re-puebla después) que el resto de esta sección. `null` = nada pendiente
 * todavía.
 */
let tableroPersonalizadoDraft = null;

/**
 * S8 — "Exportar CSV" REUBICADO acá desde el toolbar legacy del historial
 * (`LiquidacionView.js`, retirado junto con `renderRangoForm`/
 * `renderPreviewSection` — ver apply-progress.md "Unit S8"): mes y checkbox
 * "Incluir anuladas" tipeados, sobrevive a un repintado — MISMA TRAMPA que
 * `tableroPersonalizadoDraft`. El comportamiento/policy de exportación NO
 * cambian (`js/shared/liquidacion-export-policy.js`, sin tocar) — sólo el
 * mount donde vive el form. `null` hasta la primera captura; mientras es
 * `null`, `renderTableroMount` pinta el default (`mesLiquidacionPorDefecto()`,
 * checkbox destildado) directamente.
 */
let tableroExportDraft = null;

/** S8 — mensaje inline del export (mes sin filas tras el filtro de anuladas, o falla de lectura); `null` cuando no hay nada que mostrar. */
let tableroExportMensaje = null;

/** S8 — guard de re-entrancy del export: evita doble click mientras el fetch/build está en curso. */
let tableroExportInFlight = false;

/** S8 — generación del export, mismo patrón que `tableroDrawerSeq`: descarta una respuesta tardía si el mount se re-montó (cambio de pestaña) mientras el export seguía en vuelo. */
let tableroExportSeq = 0;

/**
 * Estado del cajón compartido (design.md AD7): `{ tipo:'adelanto'|'otro_descuento',
 * empleadoId, empleadoNombre, fecha?, monto?, nota?|concepto?, cargando, activos,
 * anulados, error?, anulandoId?, anularError?, submitting? }`, `null` cuando está
 * cerrado (ver `CAJON_MOVIMIENTO`).
 * S3 agrega `tipo:'anomalias'` reusando la MISMA variable/mount — Unit S4 es
 * la única dueña de la forma `tipo:'adelanto'`.
 */
let tableroDrawer = null;

/** Generación del cajón — bump al cerrar para que una respuesta tardía de un submit ya cancelado (AD7) nunca reabra ni repinte un cajón que el admin ya cerró (mismo criterio que `liquidacionAnularSeq`). */
let tableroDrawerSeq = 0;

/**
 * S2 (acceptance run): generation of the drawer LIST loads (adelantos, pagos,
 * anomalías). A list result is applied only if it is the latest list request
 * AND the open drawer is still the same target (`mismoCajonTablero`). It is
 * deliberately NOT keyed on `tableroDrawerSeq` or on the `tableroDrawer`
 * object identity: every submit bumps the seq and every re-render (a
 * validation error, Anular abrir/cancelar) replaces the object, and both used
 * to drop the only in-flight load, leaving the list on "Cargando..." forever
 * or showing a stale list after an Anular. The result is merged into the
 * CURRENT drawer state, so nothing the admin did meanwhile is lost.
 */
let tableroDrawerListSeq = 0;

/**
 * Same drawer target: same `tipo`, same employee and, for pagos, the same
 * liquidación. `false` if either side is `null` (drawer closed).
 * @param {object|null} a
 * @param {object|null} b
 * @returns {boolean}
 */
function mismoCajonTablero(a, b) {
    return !!a && !!b && a.tipo === b.tipo && a.empleadoId === b.empleadoId && (a.liquidacionId ?? null) === (b.liquidacionId ?? null);
}

/** `true` mientras un alta/anulación de adelanto está en vuelo — evita doble submit. */
let tableroDrawerSubmitting = false;

/** Elemento que disparó la apertura del cajón compartido — el foco vuelve acá al cerrar (ver `restoreTableroDrawerFocus`), `document.contains` decide si todavía existe. */
let tableroDrawerTrigger = null;

/** `tableroDrawer.tipo` del ÚLTIMO render con el cajón abierto (review-130d19515f9f2340, finding 1) — distingue una apertura/cambio de tipo (atrapa el foco) de un repintado del MISMO cajón (sólo restaura el foco si un repaint de fondo se lo robó). `null` mientras el cajón está cerrado. */
let tableroDrawerRenderedTipo = null;

/** `markTableroCacheStale` llamado mientras `tableroCache` seguía `null` — guarda el RANGO vigente en ese momento; `recomputeTablero` sólo lo re-aplica si el rango recién escrito coincide (review-130d19515f9f2340, finding 2: un cambio de período de por medio no debe heredarlo). */
let tableroCacheStalePendiente = null;

/** Pad de la ventana de lectura de fichadas (design.md AD3) — MISMO valor y MISMO propósito que `QUERY_PAD_DAYS` en `api/liquidacion.js`: un turno puede empezar bastante antes de `desde` y cruzar hacia adentro del período (una salida olvidada que se cierra tarde es justo la anomalía DURACION_EXCESIVA que tiene que verse); dos días ART cubren turnos de hasta 48h. `detectAnomalies` sigue filtrando con el `desde`/`hasta` REAL (sin padding) después, acá solo se ensancha la lectura. Los límites van en medianoche ART (`ventanaLecturaFichadas`), nunca como fecha corta. */
const TABLERO_QUERY_PAD_DAYS = 2;

/** review-982449c9e9c27e08 (findings 1/2): copia del banner degradado cuando `recomputeTablero` no pudo siquiera armar el roster (falla de `loadEmpleados`/`loadFichadas`/historial) — nunca una tabla vacía silenciosa. */
const TABLERO_LOAD_ERROR_MSG = 'No se pudo cargar el tablero.';

/** review-982449c9e9c27e08 (finding 3, AD4) — copia del banner cuando el `desde` elegido cae antes de la ventana de historial cargada: la clasificación "Liquidada" no puede confiar en un historial que no llegó a leer ese rango. */
const TABLERO_FUERA_DE_VENTANA_MSG = 'Período anterior al historial cargado.';

// ---------------------------------------------------------------------------
// Área "Configuración" del tablero (Unit S6, design.md AD5/AD17, spec
// `payroll-holidays-config`) — un CUARTO `tableroDrawer.tipo` sobre el MISMO
// shell/mount que Adelanto (S4) y Anomalías (S3). Estado PROPIO, namespace
// `tablero*` (AD1), independiente de `liquidacionCapConfig`/etc. salvo donde
// se documenta reuso explícito abajo.
// ---------------------------------------------------------------------------

/** Mes mostrado en el calendario de feriados, `'YYYY-MM'` — `null` hasta que se abre el área por primera vez (se resuelve al mes del período vigente del tablero). */
let tableroConfigMes = null;

/** Lista COMPLETA de feriados — cargada una vez al abrir el área (no por mes: `feriados.repository.js#listFeriados` trae todos; el calendario filtra client-side vía `feriadosDelMes`). */
let tableroFeriados = { cargando: true, error: null, lista: [] };

/** Generación de carga/mutación de feriados — descarta una respuesta tardía si el cajón ya se cerró (mismo criterio que `tableroDrawerSeq`). */
let tableroFeriadosSeq = 0;

/** Form inline "Agregar" abierto sobre un día del calendario — `{ fecha, descripcion, error }`, `null` = ningún día en edición. */
let tableroFeriadoDraft = null;

/** `true` mientras un alta o una baja de feriado está en vuelo (un solo form/botón a la vez, mismo criterio que `tableroDrawerSubmitting`). */
let tableroFeriadoSubmitting = false;

/** Fecha del feriado que se está quitando — deshabilita SOLO su botón "Quitar", no todo el calendario. */
let tableroFeriadoQuitandoFecha = null;

/** `payroll_config.periodo_por_defecto` vigente (AD17) — resuelto una vez por `initTablero()` (reusa esa MISMA llamada a `loadPayrollConfig`, sin un segundo round-trip cuando se abre esta área). `null` = ausente (spec: la selección se muestra en "Mes" igual, sin error). */
let tableroPeriodoPorDefectoActual = null;

/** Borrador del `<select>` "Período por defecto", sobrevive a un repintado del cajón — MISMA TRAMPA que el resto de esta sección. `null` = nada tipeado todavía (se muestra el vigente). */
let tableroPeriodoPorDefectoDraft = null;

/** `true` mientras se guarda el período por defecto. */
let tableroPeriodoPorDefectoSaving = false;

/** Mensaje de error del último intento de guardar el período por defecto. */
let tableroPeriodoPorDefectoError = null;

/** Último modelo de categorías de Fudo GP resuelto (null hasta la primera carga exitosa). */
let categoriesViewModel = null;

/** Último mapa de reglas { [fudo_category_id]: estado } resuelto. */
let categoryRulesMap = null;

/** Estado ui-only de la sección de categorías (loading/error/ready). */
let categoriesUiState = { status: 'loading' };

/** Guard de re-entrancy del cambio de regla de categoría: evita dobles cambios. */
let categoryRuleMutationInFlight = false;

/**
 * Estado ui-only de la vista de reservas (loading/error/ready + ventana
 * actual). NO es parte del modelo de vista que devuelve el servicio — el
 * servicio solo conoce `{ groups, isEmpty }`; loading/error son estados que
 * únicamente el wiring puede distinguir (una promesa rechazada vs. una lista
 * vacía resuelta se ven igual desde el tipo de retorno del servicio).
 */
let reservationsUiState = { status: 'loading', from: undefined };

/** Último modelo de vista resuelto por `loadUpcoming`/`loadArchived` (null hasta la primera carga exitosa). */
let reservationsViewModel = null;

/**
 * Modo de la vista de reservas (`activas` | `archivadas`). En `activas` se
 * carga la lista agrupada por día (`loadUpcoming`); en `archivadas` la lista
 * plana de solo-lectura (`loadArchived`). Reseteado a `activas` en cada montaje
 * fresco de la vista (Req: Archived Reservations View).
 */
let reservationsMode = 'activas';

/** Guard de re-entrancy: evita dos transition concurrentes (doble click rápido en un botón de estado). */
let reservationMutationInFlight = false;

/**
 * Modo del modal de reserva: `null` = cerrado, `'crear'` = alta manual,
 * `'editar'` = edición de una reserva existente. Un solo modal sirve a los dos
 * flujos, así que un booleano de "abierto" ya no alcanza.
 */
let reservaFormMode = null;

/** Fila en edición (`null` en alta). Guardamos la fila entera, no el id. */
let reservaEditando = null;

/** Último plano resuelto por `loadFloorPlan` (null hasta la primera carga exitosa). */
let mesasPlan = null;

/** Mesas que la reserva YA tenía al abrir el modal — la base del diff al guardar. */
let mesasAsignadas = [];

/** Selección local del operador (ids). No toca la base hasta guardar. */
let mesasSeleccion = new Set();

/**
 * ¿El operador ya tocó el plano? Distingue "no eligió nada" de "todavía no
 * cargó": sin esto, recargar el plano al cambiar de turno pisaría la selección
 * con lo que la reserva tenía asignado.
 */
let mesasSeleccionTocada = false;

/** Estado ui-only del plano (loading/ready/error) — mismo criterio que `reservationsUiState`. */
let mesasUiState = { status: 'loading' };

/** Token monotónico de `refreshMesasPlan`: descarta respuestas de un turno ya cambiado. */
let mesasPlanSeq = 0;

/** Guard de re-entrancy del alta manual de reservas: evita dobles envíos del formulario "Nueva reserva". */
let reservaCreateInFlight = false;


/**
 * Identidad de la mutación en vuelo. Si el usuario cambia de sección o cierra
 * sesión (SIGNED_OUT) mientras el `transition` está en vuelo, esto deja de
 * apuntar a la mutación original y sus efectos (Toast/refresh) se descartan al
 * resolverse — evita Toasts huérfanos y fetches sobre un cliente ya deslogueado
 * o una sección distinta.
 */
let activeReservaMutation = null;

/**
 * Token monotónico de `refreshReservations`. Dos cambios rápidos de fecha (A y
 * luego B) pueden resolverse fuera de orden; si A resuelve después de B pisaría
 * la vista con datos viejos. Cada carga captura el token vigente y descarta su
 * resultado si ya fue superada por una carga posterior.
 */
let refreshReservationsSeq = 0;

/** Un `<input type="date">` produce '' (o un valor no-ISO) que como límite inferior sería match-everything. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Decide qué vista del panel mostrar según sesión, rol y panel solicitado.
 * Helper puro (testeable sin DOM ni red).
 *
 * D1 (`staff-admin-access-split`, spec "Counter Role Never Reaches Admin
 * Sections"): un `counter` que pide el panel admin (`/admin`) nunca ve el
 * shell — recibe `'denied'` (route rename, owner decision: se le muestra el
 * aviso en el lugar, sin redirect), una CORTESÍA de UX, no el límite real
 * (ese sigue siendo RLS/`is_admin()`). Un `admin` en el panel staff
 * (`/staff`) ve el shell de staff: admin cuenta como staff.
 * `panel` por defecto `'staff'` mantiene compatibilidad con el llamado de 2
 * argumentos que ya existía antes de esta slice.
 * @param {import('@supabase/supabase-js').Session | null} session
 * @param {string|null} role
 * @param {'staff'|'admin'} [panel='staff']
 * @returns {'login' | 'shell' | 'denied'}
 */
export function pickAdminView(session, role, panel = 'staff') {
    if (!session || !isStaff(role)) return 'login';
    if (panel === 'admin' && role !== 'admin') return 'denied';
    return 'shell';
}

/**
 * Suscribe (una sola vez a la vez) el re-render del panel ante un SIGNED_OUT
 * (logout, refresh de token fallido, logout cross-tab). Limpia la suscripción
 * previa antes de crear una nueva para no acumular listeners.
 */
function subscribeAuthChanges() {
    authSubscription?.unsubscribe();
    authSubscription = onAuthChange((event) => {
        // review-2df30d416a200f2b C3: invalidate on EVERY auth transition,
        // not only the logout button. A session that ends via a failed
        // token refresh (SIGNED_OUT) or a different admin signing into the
        // same tab (SIGNED_IN) never goes through handleLogout, so it never
        // cleared the Fudo directory cache before this.
        invalidateFudoDirectoryCache();
        if (event === 'SIGNED_OUT') renderAdmin();
    });
}

/**
 * Renderiza el panel /staff (o /admin) resolviendo sesión + rol y
 * aplicando el gate.
 *
 * Si la resolución de sesión/rol falla (red/DB), NO deja el spinner colgado:
 * cae al formulario de login + Toast de error + console.error.
 *
 * `panel` por defecto sale de la URL ACTUAL (`currentPanel()`, mismo mapa
 * `PANEL_ROUTES` que registra las rutas en `js/main.js`) para que un
 * re-render sin argumento explícito — logout, login exitoso, SIGNED_OUT —
 * aterrice siempre en el panel de la ruta donde está el usuario, nunca en el
 * de un render anterior; `js/main.js` igual lo llama con un `panel` explícito
 * desde la ruta (`/staff` → `'staff'`, `/admin` → `'admin'`).
 *
 * `initialTab` es un override interno (no lo usa ninguna ruta) para aterrizar
 * en una sección específica del panel sin pasar por su tab por defecto —
 * evita, por ejemplo, que un test que quiere un mount fresco de Liquidación
 * dispare de paso una carga de Empleados (ambas comparten `loadEmpleados`) al
 * aterrizar primero en la tab por defecto del panel admin y recién después
 * clickear la real.
 * @param {'staff'|'admin'} [panel=currentPanel()]
 * @param {string} [initialTab] — sección con la que arranca el shell; por defecto la primera de `panel`.
 * @returns {Promise<void>}
 */
export async function renderAdmin(panel = currentPanel(), initialTab) {
    const container = DOM.adminContainer;
    if (!container) return;

    // Finding 5: captura la secuencia vigente ANTES de cualquier `await`. Un
    // llamado más nuevo que arranca mientras este sigue en vuelo incrementa
    // `renderSeq`, y cada chequeo de abajo corta este llamado viejo sin tocar
    // DOM ni estado. AD8's teardown/subscribe siguen SIN condicionar por
    // `seq` (corren siempre, síncronos, antes del primer `await`).
    const seq = ++renderSeq;

    // AD8: teardown de lo que estaba montado ANTES de reemplazar el shell —
    // cubre login, logout, un fallo al resolver sesión/rol y cualquier otra
    // transición de auth, no solo el cambio de tab (ver handleTabClick).
    teardownActiveSection();

    subscribeAuthChanges();

    let session = null;
    let role = null;
    try {
        session = await getCurrentSession();
        if (seq !== renderSeq) return; // finding 5: un llamado más nuevo ya ganó.
        if (session) {
            const result = await getStaffRole();
            if (seq !== renderSeq) return; // finding 5: idem, tras el segundo await.
            if (!result.ok) throw result.error ?? new Error('No se pudo resolver el rol.');
            role = result.role;
        }
    } catch (err) {
        if (seq !== renderSeq) return; // finding 5: no pisar un render más nuevo con el error de uno viejo.
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('renderAdmin: no se pudo resolver sesión/rol', err);
        container.innerHTML = LoginForm(panel);
        hideDecorativeIcons(container);
        setupAdminListeners();
        showToast(VERIFY_ERROR_MSG, { level: 'error' });
        return;
    }

    const view = pickAdminView(session, role, panel);

    if (view === 'denied') {
        // D1: cortesía de UX, no el límite — un `counter` pidiendo el panel
        // admin nunca ve el shell ni una sección admin repintarse. Route
        // rename (owner decision, sin redirects): el aviso se muestra EN EL
        // LUGAR, la URL no cambia, y queda el botón de cerrar sesión para
        // entrar con otra cuenta. El límite real sigue siendo RLS/`is_admin()`.
        //
        // Findings 1+2 (review-ea4f2a93a19e6939): el contenedor compartido
        // puede seguir mostrando contenido privilegiado de un panel anterior
        // (`teardownActiveSection` ya corrió arriba, pero eso solo para
        // timers/listeners — el HTML en sí sigue ahí): se REEMPLAZA acá por
        // la vista de acceso denegado, y se deja un `console.warn` con el rol
        // y el panel pedido (no hay Sentry en browser — mismo criterio que
        // los `console.error` de este archivo). El logout de esta vista
        // vuelve al login del panel de la URL (`currentPanel()`).
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.warn('renderAdmin: acceso admin denegado', { role, panel });
        activePanel = panel;
        container.innerHTML = AdminAccessDenied(ADMIN_PANEL_DENIED_MSG);
        hideDecorativeIcons(container);
        setupAdminListeners();
        return;
    }

    activePanel = panel;
    // Cada render fresco del shell arranca en la sección por defecto de SU
    // panel (no arrastra la tab de una sesión/panel anterior): 'reservas'
    // para el panel staff, 'empleados' para el panel admin — la primera
    // sección de cada `SECTIONS_*` en AdminShell.js — salvo que el llamador
    // pida explícitamente otra con `initialTab`.
    //
    // Finding 4: `initialTab` se valida contra `panel` ANTES de usarlo — un
    // valor que pertenece a otro panel (`SECTION_REGISTRY[initialTab].panel
    // !== panel`) se ignora y cae al default del panel pedido, con un
    // `console.warn`. Sin este chequeo, `renderActiveTab`'s if/else monta
    // igual la sección nombrada aunque no le corresponda a `panel` — el
    // mismo agujero que D1.3 ya cerró para clicks forjados, acá para el
    // argumento directo de `renderAdmin`.
    let landingTab = initialTab;
    if (landingTab && SECTION_REGISTRY[landingTab]?.panel !== panel) {
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.warn('renderAdmin: initialTab ignorado (no pertenece al panel pedido)', { initialTab: landingTab, panel });
        landingTab = undefined;
    }
    activeTab = landingTab ?? (panel === 'admin' ? 'empleados' : 'reservas');
    container.innerHTML = view === 'shell' ? AdminShell(activeTab, panel) : LoginForm(panel);
    hideDecorativeIcons(container);
    setupAdminListeners();

    if (view === 'shell') renderActiveTab();
    if (view === 'shell' && panel === 'admin') {
        void refreshJornadasAbiertasBanner();
        void refreshReportesBadge();
    }
}

/**
 * Cablea los listeners del contenedor admin (login submit + logout).
 */
function setupAdminListeners() {
    const container = DOM.adminContainer;
    if (!container) return;

    const form = container.querySelector('#admin-login-form');
    if (form) {
        const submitBtn = form.querySelector('[data-action="admin-login"]');
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            await handleLogin(submitBtn);
        });
    }

    const logoutBtn = container.querySelector('[data-action="admin-logout"]');
    if (logoutBtn) {
        logoutBtn.addEventListener('click', handleLogout);
    }

    // Los items de nav viven en el chrome del shell (renderizado una sola vez
    // por render de shell), así que se cablean acá y persisten entre cambios de
    // sección — `renderActiveTab` sólo alterna su marca visual, no los reemplaza.
    for (const tabBtn of container.querySelectorAll('[data-action="admin-tab"]')) {
        tabBtn.addEventListener('click', handleTabClick);
    }

    // Drawer de la rail lateral (mobile): hamburguesa abre, overlay + X cierran.
    const sidebarOpenBtn = container.querySelector('[data-action="admin-sidebar-open"]');
    if (sidebarOpenBtn) sidebarOpenBtn.addEventListener('click', openAdminSidebar);
    for (const closeEl of container.querySelectorAll('[data-action="admin-sidebar-close"]')) {
        closeEl.addEventListener('click', closeAdminSidebar);
    }
}

/**
 * Procesa el intento de login: valida presencia, autentica y re-gatea por rol.
 *
 * Re-entrancy: si ya hay un login en curso, ignora el submit (doble click / doble
 * Enter) y deshabilita el botón mientras dura.
 *
 * Sólo cierra sesión al usuario cuando la resolución de rol tiene ÉXITO y el rol
 * es genuinamente no-staff. Si la resolución FALLA (red/DB), no cierra sesión:
 * muestra un error distinto y deja la sesión intacta para reintentar.
 * @param {HTMLButtonElement | null} [submitBtn]
 */
async function handleLogin(submitBtn) {
    if (loginInFlight) return;

    const email = document.getElementById('admin-email')?.value.trim() ?? '';
    const password = document.getElementById('admin-password')?.value ?? '';

    if (!email || !password) {
        showToast('Completá email y contraseña.', { level: 'warning' });
        return;
    }

    loginInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        await signInStaff({ email, password });
        const result = await getStaffRole();

        if (!result.ok) {
            // No pudimos verificar el rol (transitorio): NO cerramos sesión.
            // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
            console.error('handleLogin: no se pudo verificar el rol', result.error);
            showToast(VERIFY_ERROR_MSG, { level: 'error' });
            return;
        }

        if (!isStaff(result.role)) {
            // Usuario válido pero sin permisos de staff: cerramos la sesión.
            await signOut();
            showToast('Tu cuenta no tiene acceso al panel.', { level: 'error' });
            await renderAdmin();
            return;
        }

        showToast('Bienvenido.', { level: 'success' });
        await renderAdmin();
    } catch (err) {
        window.Sentry?.captureException(err);
        showToast(err.message || 'No pudimos iniciar sesión.', { level: 'error' });
    } finally {
        loginInFlight = false;
        if (submitBtn) submitBtn.disabled = false;
    }
}

/**
 * Cierra la sesión y vuelve al formulario de login.
 */
async function handleLogout() {
    // Tear the section down and clear it before waiting for signOut: a slow
    // sign-out must not leave background work armed (e.g. the Preguntas
    // "finished search" timer recording a search after "Cerrar sesión",
    // staff-faq B3), and with the section's DOM gone nothing can re-arm it.
    // renderAdmin() below runs the teardown again; every teardown is idempotent.
    teardownActiveSection();
    const contenido = getContentMount();
    if (contenido) contenido.innerHTML = '';
    try {
        await signOut();
    } catch (err) {
        window.Sentry?.captureException(err);
    }
    // P4: cleared unconditionally (success or failure) — a stale Fudo
    // directory served to a different admin who signs into the same tab, or
    // after Fudo access was revoked, is worse than a needless refetch.
    invalidateFudoDirectoryCache();
    showToast('Sesión cerrada.', { level: 'info' });
    await renderAdmin();
}

// ---------------------------------------------------------------------------
// Secciones — orquestación del shell (Reservas [incl. Bloqueos] | Configuración)
// ---------------------------------------------------------------------------

/**
 * Registro de secciones del panel (staff-admin-access-split, AD7/AD8): una
 * entrada por tab con su `mount` y, si posee estado de fondo (timers,
 * listeners), su `teardown`. El campo `panel` ('staff' | 'admin') alimenta
 * DOS gates: el teardown genérico (`teardownActiveSection`/`stopAdminPanel`,
 * Slice A) y, desde D1, el gate de `handleTabClick` — un `data-tab` cuyo
 * `SECTION_REGISTRY[tab].panel` no coincide con `activePanel` nunca monta
 * (D1.3). El dispatch de montaje en sí sigue siendo el if/else de
 * `renderActiveTab`, no una lookup contra este registro — el registro es la
 * fuente de verdad del gate, no (todavía) del dispatch.
 */
export const SECTION_REGISTRY = {
    reservas: { panel: 'staff', mount: mountReservasSection },
    bloqueos: { panel: 'staff', mount: mountBlocksSection },
    fichaje: { panel: 'staff', mount: mountFichajeSection, teardown: stopFichaje },
    // staff-faq: Preguntas (staff, read-only) — its teardown cancels a pending
    // "finished search" so it is never recorded after the employee left (B3).
    faq: { panel: 'staff', mount: mountFaqStaffSection, teardown: stopFaqStaff },
    // staff-reportes: send a signed message to Caro (staff) and read them
    // (admin). Teardowns make a response still in flight stale.
    reportes: { panel: 'staff', mount: mountReportesStaffSection, teardown: stopReportesStaff },
    // staff-giftcards: issue and redeem (staff), states/totals/void (admin).
    // Teardowns make a response still in flight stale and end a pending print.
    giftcards: { panel: 'staff', mount: mountGiftcardsStaffSection, teardown: stopGiftcardsStaff },
    empleados: { panel: 'admin', mount: mountEmpleadosSection },
    fichadas: { panel: 'admin', mount: mountFichadasSection },
    // review-ae437ea91679bca9: leaving this tab (or logging out) with a
    // comprobante open must restore document.title, the print body class and
    // the Escape listener; mountLiquidacionPanel only covers RETURNING here.
    // review-62957fd6e8912f8e: a leftover bulk-print body class must be
    // cleared on the same leave, not just on the next remount.
    liquidacion: { panel: 'admin', mount: mountLiquidacionSection, teardown: teardownLiquidacionSection },
    // staff-faq: FAQ editor and its Búsquedas sub-item (admin only). Their
    // teardowns make an action still in flight stale (no toast or reload for
    // whoever uses the panel next).
    'faq-admin': { panel: 'admin', mount: mountFaqAdminSection, teardown: stopFaqAdmin },
    'faq-busquedas': { panel: 'admin', mount: mountFaqBusquedasSection, teardown: stopFaqBusquedas },
    'reportes-admin': { panel: 'admin', mount: mountReportesAdminSection, teardown: stopReportesAdmin },
    'giftcards-admin': { panel: 'admin', mount: mountGiftcardsAdminSection, teardown: stopGiftcardsAdmin },
};

/** Combined teardown for the "liquidación" tab (review-62957fd6e8912f8e). @see SECTION_REGISTRY */
function teardownLiquidacionSection() {
    teardownLiquidacionComprobante();
    teardownLiquidacionComprobantesLote();
}

/**
 * Corre el teardown de la sección activa, si tiene uno. Idempotente y segura
 * cuando nunca se montó ninguna sección (`activeTab` sin `teardown` ⇒ no-op),
 * y cuando se la llama más de una vez seguida (cada `teardown` registrado ya
 * es idempotente por sí mismo — ver `stopFichaje`).
 */
function teardownActiveSection() {
    SECTION_REGISTRY[activeTab]?.teardown?.();
}

/**
 * Teardown del panel completo (AD8): lo llama `router.js`'s `hideAllViews()`
 * en TODA transición de ruta, reemplazando el caso especial que tenía el
 * kiosco standalone (`stopKiosco`). Reusa `teardownActiveSection` — sigue
 * siendo idempotente y segura si el panel nunca se montó.
 */
export function stopAdminPanel() {
    // Resilience fix (review-cdd089eda05d7e2e): `router.js`'s hideAllViews()
    // calls this on EVERY navigation, including one that leaves /admin
    // entirely while a `renderAdmin()` triggered from a previous click is
    // still awaiting `getCurrentSession()`/`getStaffRole()`. Without also
    // bumping `renderSeq` here, that in-flight call's own checkpoints
    // (`seq !== renderSeq`, finding 5 of review-ea4f2a93a19e6939) never trip
    // — it can still pass every check and write a stale shell/section into
    // the now-hidden container once its promises finally resolve, silently
    // flipping `activePanel` back too. Bumping `renderSeq` invalidates it the
    // same way a NEWER `renderAdmin()` call already does.
    renderSeq += 1;
    teardownActiveSection();
}

/** @returns {HTMLElement | null} */
function getContentMount() {
    return DOM.adminContainer?.querySelector('#admin-content-mount') ?? null;
}

/** @returns {HTMLElement | null} */
function getAdminSidebar() {
    return DOM.adminContainer?.querySelector('#admin-sidebar') ?? null;
}

/** @returns {HTMLElement | null} */
function getAdminSidebarOverlay() {
    return DOM.adminContainer?.querySelector('#admin-sidebar-overlay') ?? null;
}

/**
 * Abre el drawer de la rail en mobile (equivalente local de ui.js/toggleSidebar):
 * quita el `-translate-x-full` del aside y muestra el overlay. En desktop la rail
 * es `lg:static lg:translate-x-0`, así que esto es un no-op visual.
 */
function openAdminSidebar() {
    getAdminSidebar()?.classList.remove('-translate-x-full');
    getAdminSidebarOverlay()?.classList.remove('hidden');
}

/** Cierra el drawer de la rail en mobile. */
function closeAdminSidebar() {
    getAdminSidebar()?.classList.add('-translate-x-full');
    getAdminSidebarOverlay()?.classList.add('hidden');
}

/**
 * Actualiza la marca visual (`aria-selected` + clases) de los items de nav
 * según `activeTab`. No reemplaza los botones (sus listeners persisten), sólo
 * alterna su estado — por eso no hace falta rebindear. Togglea TODAS las clases
 * de los sets activo/inactivo (no un par fijo).
 */
function updateTabButtons() {
    const container = DOM.adminContainer;
    if (!container) return;
    for (const btn of container.querySelectorAll('[data-action="admin-tab"]')) {
        const isActive = btn.dataset.tab === activeTab;
        btn.setAttribute('aria-selected', String(isActive));
        for (const cls of TAB_ACTIVE_CLASSES) btn.classList.toggle(cls, isActive);
        for (const cls of TAB_INACTIVE_CLASSES) btn.classList.toggle(cls, !isActive);
    }
}

/**
 * Renderiza la sección activa dentro del content mount. Cada sección reemplaza
 * por completo el content mount, así que las secciones de las otras (y sus
 * mount nodes) desaparecen del DOM — los guards de resolución de cada flujo
 * (`getReservationsMount`/`getBlocksMount`/`getConfigMount` → null) descartan
 * efectos huérfanos de una mutación en vuelo al cambiar de sección.
 */
function renderActiveTab() {
    updateTabButtons();
    const mount = getContentMount();
    if (!mount) return;

    if (activeTab === 'config') {
        mountConfigSection(mount);
    } else if (activeTab === 'categorias') {
        mountCategoriesSection(mount);
    } else if (activeTab === 'bloqueos') {
        mountBlocksSection(mount);
    } else if (activeTab === 'fichaje') {
        mountFichajeSection(mount);
    } else if (activeTab === 'empleados') {
        mountEmpleadosSection(mount);
    } else if (activeTab === 'fichadas') {
        mountFichadasSection(mount);
    } else if (activeTab === 'liquidacion') {
        mountLiquidacionSection(mount);
    } else if (activeTab === 'faq') {
        mountFaqStaffSection(mount);
    } else if (activeTab === 'faq-admin') {
        mountFaqAdminSection(mount);
    } else if (activeTab === 'faq-busquedas') {
        mountFaqBusquedasSection(mount);
    } else if (activeTab === 'reportes') {
        mountReportesStaffSection(mount);
    } else if (activeTab === 'reportes-admin') {
        mountReportesAdminSection(mount);
    } else if (activeTab === 'giftcards') {
        mountGiftcardsStaffSection(mount);
    } else if (activeTab === 'giftcards-admin') {
        mountGiftcardsAdminSection(mount);
    } else {
        mountReservasSection(mount);
    }
}

/**
 * Cambia de sección: cierra el drawer mobile, ignora un click sobre la sección
 * ya activa, resetea el estado pendiente de la sección que se abandona (para no
 * dejar diálogos/acciones colgando) y renderiza la nueva sección.
 * @param {MouseEvent} e
 */
function handleTabClick(e) {
    // Cerrar el drawer aunque se re-clickee la sección activa (feedback mobile).
    closeAdminSidebar();
    activarSeccion(e.currentTarget.dataset.tab);
}

/**
 * Cambio de sección compartido por la nav (`handleTabClick`) y el botón "Ir
 * a Fichadas" del banner de jornadas abiertas (F-B) — mismos gates, mismo
 * teardown, un solo camino. En el panel admin, además, vuelve a leer el
 * banner y el contador de Reportes: es la forma barata de que reflejen lo
 * que cambió desde otra terminal (el kiosco, el panel staff) sin un timer
 * de fondo.
 * @param {string|undefined} tab
 */
function activarSeccion(tab) {
    if (!tab || tab === activeTab) return;

    // D1.3 — gate registry-driven, no una lista hardcodeada: un click cuyo
    // `data-tab` no pertenece al panel activo (forjado en devtools, o un
    // remanente de un panel anterior) se ignora sin tocar `activeTab` ni
    // montar nada. Defensa en profundidad — el límite real sigue siendo
    // RLS/`is_admin()` (slice C, todavía no endurecida).
    if (SECTION_REGISTRY[tab]?.panel !== activePanel) return;

    // AD8: teardown de la sección que se abandona ANTES de cambiar de tab —
    // hoy es el único disparador de `stopFichaje` cuando se sale de Fichaje.
    teardownActiveSection();

    // Al abandonar reservas, soltamos cualquier mutación/borrado pendiente: el
    // content mount se reemplaza abajo, así que una mutación en vuelo verá su
    // mount === null y descartará su Toast/refresh.
    activeReservaMutation = null;
    pendingBlockDelete = null;
    categoriesViewModel = null;
    categoryRulesMap = null;
    empleadoEditing = null;
    empleadoPinDialog = null;
    fichadaDialog = null;
    pendingLiquidacionConfirm = null;
    pendingLiquidacionAnular = null;

    activeTab = tab;
    renderActiveTab();
    if (activePanel === 'admin') {
        void refreshJornadasAbiertasBanner();
        void refreshReportesBadge();
    }
}

/** @returns {HTMLElement | null} */
function getJornadasBannerMount() {
    return DOM.adminContainer?.querySelector('#admin-jornadas-abiertas-banner') ?? null;
}

/**
 * Lee y pinta el banner de jornadas abiertas hace más de 12 h (F-B, owner
 * decision 2026-09-27). Solo existe en el panel admin (`AdminShell` no crea
 * el mount en el panel staff), así que sin mount no lee nada. Una lectura
 * que falla NUNCA bloquea la página: se loguea y el banner se oculta — es un
 * aviso, no una sección.
 */
async function refreshJornadasAbiertasBanner() {
    if (!getJornadasBannerMount()) return;
    const seq = ++refreshJornadasBannerSeq;
    let mensaje = '';
    try {
        ({ mensaje } = await loadJornadasAbiertasOlvidadas());
    } catch (err) {
        if (seq !== refreshJornadasBannerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshJornadasAbiertasBanner: no se pudieron leer las jornadas abiertas', err);
    }
    const mount = getJornadasBannerMount();
    if (seq !== refreshJornadasBannerSeq || !mount) return;
    mount.innerHTML = JornadasAbiertasBanner({ mensaje });
    mount.hidden = !mensaje;
    mount.querySelector('[data-action="jornadas-abiertas-ir"]')?.addEventListener('click', () => activarSeccion('fichadas'));
}

/**
 * Monta la sección de reservas dentro del content mount: crea los mount nodes
 * de la lista de reservas + el modal de alta, y dispara la primera carga. Las
 * transiciones de estado son inmediatas (sin diálogo de confirmación), así que
 * ya no hay un mount de confirmación. Bloqueos es su propia subsección
 * (ver mountBlocksSection).
 * @param {HTMLElement} mount
 */
function mountReservasSection(mount) {
    mount.innerHTML = `
        <div id="admin-reservations-mount"></div>
        <div id="admin-reserva-create-mount"></div>
    `;
    mountReservationsView();
}

/**
 * Monta la subsección de Bloqueos (ahora su propia sección de nav, ya no
 * embebida bajo Reservas). Crea sus mount nodes y dispara la primera carga.
 * @param {HTMLElement} mount
 */
function mountBlocksSection(mount) {
    mount.innerHTML = `
        <div id="admin-blocks-mount"></div>
        <div id="admin-block-confirm-mount"></div>
    `;
    mountBlocksPanel();
}

/**
 * Monta la sección de Empleados (Control de Horarios y Liquidación de
 * Sueldos, slice 1): crea su mount node y dispara la primera carga.
 * @param {HTMLElement} mount
 */
function mountEmpleadosSection(mount) {
    mount.innerHTML = '<div id="admin-empleados-mount"></div>';
    mountEmpleadosPanel();
}

/**
 * Monta la subsección de Fichadas (Control de Horarios y Liquidación de
 * Sueldos, slice 3 — Auditoría), sub-item de Empleados en el nav (mismo
 * patrón que Bloqueos bajo Reservas): crea su mount node y dispara la
 * primera carga.
 * @param {HTMLElement} mount
 */
function mountFichadasSection(mount) {
    mount.innerHTML = '<div id="admin-fichadas-mount"></div>';
    mountFichadasPanel();
}

/**
 * Monta la sección de Liquidación (Control de Horarios y Liquidación de
 * Sueldos, slice 5; Unit S2a design.md AD1 la extiende a 7 mounts
 * hermanos — los 7 ya están acá desde S7): el mount NUEVO del tablero de período
 * (`admin-liquidacion-tablero-mount`, PRIMERO en el DOM — reemplaza al
 * selector mes/empleados como la forma principal de liquidar, AD1) + el
 * mount EXISTENTE del selector/historial/configuración (`admin-liquidacion-mount`,
 * que sigue debajo, sin cambios) + el mount SEPARADO del cajón compartido
 * (`admin-liquidacion-drawer-mount`, Unit S4/AD7 — adelantado desde su
 * atribución nominal "S3" en design.md porque el ship order real pone S4
 * ANTES de S3; `DrawerShell.js` + `AdelantoDrawerView.js` de ESTA unidad,
 * `AnomaliasDrawerView.js` de S3 reusa el MISMO mount y shell) + el mount
 * SEPARADO del `ConfirmDialog` (mismo patrón que Bloqueos —
 * `admin-block-confirm-mount` — así abrir/cerrar la confirmación nunca
 * repinta el preview) + el mount SEPARADO del `AnularLiquidacionDialog`
 * (Unit B2, design.md AD12 — mismo criterio: anular una fila, o mostrar un
 * error de submit, nunca debe repintar el selector ni el historial) + el
 * mount SEPARADO del comprobante (Unit A2, design.md AD5 — abrir/cerrar el
 * comprobante jamás repinta el selector, el preview ni el historial, y al no
 * ser descendiente de `#admin-liquidacion-mount` puede quedar visible cuando
 * el `@media print` de AD7 oculta todo lo demás) + el mount SEPARADO del
 * print en lote (Unit S7, design.md AD16 — `#admin-liquidacion-comprobantes-lote-mount`,
 * 7mo y último hermano, PARALELO al del comprobante individual: su propio
 * body class + bloque `@media print`, nunca toca el gate individual) y
 * dispara la primera carga (el listado de empleados alimenta el tablero y
 * las búsquedas de nombre del historial/confirm — `mountLiquidacionPanel`
 * dispara ambos).
 * @param {HTMLElement} mount
 */
function mountLiquidacionSection(mount) {
    mount.innerHTML = `
        <div id="admin-liquidacion-tablero-mount"></div>
        <div id="admin-liquidacion-mount"></div>
        <div id="admin-liquidacion-drawer-mount"></div>
        <div id="admin-liquidacion-confirm-mount"></div>
        <div id="admin-liquidacion-anular-mount"></div>
        <div id="admin-liquidacion-comprobante-mount"></div>
        <div id="admin-liquidacion-comprobantes-lote-mount"></div>
    `;
    mountLiquidacionPanel();
}

/**
 * Fecha local de hoy en formato ISO `YYYY-MM-DD` (hora local, no `toISOString()`),
 * usada para prellenar el date-picker con el límite inferior por defecto
 * (Req: Default Window and Date Picker).
 * @param {Date} [now]
 * @returns {string}
 */
function todayLocalISO(now = new Date()) {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Normaliza el valor del date-picker: un valor vacío o no-ISO (`<input
 * type="date">` sin selección) se trata como "hoy" (mismo default que el
 * servicio, Req: Default Window and Date Picker) en lugar de consultar con un
 * límite inferior vacío (`.gte('fecha','')` = traer todo).
 * @param {string} value
 * @returns {string} fecha ISO YYYY-MM-DD
 */
function normalizeFrom(value) {
    return ISO_DATE_RE.test(value) ? value : todayLocalISO();
}

/** @returns {HTMLElement | null} */
function getReservationsMount() {
    return DOM.adminContainer?.querySelector('#admin-reservations-mount') ?? null;
}

/** @returns {HTMLElement | null} */
function getReservaCreateMount() {
    return DOM.adminContainer?.querySelector('#admin-reserva-create-mount') ?? null;
}

/**
 * Monta la vista de reservas dentro del shell recién renderizado y dispara la
 * primera carga. Solo se llama cuando `pickAdminView` resolvió `'shell'`.
 * Resetea todo el estado local (ui-state, modelo, acción pendiente, guard) para
 * no arrastrar estado de una sesión de staff anterior.
 */
function mountReservationsView() {
    const mount = getReservationsMount();
    if (!mount) return;

    reservationsViewModel = null;
    reservationsMode = 'activas';
    activeReservaMutation = null;
    reservationMutationInFlight = false;
    reservaCreateInFlight = false;
    reservaFormMode = null;
    reservaEditando = null;
    resetMesasSeleccion();

    const from = todayLocalISO();
    reservationsUiState = { status: 'loading', from, mode: reservationsMode };

    renderReservationsMount();
    renderReservaCreateModal();
    void refreshReservations(from);
}

/**
 * Renderiza el ui-state actual (loading/error/ready) dentro del mount node de
 * reservas y re-cablea sus listeners (el mount se reemplaza por completo en
 * cada render, así que los listeners deben rebindearse — mismo patrón que
 * `setupAdminListeners` tras cada render completo del shell).
 */
function renderReservationsMount() {
    const mount = getReservationsMount();
    if (!mount) return;
    mount.innerHTML = ReservationsView(reservationsViewModel, reservationsUiState);
    bindReservationsMountListeners(mount);
}



/**
 * Lee el plano de mesas para el turno que dice EL FORMULARIO, no la reserva.
 *
 * Mientras el modal está abierto el operador puede cambiar fecha, momento o
 * área; la ocupación se calcula por `(fecha, momento)`, así que el plano tiene
 * que seguir al formulario. Tomarlo de la reserva mostraría la ocupación del
 * turno viejo y dejaría elegir una mesa que en el turno nuevo está tomada.
 *
 * @returns {{ id: string|null, fecha: string, momento: string, area: string } | null}
 */
function turnoDelFormulario() {
    const form = getReservaFormEl();
    if (!form) return null;

    const fecha = form.querySelector('[name="fecha"]')?.value ?? '';
    const momento = form.querySelector('[name="momento"]')?.value ?? '';
    const area = form.querySelector('[name="area"]')?.value ?? '';
    if (!fecha || !momento || !area) return null;

    return { id: reservaEditando?.id ?? null, fecha, momento, area };
}

/** @returns {HTMLFormElement|null} */
function getReservaFormEl() {
    return getReservaCreateMount()?.querySelector('form') ?? null;
}

/**
 * Recarga el plano para el turno actual del formulario y repinta SOLO el
 * contenedor del plano, no el modal entero: un re-render completo perdería lo
 * que el operador ya tipeó en los campos.
 */
async function refreshMesasPlan() {
    const turno = turnoDelFormulario();
    const mount = getMesasSlot();
    if (!mount) return;

    if (!turno) {
        mesasPlan = null;
        mesasUiState = { status: 'error', message: 'Elegí fecha, momento y área para ver el plano.' };
        renderMesasSlot();
        return;
    }

    const token = ++mesasPlanSeq;
    mesasUiState = { status: 'loading' };
    renderMesasSlot();

    try {
        const { plan, asignadas } = await loadFloorPlan({ reservation: turno });
        if (token !== mesasPlanSeq || !getMesasSlot()) return;
        mesasPlan = plan;
        // En el alta no hay nada asignado todavía; en la edición, lo que ya
        // tiene la reserva es el punto de partida de la selección.
        mesasAsignadas = asignadas;
        if (!mesasSeleccionTocada) mesasSeleccion = new Set(asignadas.map((m) => m.id));
        mesasUiState = { status: 'ready' };
    } catch {
        if (token !== mesasPlanSeq || !getMesasSlot()) return;
        mesasUiState = { status: 'error', message: MESAS_LOAD_ERROR_MSG };
    }
    renderMesasSlot();
}

/** @returns {HTMLElement|null} contenedor del plano dentro del formulario. */
function getMesasSlot() {
    return getReservaCreateMount()?.querySelector('#admin-mesas-slot') ?? null;
}

/** HTML del plano según el ui-state actual. */
function mesasSlotHtml() {
    if (mesasUiState.status === 'loading') {
        return '<p class="py-6 text-center text-sm text-text-muted">Cargando el plano…</p>';
    }
    if (mesasUiState.status === 'error') {
        return `<p class="py-6 text-center text-sm text-text-muted">${escapeHTML(mesasUiState.message ?? '')}</p>`;
    }
    return MesaMapa({
        plan: applySeleccion(mesasPlan, mesasSeleccion),
        reservationId: reservaEditando?.id ?? null,
    });
}

/** Repinta el plano y recablea sus botones. */
function renderMesasSlot() {
    const slot = getMesasSlot();
    if (!slot) return;
    slot.innerHTML = mesasSlotHtml();

    for (const btn of slot.querySelectorAll('[data-action="mesa-toggle"]')) {
        btn.addEventListener('click', handleMesaToggle);
    }
    const syncBtn = slot.querySelector('[data-action="mesas-sincronizar"]');
    if (syncBtn) syncBtn.addEventListener('click', handleMesasSync);
}

/**
 * Renderiza (o limpia) el modal de reserva según `reservaFormMode`.
 * `null` = cerrado.
 */
function renderReservaCreateModal() {
    const mount = getReservaCreateMount();
    if (!mount) return;

    if (!reservaFormMode) {
        mount.innerHTML = '';
        return;
    }

    mount.innerHTML = NuevaReservaModal({
        mode: reservaFormMode,
        mesasHtml: '<div id="admin-mesas-slot"></div>',
    });
    bindReservaCreateModalListeners(mount);

    if (reservaEditando) prefillReservaForm(getReservaFormEl(), reservaEditando);
    renderMesasSlot();
    void refreshMesasPlan();
}

/**
 * Vuelca una reserva existente en los campos del formulario.
 *
 * `fecha` se inyecta como `<option>` si no está en la lista: el select solo
 * ofrece los próximos días, y una reserva de ayer que se está editando dejaría
 * el campo en otra fecha silenciosamente — moviéndola sin que nadie lo pida.
 * La cascada se dispara a mano en orden (fecha → momento → hora) porque setear
 * `.value` por DOM no emite `change`.
 *
 * @param {HTMLFormElement|null} form
 * @param {object} row
 */
function prefillReservaForm(form, row) {
    if (!form) return;

    const set = (name, value) => {
        const el = form.querySelector(`[name="${name}"]`);
        if (el && value != null) el.value = value;
    };

    set('nombre', row.nombre);
    set('apellido', row.apellido);
    set('telefono', row.telefono ?? '');
    set('personas', row.personas);
    set('area', row.area);
    set('comentarios', row.comentarios ?? '');

    const fechaSelect = form.querySelector('[name="fecha"]');
    const momentoSelect = form.querySelector('[name="momento"]');
    const horaSelect = form.querySelector('[name="hora"]');

    if (fechaSelect) {
        const existe = [...fechaSelect.options].some((o) => o.value === row.fecha);
        if (!existe) {
            fechaSelect.insertAdjacentHTML('afterbegin',
                `<option value="${escapeHTML(row.fecha)}">${escapeHTML(row.fecha)}</option>`);
        }
        fechaSelect.value = row.fecha;
    }
    if (momentoSelect) {
        momentoSelect.innerHTML = momentoOptions(row.fecha);
        const existe = [...momentoSelect.options].some((o) => o.value === row.momento);
        if (!existe) {
            momentoSelect.insertAdjacentHTML('afterbegin',
                `<option value="${escapeHTML(row.momento)}">${escapeHTML(row.momento)}</option>`);
        }
        momentoSelect.value = row.momento;
    }
    if (horaSelect) {
        horaSelect.innerHTML = horaOptions(momentoSelect?.value ?? row.momento);
        if (row.hora) {
            const existe = [...horaSelect.options].some((o) => o.value === row.hora);
            if (!existe) {
                horaSelect.insertAdjacentHTML('afterbegin',
                    `<option value="${escapeHTML(row.hora)}">${escapeHTML(row.hora)}</option>`);
            }
            horaSelect.value = row.hora;
        }
    }
}

/**
 * Cablea los listeners del modal de reserva: cerrar, backdrop, la cascada
 * fecha → momento → hora, y el submit (alta o edición según el modo). Cada
 * cambio de fecha/momento/área recarga el plano, porque la ocupación depende
 * del turno.
 * @param {HTMLElement} mount
 */
function bindReservaCreateModalListeners(mount) {
    const closeBtn = mount.querySelector('[data-action="reserva-nueva-cerrar"]');
    if (closeBtn) closeBtn.addEventListener('click', handleReservaCreateClose);

    const backdrop = mount.querySelector('[data-action="reserva-nueva-backdrop"]');
    if (backdrop) {
        backdrop.addEventListener('click', (e) => {
            if (e.target === e.currentTarget) handleReservaCreateClose();
        });
    }

    const fechaSelect = mount.querySelector('[name="fecha"]');
    const momentoSelect = mount.querySelector('[name="momento"]');
    const horaSelect = mount.querySelector('[name="hora"]');
    const areaSelect = mount.querySelector('[name="area"]');

    if (fechaSelect && momentoSelect && horaSelect) {
        fechaSelect.addEventListener('change', () => {
            momentoSelect.innerHTML = momentoOptions(fechaSelect.value);
            horaSelect.innerHTML = horaOptions(momentoSelect.value);
            void refreshMesasPlan();
        });
    }
    if (momentoSelect && horaSelect) {
        momentoSelect.addEventListener('change', () => {
            horaSelect.innerHTML = horaOptions(momentoSelect.value);
            void refreshMesasPlan();
        });
    }
    if (areaSelect) areaSelect.addEventListener('change', () => void refreshMesasPlan());

    const createBtn = mount.querySelector('[data-action="reserva-crear"]');
    if (createBtn?.closest('form')) createBtn.closest('form').addEventListener('submit', handleReservaCreate);

    const saveBtn = mount.querySelector('[data-action="reserva-guardar"]');
    if (saveBtn?.closest('form')) saveBtn.closest('form').addEventListener('submit', handleReservaGuardar);
}

/** Abre el modal en modo alta. */
function handleReservaCreateOpen() {
    reservaFormMode = 'crear';
    reservaEditando = null;
    resetMesasSeleccion();
    renderReservaCreateModal();
}

/** Abre el modal en modo edición sobre una reserva existente. */
function handleReservaEditar(e) {
    const row = findReservationRow(e.currentTarget.dataset.id);
    if (!row) return;

    reservaFormMode = 'editar';
    reservaEditando = row;
    resetMesasSeleccion();
    renderReservaCreateModal();
}

/** Cierra el modal (✕ / backdrop / alta o edición exitosa). */
function handleReservaCreateClose() {
    reservaFormMode = null;
    reservaEditando = null;
    resetMesasSeleccion();
    renderReservaCreateModal();
}

/** Estado del plano a foja cero (al abrir o cerrar el modal). */
function resetMesasSeleccion() {
    mesasPlan = null;
    mesasAsignadas = [];
    mesasSeleccion = new Set();
    mesasSeleccionTocada = false;
    mesasUiState = { status: 'loading' };
}

/**
 * Marca o desmarca una mesa. La selección es LOCAL hasta guardar, igual en el
 * alta que en la edición: una reserva nueva todavía no tiene id contra el cual
 * asignar, y tener dos comportamientos distintos en la misma pantalla sería
 * peor que esperar al submit.
 */
function handleMesaToggle(e) {
    const mesaId = Number(e.currentTarget.dataset.mesaId);
    mesasSeleccionTocada = true;

    if (mesasSeleccion.has(mesaId)) mesasSeleccion.delete(mesaId);
    else mesasSeleccion.add(mesaId);

    renderMesasSlot();
}

/** Trae el plano de mesas desde Fudo y recarga (estado vacío del plano). */
async function handleMesasSync() {
    const result = await sincronizarMesas();

    if (!result.ok) {
        showToast(result.message, { level: 'error' });
        return;
    }
    if (result.unmapped?.length) {
        showToast(
            `${result.unmapped.length} mesa(s) quedaron sin área. Revisá los salones en Fudo.`,
            { level: 'warning' },
        );
    } else {
        showToast(`${result.total} mesas sincronizadas.`, { level: 'success' });
    }
    await refreshMesasPlan();
}

/**
 * Aplica la selección de mesas a una reserva y reporta los conflictos. Se usa
 * igual después del alta y después de la edición.
 * @param {{ id: string, fecha: string, momento: string, area: string }} reservation
 */
async function guardarMesasDe(reservation) {
    const { conflictos } = await guardarAsignacion({
        reservation,
        actuales: mesasAsignadas,
        seleccion: mesasSeleccion,
    });

    // La reserva ya se guardó; los conflictos de mesa no la invalidan, pero
    // callarlos dejaría al staff creyendo que asignó una mesa que no tiene.
    for (const conflicto of conflictos) {
        showToast(conflicto.message, { level: 'error' });
    }
}

/**
 * Lee y valida los campos del formulario. Devuelve `null` (y avisa) si falta
 * algo — mismos guards que ya tenía el alta manual.
 * @param {HTMLFormElement} form
 * @returns {object|null}
 */
function readReservaForm(form) {
    const get = (name) => form.querySelector(`[name="${name}"]`)?.value ?? '';
    const values = {
        nombre: get('nombre'),
        apellido: get('apellido'),
        telefono: get('telefono'),
        fecha: get('fecha'),
        momento: get('momento'),
        hora: get('hora'),
        personas: get('personas'),
        area: get('area'),
        comentarios: get('comentarios').trim(),
    };

    if (!values.telefono.trim()) {
        showToast(RESERVA_TELEFONO_REQUIRED_MSG, { level: 'error' });
        return null;
    }
    const requeridos = [values.nombre, values.apellido, values.fecha, values.momento, values.hora, values.personas, values.area];
    if (!requeridos.every((v) => String(v).trim())) {
        showToast(RESERVA_ALL_FIELDS_REQUIRED_MSG, { level: 'error' });
        return null;
    }
    return values;
}

/**
 * Guarda la edición de una reserva y luego aplica la selección de mesas.
 *
 * Orden deliberado: primero los datos, después las mesas. Si la reserva cambia
 * de turno, el trigger arrastra sus mesas actuales; recién con la reserva ya
 * movida tiene sentido reconciliar la selección contra el turno nuevo.
 */
async function handleReservaGuardar(e) {
    e.preventDefault();
    if (reservaCreateInFlight || !reservaEditando) return;

    const form = e.currentTarget;
    const values = readReservaForm(form);
    if (!values) return;

    const submitBtn = form.querySelector('[data-action="reserva-guardar"]');
    reservaCreateInFlight = true;
    if (submitBtn) submitBtn.disabled = true;

    try {
        const result = await editReservation(reservaEditando.id, {
            ...values,
            telefono: values.telefono || null,
            hora: values.hora || null,
        });

        if (!getReservationsMount()) return;
        if (!result.ok) {
            showToast(result.error, { level: 'error' });
            return;
        }

        await guardarMesasDe({
            id: reservaEditando.id,
            fecha: values.fecha,
            momento: values.momento,
            area: values.area,
        });

        showToast(RESERVA_EDIT_SUCCESS_MSG, { level: 'success' });
        reservaFormMode = null;
        reservaEditando = null;
        resetMesasSeleccion();
        renderReservaCreateModal();
        await refreshReservations(reservationsUiState.from, { silent: true });
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleReservaGuardar: no se pudo editar la reserva', err);
        if (!getReservationsMount()) return;
        showToast(RESERVA_EDIT_ERROR_MSG, { level: 'error' });
    } finally {
        reservaCreateInFlight = false;
        if (submitBtn) submitBtn.disabled = false;
    }
}

/**
 * Busca una fila de reserva en el modelo de vista actual (activas: agrupado
 * por día; archivadas: lista plana).
 * @param {string} id
 * @returns {object|null}
 */
function findReservationRow(id) {
    const groups = reservationsViewModel?.groups ?? [];
    for (const group of groups) {
        const found = group.rows.find((row) => row.id === id);
        if (found) return found;
    }
    return reservationsViewModel?.rows?.find((row) => row.id === id) ?? null;
}


/**
 * Recarga las reservas y re-renderiza. En modo `activas` carga la lista
 * agrupada por día para la ventana `fecha >= from` (Req: Default Window and
 * Date Picker); en modo `archivadas` carga la lista plana de solo-lectura
 * (`loadArchived`, ignora `from`). `loading` mientras está en vuelo, luego
 * `ready` con el modelo resuelto o `error` si el fetch falla.
 * @param {string} from fecha ISO (YYYY-MM-DD) — solo aplica en modo activas
 */
async function refreshReservations(from, { silent = false } = {}) {
    refreshReservationsSeq += 1;
    const seq = refreshReservationsSeq;
    const mode = reservationsMode;

    if (silent) {
        // Refresh silencioso (reconciliación tras una transición de estado):
        // NO pintamos el estado `loading`. La lista actual queda visible en
        // pantalla mientras el fetch está en vuelo, así no hay flash de
        // spinner que se sienta como "se recargó toda la página".
        reservationsUiState = { ...reservationsUiState, status: 'ready', from, mode };
    } else {
        reservationsUiState = { status: 'loading', from, mode };
        renderReservationsMount();
    }

    try {
        const viewModel = await conTimeoutDeCarga(mode === 'archivadas' ? loadArchived() : loadUpcoming({ from }));
        // Descarta la respuesta si otra carga posterior ya la superó (evita pisar
        // la vista y el date-picker con datos de una ventana/modo viejos).
        if (seq !== refreshReservationsSeq) return;
        reservationsViewModel = viewModel;
        reservationsUiState = { status: 'ready', from, mode };
    } catch (err) {
        if (seq !== refreshReservationsSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshReservations: no se pudieron cargar las reservas', err);
        // En un refresh silencioso con datos ya en pantalla, mantenemos la
        // lista visible en vez de reemplazarla por la pantalla de error (el
        // fallo de reconciliación no debería borrar lo que el staff ya ve).
        reservationsUiState =
            silent && reservationsViewModel
                ? { ...reservationsUiState, status: 'ready', from, mode }
                : { status: 'error', from, mode };
    }

    renderReservationsMount();
}

/**
 * Deriva un NUEVO viewModel con el efecto optimista de una transición, para
 * reflejar el cambio al instante (sin esperar al server ni flashear un
 * spinner); el refresh silencioso posterior reconcilia con la verdad del
 * server. Es INMUTABLE a propósito: clona grupos/filas en vez de mutar el
 * objeto actual, así el snapshot previo (`prevViewModel`) sigue intacto para
 * poder revertir sin un refetch si la mutación falla.
 *
 * Devuelve `null` si el viewModel todavía no está cargado o la acción no aplica
 * (el caller entonces no hace update optimista).
 * @param {string} id
 * @param {'confirmar'|'cancelar'|'terminar'|'archivar'} action
 * @returns {object|null}
 */
const OPTIMISTIC_STATUS_BY_ACTION = {
    confirmar: 'confirmada',
    cancelar: 'cancelada',
    terminar: 'terminada',
};
function computeOptimisticViewModel(id, action) {
    const vm = reservationsViewModel;
    const groups = vm?.groups;
    if (!Array.isArray(groups)) return null;

    if (action === 'archivar') {
        // Archivar saca la reserva de la lista activa: la quitamos de su grupo
        // y descartamos los grupos que quedan vacíos.
        const nextGroups = groups
            .map((group) => ({ ...group, rows: group.rows.filter((row) => row.id !== id) }))
            .filter((group) => group.rows.length > 0);
        return { ...vm, groups: nextGroups, isEmpty: nextGroups.length === 0 };
    }

    const nextStatus = OPTIMISTIC_STATUS_BY_ACTION[action];
    if (!nextStatus) return null;
    const nextGroups = groups.map((group) => ({
        ...group,
        rows: group.rows.map((row) => (row.id === id ? { ...row, status: nextStatus } : row)),
    }));
    return { ...vm, groups: nextGroups };
}

/**
 * Cablea los listeners del mount node de reservas: botón de abrir el modal
 * "Nueva reserva", picker de fecha, el toggle activas/archivadas, los botones
 * de estado segmentados (`reserva-set-estado`), el botón archivar
 * (`reserva-archivar`) y reintentar.
 * Se llama después de CADA render del mount (el innerHTML se reemplaza
 * entero, así que no hay listeners que sobrevivan de un render al
 * siguiente).
 * @param {HTMLElement} mount
 */
function bindReservationsMountListeners(mount) {
    const openBtn = mount.querySelector('[data-action="reserva-nueva-abrir"]');
    if (openBtn) openBtn.addEventListener('click', handleReservaCreateOpen);

    const toggleBtn = mount.querySelector('[data-action="reserva-toggle-archivadas"]');
    if (toggleBtn) toggleBtn.addEventListener('click', handleReservaToggleArchivadas);

    const picker = mount.querySelector('[data-action="reserva-change-window"]');
    if (picker) {
        picker.addEventListener('change', (e) => {
            void refreshReservations(normalizeFrom(e.target.value));
        });
    }

    for (const btn of mount.querySelectorAll('[data-action="reserva-set-estado"]')) {
        btn.addEventListener('click', handleReservaSetEstado);
    }

    for (const btn of mount.querySelectorAll('[data-action="reserva-archivar"]')) {
        btn.addEventListener('click', handleReservaArchivar);
    }

    for (const btn of mount.querySelectorAll('[data-action="reserva-editar"]')) {
        btn.addEventListener('click', handleReservaEditar);
    }

    const retryBtn = mount.querySelector('[data-action="reserva-retry"]');
    if (retryBtn) {
        retryBtn.addEventListener('click', () => {
            void refreshReservations(reservationsUiState.from);
        });
    }
}

/**
 * Alterna el modo de la vista entre `activas` y `archivadas` y recarga. El
 * toggle solo está disponible cuando la vista de reservas está montada.
 */
function handleReservaToggleArchivadas() {
    reservationsMode = reservationsMode === 'archivadas' ? 'activas' : 'archivadas';
    void refreshReservations(reservationsUiState.from);
}

/**
 * Click en un botón de estado segmentado (`reserva-set-estado`): mapea el
 * `data-estado` a su acción de transición y la aplica de inmediato (sin
 * diálogo). Click sobre el estado ya activo es un no-op inofensivo desde el
 * server (el `.in('status', from)` incluye el estado actual → `{ changed: true }`
 * pero sin cambio real de estado).
 * @param {MouseEvent} e
 */
function handleReservaSetEstado(e) {
    const target = e.currentTarget;
    const action = RESERVA_ESTADO_ACTIONS[target.dataset.estado];
    if (!action) return;
    void applyReservaTransition(target.dataset.id, action);
}

/**
 * Click en el botón "Archivar" (`reserva-archivar`): aplica la transición
 * `archivar` de inmediato. El server la guardea a cancelada/terminada.
 * @param {MouseEvent} e
 */
function handleReservaArchivar(e) {
    void applyReservaTransition(e.currentTarget.dataset.id, 'archivar');
}

/**
 * Aplica una transición de estado de forma INMEDIATA (sin diálogo de
 * confirmación) y reconcilia la lista.
 *
 * Re-entrancy (`reservationMutationInFlight`): si ya hay una mutación en curso,
 * ignora el click — evita dos transiciones concurrentes de un doble-click.
 *
 * Guard de resolución (`activeReservaMutation`): si al resolver el usuario ya
 * cambió de sección/modo o cerró sesión (la vista dejó de estar montada, o
 * llegó otra mutación), descartamos silenciosamente los efectos — nada de Toast
 * huérfano ni refresh sobre un cliente deslogueado / una pantalla distinta.
 *
 * `changed: true` → Toast de éxito per-acción + recarga. `changed: false` →
 * transición stale (0 filas afectadas, Req: "Concurrent transition is
 * rejected"): Toast informativo breve + recarga para reflejar el estado actual.
 * Un throw real (red/permiso) muestra un Toast de error genérico y NO recarga.
 * @param {string} id
 * @param {'confirmar'|'cancelar'|'terminar'|'archivar'} action
 */
async function applyReservaTransition(id, action) {
    if (reservationMutationInFlight) return;
    const mutation = { id, action };
    reservationMutationInFlight = true;
    activeReservaMutation = mutation;

    // Feedback instantáneo: reflejamos el cambio en la vista de inmediato
    // (sin spinner) y reconciliamos con el server en segundo plano. Solo en
    // modo activas; archivadas es read-only. Guardamos el snapshot previo para
    // poder revertir sin refetch si la mutación falla.
    const optimistic = reservationsMode === 'activas';
    const prevViewModel = reservationsViewModel;
    if (optimistic) {
        const nextViewModel = computeOptimisticViewModel(id, action);
        if (nextViewModel) {
            reservationsViewModel = nextViewModel;
            renderReservationsMount();
        }
    }

    try {
        const { changed } = await transition(id, action);

        if (activeReservaMutation !== mutation || !getReservationsMount()) return;

        if (changed) {
            showToast(RESERVA_SUCCESS_MESSAGES[action], { level: 'success' });
        } else {
            showToast(RESERVA_STALE_MSG, { level: 'info' });
        }

        // Refresh SILENCIOSO: reconcilia con la verdad del server (capacidad,
        // orden, transiciones stale) sin flashear el estado de loading.
        await refreshReservations(reservationsUiState.from, { silent: true });
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('applyReservaTransition: no se pudo actualizar la reserva', err);
        if (activeReservaMutation !== mutation || !getReservationsMount()) return;
        showToast(RESERVA_MUTATION_ERROR_MSG, { level: 'error' });
        // Revertimos el cambio optimista restaurando el snapshot en memoria —
        // SIN refetch: un fallo de mutación no dispara una recarga de la lista.
        if (optimistic) {
            reservationsViewModel = prevViewModel;
            renderReservationsMount();
        }
    } finally {
        reservationMutationInFlight = false;
        if (activeReservaMutation === mutation) activeReservaMutation = null;
    }
}

/**
 * Procesa el submit del formulario "Nueva reserva" (alta manual de staff): lee
 * los campos, exige teléfono (guard presentacional, ver abajo), llama al
 * servicio y refleja el resultado. `{ ok: true }` cierra el
 * modal + recarga la lista + Toast de éxito; `{ ok: false }` muestra el error en
 * español que devolvió el endpoint (p. ej. "Sin cupo...") SIN recargar y deja el
 * modal abierto para corregir; un throw real (red/timeout) muestra un Toast
 * genérico. Mismo patrón que `handleBlockCreate`.
 *
 * Re-entrancy: `reservaCreateInFlight` evita dobles envíos y deshabilita el
 * botón mientras dura. Guard de resolución: si la vista de reservas ya no está
 * montada (cambio de sección / logout), descarta los efectos.
 * @param {SubmitEvent} e
 */
async function handleReservaCreate(e) {
    e.preventDefault();
    if (reservaCreateInFlight) return;

    const form = e.currentTarget;
    const nombre = form.querySelector('[name="nombre"]')?.value ?? '';
    const apellido = form.querySelector('[name="apellido"]')?.value ?? '';
    const telefono = form.querySelector('[name="telefono"]')?.value ?? '';
    const fecha = form.querySelector('[name="fecha"]')?.value ?? '';
    const momento = form.querySelector('[name="momento"]')?.value ?? '';
    const hora = form.querySelector('[name="hora"]')?.value ?? '';
    const personas = form.querySelector('[name="personas"]')?.value ?? '';
    const area = form.querySelector('[name="area"]')?.value ?? '';
    const submitBtn = form.querySelector('[data-action="reserva-crear"]');

    // Teléfono obligatorio en el alta manual (guard presentacional; el flujo
    // público sigue aceptando reservas sin teléfono). No llamamos al servicio ni
    // cerramos el modal: el staff completa el campo y reintenta.
    if (!telefono.trim()) {
        showToast(RESERVA_TELEFONO_REQUIRED_MSG, { level: 'error' });
        return;
    }

    // Todos los campos son obligatorios en el alta manual: si alguno quedó vacío
    // (o en blanco tras trim), no llamamos al servicio ni cerramos el modal.
    if (![nombre, apellido, fecha, momento, hora, personas, area].every((v) => String(v).trim())) {
        showToast(RESERVA_ALL_FIELDS_REQUIRED_MSG, { level: 'error' });
        return;
    }

    reservaCreateInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        const result = await createReservation({
            nombre,
            apellido,
            telefono: telefono || null,
            fecha,
            momento,
            hora: hora || null,
            personas,
            area,
        });

        if (!getReservationsMount()) return; // sección cambiada durante el request

        if (!result.ok) {
            showToast(result.error ?? RESERVA_CREATE_ERROR_MSG, { level: 'error' });
            return;
        }

        // La reserva ya existe: recién ahora se pueden asignar las mesas que el
        // operador eligió en el formulario (antes no había id contra el cual
        // asignarlas). Un conflicto de mesa no invalida el alta.
        if (result.reservationId) {
            await guardarMesasDe({ id: result.reservationId, fecha, momento, area });
        }

        showToast(RESERVA_CREATE_SUCCESS_MSG, { level: 'success' });
        // Alta exitosa: cerramos el modal (además del refresh + Toast de siempre).
        // Un `{ ok: false }` lo deja ABIERTO para que el staff corrija y reintente.
        reservaFormMode = null;
        reservaEditando = null;
        resetMesasSeleccion();
        renderReservaCreateModal();
        // Silencioso: la lista actual queda visible mientras entra la nueva
        // reserva, sin flash de spinner (misma fluidez que las transiciones).
        await refreshReservations(reservationsUiState.from, { silent: true });
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleReservaCreate: no se pudo crear la reserva', err);
        if (!getReservationsMount()) return;
        showToast(RESERVA_CREATE_ERROR_MSG, { level: 'error' });
    } finally {
        reservaCreateInFlight = false;
        if (submitBtn) submitBtn.disabled = false;
    }
}

// ---------------------------------------------------------------------------
// Bloqueos — subsección propia bajo Reservas (crear + listar + eliminar). Los
// mount nodes (`#admin-blocks-mount` / `#admin-block-confirm-mount`) los crea
// `mountBlocksSection`; acá sólo se resetea el estado y se carga.
// ---------------------------------------------------------------------------

/** @returns {HTMLElement | null} */
function getBlocksMount() {
    return DOM.adminContainer?.querySelector('#admin-blocks-mount') ?? null;
}

/** @returns {HTMLElement | null} */
function getBlockConfirmMount() {
    return DOM.adminContainer?.querySelector('#admin-block-confirm-mount') ?? null;
}

/**
 * Inicializa el panel de bloqueos (ya montado dentro de la sección Reservas):
 * resetea el estado local y dispara la primera carga. NO crea mount nodes —
 * eso lo hace `mountReservasSection`.
 */
function mountBlocksPanel() {
    blocksViewModel = null;
    pendingBlockDelete = null;
    blockMutationInFlight = false;
    blocksUiState = { status: 'loading' };

    renderBlocksMount();
    renderBlockConfirmMount();
    void refreshBlocks();
}

/**
 * Renderiza el ui-state actual (loading/error/ready) dentro del mount de
 * bloqueos y re-cablea sus listeners (el mount se reemplaza por completo en
 * cada render, mismo patrón que reservas).
 */
function renderBlocksMount() {
    const mount = getBlocksMount();
    if (!mount) return;

    if (blocksUiState.status === 'loading') {
        mount.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando bloqueos...</p>
            </div>
        `;
        return;
    }

    if (blocksUiState.status === 'error') {
        mount.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${BLOCKS_LOAD_ERROR_MSG}</p>
                <button data-action="bloqueo-retry"
                    class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">
                    Reintentar
                </button>
            </div>
        `;
        const retryBtn = mount.querySelector('[data-action="bloqueo-retry"]');
        if (retryBtn) retryBtn.addEventListener('click', () => void refreshBlocks());
        return;
    }

    mount.innerHTML = BlocksSection({ blocks: blocksViewModel ?? [] });
    bindBlocksMountListeners(mount);
}

/**
 * Renderiza (o limpia) el diálogo de confirmación del borrado pendiente.
 * Reutiliza el `ConfirmDialog` de reservas — sus hooks (`reserva-confirm`/
 * `reserva-dismiss`) se cablean acá a los handlers de bloqueos, y como el
 * binding es por-elemento dentro de este mount separado, no colisiona con el
 * flujo de reservas.
 * @param {{ id: string, message: string } | null} [pending]
 */
function renderBlockConfirmMount(pending = pendingBlockDelete) {
    const mount = getBlockConfirmMount();
    if (!mount) return;

    if (!pending) {
        mount.innerHTML = '';
        return;
    }

    mount.innerHTML = ConfirmDialog({ id: pending.id, action: 'eliminar', message: pending.message });
    const dismissBtn = mount.querySelector('[data-action="reserva-dismiss"]');
    if (dismissBtn) dismissBtn.addEventListener('click', handleBlockDeleteDismiss);
    const confirmBtn = mount.querySelector('[data-action="reserva-confirm"]');
    if (confirmBtn) confirmBtn.addEventListener('click', () => handleBlockDeleteConfirm(confirmBtn));
}

/**
 * Cablea los listeners del mount de bloqueos: submit del formulario de creación
 * y los botones de eliminar de cada fila.
 * @param {HTMLElement} mount
 */
function bindBlocksMountListeners(mount) {
    const createBtn = mount.querySelector('[data-action="bloqueo-crear"]');
    const form = createBtn?.closest('form');
    if (form) form.addEventListener('submit', handleBlockCreate);

    // Atajo "Todos": marcarlo marca todos los momentos; desmarcarlo los limpia.
    // Y al tocar un momento individual, "Todos" refleja si están todos marcados.
    const todosCheckbox = mount.querySelector('[name="momento-todos"]');
    const momentoCheckboxes = [...mount.querySelectorAll('[name="momento"]')];
    if (todosCheckbox) {
        todosCheckbox.addEventListener('change', () => {
            for (const cb of momentoCheckboxes) cb.checked = todosCheckbox.checked;
        });
        for (const cb of momentoCheckboxes) {
            cb.addEventListener('change', () => {
                todosCheckbox.checked = momentoCheckboxes.every((c) => c.checked);
            });
        }
    }

    for (const btn of mount.querySelectorAll('[data-action="bloqueo-eliminar"]')) {
        btn.addEventListener('click', handleBlockDeleteClick);
    }
}

/**
 * Recarga la lista de bloqueos (`fecha >= hoy`) y re-renderiza: `loading`
 * mientras está en vuelo, luego `ready` con la lista o `error` si falla.
 */
async function refreshBlocks() {
    blocksUiState = { status: 'loading' };
    renderBlocksMount();

    try {
        const blocks = await conTimeoutDeCarga(loadBlocks({}));
        if (!getBlocksMount()) return; // tab cambiada mientras cargaba
        blocksViewModel = blocks;
        blocksUiState = { status: 'ready' };
    } catch (err) {
        if (!getBlocksMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshBlocks: no se pudieron cargar los bloqueos', err);
        blocksUiState = { status: 'error' };
    }

    renderBlocksMount();
}

/**
 * Procesa el submit del formulario de creación de bloqueo: lee los campos,
 * llama al servicio y refleja el resultado. `{ ok: false }` es un fallo de
 * validación (Spanish error, sin refresh); `{ ok: true }` recarga la lista +
 * Toast de éxito; un throw real muestra un Toast genérico.
 *
 * Re-entrancy: comparte `blockMutationInFlight` con el borrado — sólo una
 * mutación de bloqueos a la vez.
 * @param {SubmitEvent} e
 */
async function handleBlockCreate(e) {
    e.preventDefault();
    if (blockMutationInFlight) return;

    const form = e.currentTarget;
    const fecha = form.querySelector('[name="fecha"]')?.value ?? '';
    const area = form.querySelector('[name="area"]')?.value ?? '';
    const reason = form.querySelector('[name="reason"]')?.value ?? '';
    const submitBtn = form.querySelector('[data-action="bloqueo-crear"]');

    // Momento es ahora un multi-select por checkboxes. "Todos" marcado, ninguno
    // marcado, o los 5 marcados → bloqueo del día entero (un único bloqueo con
    // `momento: null`, que en la DB bloquea todos los momentos). Un subconjunto
    // propio → un bloqueo POR momento (loop de inserts single-row). El schema no
    // cambia: el loop vive acá, en el wiring.
    const momentoCheckboxes = [...form.querySelectorAll('[name="momento"]')];
    const checkedMomentos = momentoCheckboxes.filter((cb) => cb.checked).map((cb) => cb.value);
    const todosChecked = form.querySelector('[name="momento-todos"]')?.checked ?? false;
    const treatAsAll =
        todosChecked ||
        checkedMomentos.length === 0 ||
        checkedMomentos.length === momentoCheckboxes.length;
    const momentos = treatAsAll ? [null] : checkedMomentos;

    blockMutationInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        // Un insert por momento (o uno solo con null). Cortamos ante el primer
        // `{ ok: false }` y surfaceamos su error en español, sin refrescar — mismo
        // criterio que el single-insert previo (un fallo de validación no recarga).
        let failure = null;
        for (const momento of momentos) {
            const result = await createBlock({
                fecha,
                momento,
                area: area || null,
                reason,
            });
            if (!result.ok) {
                failure = result;
                break;
            }
        }

        if (!getBlocksMount()) return; // tab cambiada durante el request

        if (failure) {
            showToast(failure.error ?? BLOCK_CREATE_ERROR_MSG, { level: 'error' });
            return;
        }

        showToast(BLOCK_CREATE_SUCCESS_MSG, { level: 'success' });
        await refreshBlocks();
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleBlockCreate: no se pudo crear el bloqueo', err);
        if (!getBlocksMount()) return;
        showToast(BLOCK_CREATE_ERROR_MSG, { level: 'error' });
    } finally {
        blockMutationInFlight = false;
        if (submitBtn) submitBtn.disabled = false;
    }
}

/**
 * Abre el `ConfirmDialog` para eliminar un bloqueo — NUNCA llama a `deleteBlock`
 * directamente desde acá. Mientras una mutación está en vuelo no abre un segundo
 * diálogo (mismo criterio que reservas).
 * @param {MouseEvent} e
 */
function handleBlockDeleteClick(e) {
    if (blockMutationInFlight) return;

    const id = e.currentTarget.dataset.id;
    const block = (blocksViewModel ?? []).find((b) => b.id === id);
    const message = block
        ? `¿Eliminar el bloqueo del ${block.fecha}?`
        : '¿Eliminar este bloqueo?';

    pendingBlockDelete = { id, message };
    renderBlockConfirmMount(pendingBlockDelete);
}

/**
 * Descarta el diálogo de confirmación de borrado sin enviar ningún request.
 */
function handleBlockDeleteDismiss() {
    pendingBlockDelete = null;
    renderBlockConfirmMount(null);
}

/**
 * Confirma el borrado del bloqueo pendiente. Re-entrancy: ignora el doble click
 * en "Confirmar" y deshabilita el botón mientras dura. Guard de resolución: si
 * la sección de bloqueos ya no está montada (cambio de tab / logout), descarta
 * los efectos.
 * @param {HTMLButtonElement | null} [confirmBtn]
 */
async function handleBlockDeleteConfirm(confirmBtn) {
    if (blockMutationInFlight) return;
    const pending = pendingBlockDelete;
    if (!pending) return;

    blockMutationInFlight = true;
    if (confirmBtn) confirmBtn.disabled = true;
    try {
        await deleteBlock(pending.id);
        if (!getBlockConfirmMount()) return;
        pendingBlockDelete = null;
        renderBlockConfirmMount(null);
        showToast(BLOCK_DELETE_SUCCESS_MSG, { level: 'success' });
        await refreshBlocks();
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleBlockDeleteConfirm: no se pudo eliminar el bloqueo', err);
        if (!getBlockConfirmMount()) return;
        showToast(BLOCK_DELETE_ERROR_MSG, { level: 'error' });
        pendingBlockDelete = null;
        renderBlockConfirmMount(null);
    } finally {
        blockMutationInFlight = false;
        if (confirmBtn) confirmBtn.disabled = false;
    }
}

// ---------------------------------------------------------------------------
// Empleados — Control de Horarios y Liquidación de Sueldos (slice 1). El mount
// node (`#admin-empleados-mount`) lo crea `mountEmpleadosSection`.
// ---------------------------------------------------------------------------

/** @returns {HTMLElement | null} */
function getEmpleadosMount() {
    return DOM.adminContainer?.querySelector('#admin-empleados-mount') ?? null;
}

/** Resetea el estado local de Empleados y dispara la primera carga. */
function mountEmpleadosPanel() {
    empleadosViewModel = null;
    empleadoEditing = null;
    empleadoPinDialog = null;
    empleadoMutationInFlight = false;
    empleadoMostrarTodosClientes = false;
    empleadoFudoDirectoryUnavailable = false;
    empleadosUiState = { status: 'loading' };
    renderEmpleadosMount({ keepFormDraft: false });
    void refreshEmpleados({ keepFormDraft: false });
    void refreshFudoDirectory();
}

/**
 * Re-renderiza el mount de Empleados (form + lista + diálogo de PIN) y re-cablea
 * sus listeners.
 *
 * El mount es uno solo: repintarlo reemplaza también el form de alta/edición.
 * Como `renderForm` deriva sus valores del estado y no del DOM, cualquier acción
 * que repinte (abrir el diálogo de PIN, activar/desactivar, refrescar la lista)
 * borraría lo que el admin venía tipeando sin haber guardado. Por eso se
 * captura el form vivo en `empleadoFormDraft` ANTES de reemplazar el HTML y se
 * rinde desde ese borrador. Mismo problema que reservas resuelve repintando
 * solo el plano (ver `renderMesasSlot`); acá el borrador es más barato que
 * partir el mount en dos.
 *
 * Donde el form SÍ debe cambiar (guardado exitoso, cancelar edición, empezar a
 * editar otra fila, montar la sección) se pasa `keepFormDraft: false`. Tiene
 * que ser el repintado el que descarte el borrador y no el llamador: limpiar la
 * variable antes de llamar no alcanza, porque la captura de acá volvería a leer
 * el form todavía lleno y lo reintroduciría.
 * @param {{ keepFormDraft?: boolean }} [options]
 */
function renderEmpleadosMount({ keepFormDraft = true } = {}) {
    const mount = getEmpleadosMount();
    if (!mount) return;

    if (keepFormDraft) captureEmpleadoFormDraft(mount);
    else empleadoFormDraft = null;

    mount.innerHTML = EmpleadosView(empleadosViewModel, {
        ...empleadosUiState,
        editing: empleadoFormDraft ?? empleadoEditing,
        pinDialog: empleadoPinDialog,
        mostrarTodosClientes: empleadoMostrarTodosClientes,
        fudoDirectoryUnavailable: empleadoFudoDirectoryUnavailable,
    });
    bindEmpleadosMountListeners(mount);
}

/**
 * Guarda lo tipeado en el form de Empleados antes de un repintado. No hace nada
 * si el mount no está mostrando el form (estados `loading`/`error`).
 * @param {HTMLElement} mount
 */
function captureEmpleadoFormDraft(mount) {
    const form = mount.querySelector('form[data-mode]');
    if (!form) return;
    empleadoFormDraft = {
        id: form.querySelector('[name="id"]')?.value || undefined,
        ...readEmpleadoForm(form),
    };
}

/**
 * Recarga la lista de empleados (activos e inactivos) y re-renderiza.
 */
async function refreshEmpleados({ keepFormDraft = true } = {}) {
    // El mount se recrea con el MISMO id en cada entrada a la pestaña, así que
    // chequear que exista no alcanza para saber si esta carga sigue siendo la
    // vigente: una respuesta lenta de una visita anterior pisaría el estado
    // actual. Misma generación que usa `refreshReservationsSeq`.
    const seq = ++refreshEmpleadosSeq;

    empleadosUiState = { status: 'loading' };
    // Solo el primer repintado necesita el flag: el estado `loading` no rinde
    // el form, así que las capturas siguientes no encuentran nada que guardar.
    renderEmpleadosMount({ keepFormDraft });
    try {
        const empleados = await conTimeoutDeCarga(loadEmpleados());
        if (seq !== refreshEmpleadosSeq || !getEmpleadosMount()) return;
        // Merge, no reemplazo: `refreshFudoDirectory` corre en paralelo desde
        // `mountEmpleadosPanel` y puede resolver antes o después — cualquiera
        // de las dos cargas que llegue primero no debe pisar lo que la otra
        // ya haya guardado.
        empleadosViewModel = { ...(empleadosViewModel ?? {}), empleados };
        empleadosUiState = { status: 'ready' };
    } catch (err) {
        if (seq !== refreshEmpleadosSeq || !getEmpleadosMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshEmpleados: no se pudieron cargar los empleados', err);
        empleadosUiState = { status: 'error' };
    }
    renderEmpleadosMount();
}

/**
 * Carga clientes/proveedores Fudo para los pickers (PR A2b). Corre en
 * paralelo a `refreshEmpleados` desde `mountEmpleadosPanel`, con su PROPIA
 * generación: una respuesta lenta de una visita anterior a la sección no
 * debe pintar sobre una visita más nueva (misma trampa que
 * `refreshEmpleadosSeq`, ver cabecera del archivo). Sin sesión activa, los
 * selects quedan vacíos — el form del empleado sigue usable igual (nombre,
 * tipo_pago, valor no dependen de Fudo).
 * @param {{ forceRefresh?: boolean }} [options]
 */
async function refreshFudoDirectory({ forceRefresh = false } = {}) {
    const seq = ++refreshFudoDirectorySeq;
    try {
        const session = await getCurrentSession();
        const accessToken = session?.access_token;
        if (!accessToken) return;

        const { customers, providers } = await loadFudoDirectory({ accessToken, forceRefresh });
        if (seq !== refreshFudoDirectorySeq || !getEmpleadosMount()) return;
        empleadosViewModel = { ...(empleadosViewModel ?? { empleados: [] }), fudoCustomers: customers, fudoProviders: providers };
        empleadoFudoDirectoryUnavailable = false;
    } catch (err) {
        if (seq !== refreshFudoDirectorySeq || !getEmpleadosMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshFudoDirectory: no se pudo cargar el directorio de Fudo', err);
        // Los selects quedan vacíos (fallback a [] en EmpleadosView) — no bloquea el
        // resto del form. `empleadoFudoDirectoryUnavailable` distingue esto de un id
        // genuinamente ausente (P2): cambia el TEXTO del aviso, nunca el vínculo.
        empleadoFudoDirectoryUnavailable = true;
    }
    renderEmpleadosMount();
}

/** @param {HTMLElement} mount */
function bindEmpleadosMountListeners(mount) {
    const saveForm = mount.querySelector('[data-action="empleado-guardar"]')?.closest('form');
    if (saveForm) saveForm.addEventListener('submit', handleEmpleadoSave);

    const cancelBtn = mount.querySelector('[data-action="empleado-cancelar-edicion"]');
    if (cancelBtn) cancelBtn.addEventListener('click', handleEmpleadoCancelarEdicion);

    for (const btn of mount.querySelectorAll('[data-action="empleado-editar"]')) {
        btn.addEventListener('click', handleEmpleadoEditarClick);
    }
    for (const btn of mount.querySelectorAll('[data-action="empleado-toggle-activo"]')) {
        btn.addEventListener('click', handleEmpleadoToggleActivo);
    }
    for (const btn of mount.querySelectorAll('[data-action="empleado-pin-abrir"]')) {
        btn.addEventListener('click', handleEmpleadoPinAbrir);
    }

    const pinCancelBtn = mount.querySelector('[data-action="empleado-pin-cancelar"]');
    if (pinCancelBtn) pinCancelBtn.addEventListener('click', handleEmpleadoPinCancelar);

    const pinForm = mount.querySelector('[data-action="empleado-pin-guardar"]')?.closest('form');
    if (pinForm) pinForm.addEventListener('submit', handleEmpleadoPinGuardar);

    const retryBtn = mount.querySelector('[data-action="empleado-retry"]');
    if (retryBtn) retryBtn.addEventListener('click', () => void refreshEmpleados());

    // Picker de Fudo (PR A2b): "mostrar todos" es puramente ui-state, así que
    // el toggle repinta (con `keepFormDraft` por defecto en true) en vez de
    // tocar el DOM del <select> directamente — mismo criterio que el resto
    // de esta sección.
    const mostrarTodosCheckbox = mount.querySelector('[data-action="empleado-mostrar-todos-clientes"]');
    if (mostrarTodosCheckbox) {
        mostrarTodosCheckbox.addEventListener('change', (e) => {
            empleadoMostrarTodosClientes = e.currentTarget.checked;
            renderEmpleadosMount();
        });
    }

    // Review follow-up W2: `EmpleadosView` deriva el estado deshabilitado y la
    // nota del checkbox de feriado de `tipo_pago` en cada render, pero nada
    // repintaba al CAMBIAR el select — quedaba con el aspecto del modo
    // anterior hasta un repintado ajeno. Solo cosmético (el motor fuerza el
    // multiplicador a 1 en `fijo` sea cual sea el flag guardado), pero el
    // admin debe verlo en vivo. Mismo criterio que "mostrar todos": repinta
    // con `keepFormDraft` por defecto para no perder lo tipeado.
    const tipoPagoSelect = mount.querySelector('[name="tipo_pago"]');
    if (tipoPagoSelect) {
        tipoPagoSelect.addEventListener('change', () => {
            renderEmpleadosMount();
        });
    }
}

/**
 * Lee el `data-nombre` de la opción elegida de un `<select>` picker de Fudo
 * (Req: "picking writes id and nombre together") — `null` si no hay
 * selección, nunca el texto visible por si algún día difiere del atributo.
 * @param {HTMLSelectElement|null} select
 * @returns {string|null}
 */
function selectedPickerNombre(select) {
    const opt = select?.selectedOptions?.[0];
    return opt?.value ? (opt.dataset.nombre ?? null) : null;
}

/**
 * Lee los campos del form de alta/edición (Req: Required fields enforced se
 * valida en el servicio, no acá — este solo transporta lo tipeado).
 *
 * S8 (design.md/tasks.md, spec `payroll-advances` "fudo_proveedor_id
 * Ignored, Not Dropped"): `fudo_proveedor_id`/`fudo_proveedor_nombre`
 * DEJARON de leerse acá a propósito — el form ya no tiene ese `<select>`
 * (`EmpleadosView.js`). Omitir las CLAVES del objeto devuelto (no mandarlas
 * como `null`) es lo que importa: `handleEmpleadoSave` pasa este objeto
 * directo a `updateEmpleado`/`empleados.repository.js#updateEmpleado`, que
 * hace un `.update(editables)` de Supabase — sólo las columnas presentes en
 * el objeto se tocan. Si esta función siguiera devolviendo `fudo_proveedor_id:
 * null` (porque el select ya no existe, `fudoProveedorSelect?.value` sería
 * siempre `undefined`), CADA guardado de CUALQUIER otro campo pisaría con
 * `null` el valor ya cargado en la base — exactamente lo que el requisito
 * "column... MUST remain in the database" prohíbe. La columna sigue viva,
 * documentada obsoleta, y esta función simplemente ya no la toca.
 * @param {HTMLFormElement} form
 */
function readEmpleadoForm(form) {
    const valorRaw = form.querySelector('[name="valor"]')?.value ?? '';
    const fudoClienteSelect = form.querySelector('[name="fudo_cliente_id"]');
    return {
        nombre: form.querySelector('[name="nombre"]')?.value ?? '',
        tipo_pago: form.querySelector('[name="tipo_pago"]')?.value || undefined,
        valor: valorRaw === '' ? undefined : Number(valorRaw),
        paga_feriado_doble: form.querySelector('[name="paga_feriado_doble"]')?.checked ?? false,
        fudo_cliente_id: fudoClienteSelect?.value || null,
        fudo_cliente_nombre: selectedPickerNombre(fudoClienteSelect),
        foto_url: form.querySelector('[name="foto_url"]')?.value || null,
    };
}

/**
 * Crea (sin `id`) o edita (con `id`) un empleado. El PIN nunca viaja por acá
 * (tiene su propio flujo, `handleEmpleadoPinGuardar`).
 * @param {SubmitEvent} e
 */
async function handleEmpleadoSave(e) {
    e.preventDefault();
    if (empleadoMutationInFlight) return;

    const form = e.currentTarget;
    const id = form.querySelector('[name="id"]')?.value || null;
    const fields = readEmpleadoForm(form);
    const submitBtn = form.querySelector('[data-action="empleado-guardar"]');

    empleadoMutationInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        const result = id ? await updateEmpleado(id, fields) : await createEmpleado(fields);
        if (!getEmpleadosMount()) return;
        if (!result.ok) {
            // Rechazo de negocio: se conserva lo tipeado para que el admin corrija.
            showToast(result.error ?? EMPLEADO_SAVE_ERROR_MSG, { level: 'error' });
            return;
        }
        empleadoEditing = null;
        showToast(EMPLEADO_SAVE_SUCCESS_MSG, { level: 'success' });
        await refreshEmpleados({ keepFormDraft: false });   // guardado OK: form vacío
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleEmpleadoSave: no se pudo guardar el empleado', err);
        if (!getEmpleadosMount()) return;
        showToast(EMPLEADO_SAVE_ERROR_MSG, { level: 'error' });
    } finally {
        empleadoMutationInFlight = false;
        if (submitBtn?.isConnected) submitBtn.disabled = false;
    }
}

/** Sale del modo edición sin guardar y vuelve el form a modo alta. */
function handleEmpleadoCancelarEdicion() {
    empleadoEditing = null;
    renderEmpleadosMount({ keepFormDraft: false });   // cancelar SÍ descarta lo tipeado
}

/** @param {MouseEvent} e */
function handleEmpleadoEditarClick(e) {
    const id = e.currentTarget.dataset.id;
    empleadoEditing = (empleadosViewModel?.empleados ?? []).find((emp) => emp.id === id) ?? null;
    renderEmpleadosMount({ keepFormDraft: false });   // el form muestra al empleado elegido
}

/**
 * Activa/desactiva un empleado (Req: Active/Inactive Lifecycle) — un simple
 * `update` parcial, sin diálogo de confirmación (reversible con un click más).
 * @param {MouseEvent} e
 */
async function handleEmpleadoToggleActivo(e) {
    if (empleadoMutationInFlight) return;
    const id = e.currentTarget.dataset.id;
    const empleado = (empleadosViewModel?.empleados ?? []).find((emp) => emp.id === id);
    if (!empleado) return;

    empleadoMutationInFlight = true;
    try {
        const result = await updateEmpleado(id, { activo: !empleado.activo });
        if (!getEmpleadosMount()) return;
        if (!result.ok) {
            showToast(result.error ?? EMPLEADO_SAVE_ERROR_MSG, { level: 'error' });
            return;
        }
        await refreshEmpleados();
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleEmpleadoToggleActivo: no se pudo cambiar el estado', err);
        if (!getEmpleadosMount()) return;
        showToast(EMPLEADO_SAVE_ERROR_MSG, { level: 'error' });
    } finally {
        empleadoMutationInFlight = false;
    }
}

/** @param {MouseEvent} e */
function handleEmpleadoPinAbrir(e) {
    empleadoPinDialog = { empleadoId: e.currentTarget.dataset.id };
    renderEmpleadosMount();
}

function handleEmpleadoPinCancelar() {
    empleadoPinDialog = null;
    renderEmpleadosMount();
}

/**
 * Envía el nuevo PIN al servicio. Un rechazo por duplicado (Req: Duplicate
 * PIN rejected) reabre el diálogo con el error inline en vez de un Toast —
 * el PIN tipeado NUNCA se reintroduce como `value` (ver EmpleadosView.js).
 * @param {SubmitEvent} e
 */
async function handleEmpleadoPinGuardar(e) {
    e.preventDefault();
    if (empleadoMutationInFlight) return;

    const form = e.currentTarget;
    const empleadoId = form.dataset.id;
    const pin = form.querySelector('[name="pin"]')?.value ?? '';

    empleadoMutationInFlight = true;
    try {
        const result = await setEmpleadoPin({ empleadoId, pin });
        if (!getEmpleadosMount()) return;
        if (!result.ok) {
            empleadoPinDialog = { empleadoId, error: result.error };
            renderEmpleadosMount();
            return;
        }
        empleadoPinDialog = null;
        showToast(EMPLEADO_PIN_SUCCESS_MSG, { level: 'success' });
        renderEmpleadosMount();
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleEmpleadoPinGuardar: no se pudo guardar el PIN', err);
        if (!getEmpleadosMount()) return;
        showToast(EMPLEADO_PIN_ERROR_MSG, { level: 'error' });
    } finally {
        empleadoMutationInFlight = false;
    }
}

// ---------------------------------------------------------------------------
// Fichadas — Control de Horarios y Liquidación de Sueldos (slice 3 —
// Auditoría). El mount node (`#admin-fichadas-mount`) lo crea
// `mountFichadasSection`.
// ---------------------------------------------------------------------------

/** @returns {HTMLElement | null} */
function getFichadasMount() {
    return DOM.adminContainer?.querySelector('#admin-fichadas-mount') ?? null;
}

/**
 * L7 — fechas por defecto del filtro "Desde"/"Hasta" de Fichadas: los
 * últimos 30 días ART, hoy incluido. Fechas-calendario ART (`YYYY-MM-DD`),
 * nunca la fecha local del navegador ni la UTC de `toISOString()` (las dos
 * son el día siguiente desde las 21:00 ART). La LECTURA las convierte a
 * medianoche ART (`ventanaLecturaFichadas`), nunca como fecha corta.
 * @returns {{ desde: string, hasta: string }}
 */
function fichadasFechasDefault() {
    const hasta = hoyArtISO();
    const desde = artDateKey(Date.parse(artMidnightUtcIso(hasta, -30)));
    return { desde, hasta };
}

const FICHADA_FILTRO_RANGO_INVALIDO_MSG = 'La fecha «Desde» no puede ser posterior a «Hasta».';
const FICHADA_FILTRO_FECHA_VACIA_MSG = 'Elegí una fecha «Desde» y una fecha «Hasta».';

/**
 * Convierte el valor crudo de un `<input type="datetime-local">`
 * (`YYYY-MM-DDTHH:mm`, sin offset) a un ISO8601 con offset, interpretándolo
 * como hora LOCAL del navegador (comportamiento nativo de `new Date(...)`
 * para ese formato) — así la RPC recibe un instante absoluto correcto sin
 * importar la zona horaria del navegador del admin, en vez de un string
 * ambiguo que Postgres tendría que adivinar.
 * @param {string} value
 * @returns {string|undefined} `undefined` si `value` está vacío o es inválido (== "no tocar este campo")
 */
/**
 * Misma truncación a minutos que hace `toDatetimeLocalValue` en el diálogo, para
 * poder comparar lo que el admin ve contra lo que tipeó y decidir si un campo
 * cambió de verdad. Sin esto no se puede distinguir "no lo tocó" de "lo dejó
 * igual", y ambos casos terminarían escribiendo.
 * @param {string | null | undefined} iso
 * @returns {string}
 */
function toLocalDatetimeInputValue(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localDatetimeToIso(value) {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
}

/** Resetea el estado local de Fichadas y dispara la primera carga. */
function mountFichadasPanel() {
    fichadasViewModel = null;
    fichadaDialog = null;
    fichadaFormDraft = null;
    fichadaMutationInFlight = false;
    fichadasUiState = { status: 'loading' };
    fichadaFiltro = { empleadoId: '', todoHistorial: false, ...fichadasFechasDefault(), error: null };
    fichadaHistorialCompleto = null;
    renderFichadasMount({ keepFormDraft: false });
    void refreshFichadas({ keepFormDraft: false });
}

/**
 * Re-renderiza el mount de Fichadas (anomalías + alta manual + diálogo de
 * corrección) y re-cablea sus listeners.
 *
 * Mismo problema y misma solución que `renderEmpleadosMount`: el mount es
 * uno solo, así que abrir/cerrar el diálogo de corrección repinta también el
 * form de alta manual. Como ese form deriva sus valores del estado (acá:
 * nada, siempre arranca vacío) y NO del DOM, cualquier repintado lo
 * borraría — por eso se captura en `fichadaFormDraft` ANTES de reemplazar el
 * HTML y se re-puebla desde ese borrador tras el repintado.
 * @param {{ keepFormDraft?: boolean }} [options]
 */
function renderFichadasMount({ keepFormDraft = true } = {}) {
    const mount = getFichadasMount();
    if (!mount) return;

    if (keepFormDraft) captureFichadaFormDraft(mount);
    else fichadaFormDraft = null;

    const { fichadas, truncado } = fichadasParaMostrar();
    mount.innerHTML = FichadasView(
        { ...fichadasViewModel, fichadas, truncado },
        { ...fichadasUiState, dialog: fichadaDialog, filtro: fichadaFiltro },
    );
    applyFichadaFormDraft(mount);
    bindFichadasMountListeners(mount);
}

/**
 * Qué conjunto de fichadas (+ el aviso de tope, review-6d2bc4aaef1ed2ce
 * finding 2) le pasa `renderFichadasMount` a la vista: el historial completo
 * de UN empleado (owner request 2026-09-02) si "Todo el historial" está
 * tildado Y ya se cargó para ESE empleado puntual (`truncado` viene de
 * `admin-fichadas.service.js#loadFichadas`, que a su vez lo recibe de
 * `fichadas.repository.js#listFichadasHistorial`'s 2000-row cap), o el rango
 * del filtro «Desde»/«Hasta» (`fichadaFiltro`, L7; todos los empleados,
 * nunca truncado) en cualquier otro caso — `FichadasView` filtra por
 * `fichadaFiltro.empleadoId` a partir de acá, así que no hace falta
 * pre-filtrar ese rango acá también.
 * @returns {{ fichadas: Array<object>, truncado: boolean }}
 */
function fichadasParaMostrar() {
    if (fichadaFiltro.todoHistorial && fichadaFiltro.empleadoId && fichadaHistorialCompleto?.empleadoId === fichadaFiltro.empleadoId) {
        return { fichadas: fichadaHistorialCompleto.fichadas, truncado: Boolean(fichadaHistorialCompleto.truncado) };
    }
    return { fichadas: fichadasViewModel?.fichadas ?? [], truncado: false };
}

/**
 * Guarda lo tipeado en el form de alta manual de Fichadas antes de un
 * repintado. No hace nada si el mount no está mostrando ese form (estados
 * `loading`/`error`, o si el diálogo de corrección tapa la sección — el form
 * sigue en el DOM detrás del diálogo, así que esto igual lo encuentra).
 * @param {HTMLElement} mount
 */
function captureFichadaFormDraft(mount) {
    const form = mount.querySelector('form[data-action-form="fichada-crear"]');
    if (!form) return;
    fichadaFormDraft = {
        empleado_id: form.querySelector('[name="empleado_id"]')?.value ?? '',
        entrada_at: form.querySelector('[name="entrada_at"]')?.value ?? '',
        motivo: form.querySelector('[name="motivo"]')?.value ?? '',
    };
}

/**
 * Re-puebla el form de alta manual con el borrador guardado, si hay uno.
 * `FichadasView` no recibe el borrador como prop (a diferencia de
 * `EmpleadosView#editing`) porque el form de alta no tiene modo
 * crear/editar que derivar de él — es más simple parchear el DOM recién
 * pintado que sumarle una prop más a la vista para un solo campo de texto
 * libre por control.
 * @param {HTMLElement} mount
 */
function applyFichadaFormDraft(mount) {
    if (!fichadaFormDraft) return;
    const form = mount.querySelector('form[data-action-form="fichada-crear"]');
    if (!form) return;
    if (form.querySelector('[name="empleado_id"]')) form.querySelector('[name="empleado_id"]').value = fichadaFormDraft.empleado_id;
    if (form.querySelector('[name="entrada_at"]')) form.querySelector('[name="entrada_at"]').value = fichadaFormDraft.entrada_at;
    if (form.querySelector('[name="motivo"]')) form.querySelector('[name="motivo"]').value = fichadaFormDraft.motivo;
}

/**
 * Recarga las anomalías del rango por defecto + la lista de empleados
 * (activos, para el `<select>` del alta manual) y re-renderiza.
 */
async function refreshFichadas({ keepFormDraft = true } = {}) {
    // Misma generación que `refreshEmpleadosSeq`: descarta una respuesta lenta
    // de una visita anterior a la subsección.
    const seq = ++refreshFichadasSeq;

    fichadasUiState = { status: 'loading' };
    renderFichadasMount({ keepFormDraft });
    try {
        // L7: el rango elegido en "Desde"/"Hasta" (fechas ART), leído con
        // límites en medianoche ART; las anomalías se acotan por fecha ART.
        const fechasArt = { desde: fichadaFiltro.desde, hasta: fichadaFiltro.hasta };
        const lectura = ventanaLecturaFichadas(fechasArt, 0);
        const [{ fichadas, anomalies }, empleados] = await conTimeoutDeCarga(
            Promise.all([loadFichadas({ ...lectura, fechasArt }), loadEmpleados()]),
        );
        if (seq !== refreshFichadasSeq || !getFichadasMount()) return;
        fichadasViewModel = { anomalies, empleados, fichadas };
        fichadasUiState = { status: 'ready' };
    } catch (err) {
        if (seq !== refreshFichadasSeq || !getFichadasMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshFichadas: no se pudieron cargar las fichadas', err);
        fichadasUiState = { status: 'error' };
    }
    renderFichadasMount();
}

/** @param {HTMLElement} mount */
function bindFichadasMountListeners(mount) {
    const crearForm = mount.querySelector('form[data-action-form="fichada-crear"]');
    if (crearForm) crearForm.addEventListener('submit', handleFichadaCrearSave);

    const filtroForm = mount.querySelector('form[data-action-form="fichada-filtro"]');
    const filtroEmpleadoSelect = filtroForm?.querySelector('[name="empleado_id"]');
    if (filtroEmpleadoSelect) filtroEmpleadoSelect.addEventListener('change', handleFichadaFiltroEmpleadoChange);
    const todoHistorialCheckbox = filtroForm?.querySelector('[name="todo_historial"]');
    if (todoHistorialCheckbox) todoHistorialCheckbox.addEventListener('change', handleFichadaFiltroTodoHistorialChange);
    for (const input of filtroForm?.querySelectorAll('[name="desde"], [name="hasta"]') ?? []) {
        input.addEventListener('change', handleFichadaFiltroFechaChange);
    }

    for (const btn of mount.querySelectorAll('[data-action="fichada-corregir"]')) {
        btn.addEventListener('click', handleFichadaCorregirClick);
    }
    for (const btn of mount.querySelectorAll('[data-action="fichada-fila"]')) {
        btn.addEventListener('click', handleFichadaFilaClick);
    }

    const dialogCancelBtn = mount.querySelector('[data-action="fichada-dialog-cancelar"]');
    if (dialogCancelBtn) dialogCancelBtn.addEventListener('click', handleFichadaDialogCancelar);

    const dialogForm = mount.querySelector('[data-action="fichada-dialog-guardar"]')?.closest('form');
    if (dialogForm) dialogForm.addEventListener('submit', handleFichadaDialogGuardar);

    const retryBtn = mount.querySelector('[data-action="fichada-retry"]');
    if (retryBtn) retryBtn.addEventListener('click', () => void refreshFichadas());
}

/**
 * Alta manual de una fichada (Req: Admin Fichada Create, Edit, and Close).
 * @param {SubmitEvent} e
 */
async function handleFichadaCrearSave(e) {
    e.preventDefault();
    if (fichadaMutationInFlight) return;

    const form = e.currentTarget;
    const empleado_id = form.querySelector('[name="empleado_id"]')?.value ?? '';
    const entrada_at = localDatetimeToIso(form.querySelector('[name="entrada_at"]')?.value);
    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    const submitBtn = form.querySelector('[data-action="fichada-crear-guardar"]');

    fichadaMutationInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        // F-E: el nombre solo alimenta el mensaje de "día liquidado".
        const empleadoNombre = (fichadasViewModel?.empleados ?? []).find((emp) => emp.id === empleado_id)?.nombre ?? '';
        const result = await crearFichada({ empleado_id, entrada_at, motivo, empleadoNombre });
        if (!getFichadasMount()) return;
        if (!result.ok) {
            // L9c: un guardado que no respondió a tiempo puede haberse aplicado — aviso, no error.
            showToast(result.error ?? FICHADA_GENERIC_ERROR_MSG, { level: result.incierto ? 'warning' : 'error' });
            // R5: el aviso dice "revisá la lista" — se recarga para que muestre
            // el estado real. Lo tipeado se conserva: si no se guardó, se
            // puede reenviar sin volver a escribirlo.
            if (result.incierto) await refreshFichadasAfterMutation();
            return;
        }
        showToast(FICHADA_CREAR_SUCCESS_MSG, { level: 'success' });
        await refreshFichadasAfterMutation({ keepFormDraft: false });   // alta OK: form vacío
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleFichadaCrearSave: no se pudo crear la fichada', err);
        if (!getFichadasMount()) return;
        showToast(FICHADA_GENERIC_ERROR_MSG, { level: 'error' });
    } finally {
        fichadaMutationInFlight = false;
        if (submitBtn?.isConnected) submitBtn.disabled = false;
    }
}

/**
 * Cambia el empleado elegido en el filtro (owner request 2026-09-02). Un
 * cambio de empleado resetea "Todo el historial" y descarta el caché de
 * historial completo previo: quedarían pisando el empleado equivocado si se
 * mantuvieran, y "Todo el historial" no tiene sentido sin un empleado
 * puntual (`FichadasView` ya oculta el checkbox para "Todos los empleados",
 * esto es la mitad wiring de esa misma regla).
 * @param {Event} e
 */
function handleFichadaFiltroEmpleadoChange(e) {
    fichadaFiltro = { ...fichadaFiltro, empleadoId: e.currentTarget.value, todoHistorial: false };
    fichadaHistorialCompleto = null;
    renderFichadasMount();
}

/**
 * L7 — cambio de "Desde"/"Hasta". Relee el rango (lista, resumen y
 * anomalías salen de la misma lectura) conservando el empleado elegido. Un
 * rango inválido (fecha vacía, o "Desde" posterior a "Hasta") no lee nada:
 * deja el rango anterior y muestra por qué.
 * @param {Event} e
 */
function handleFichadaFiltroFechaChange(e) {
    const form = e.currentTarget.closest('form');
    const desde = form?.querySelector('[name="desde"]')?.value ?? '';
    const hasta = form?.querySelector('[name="hasta"]')?.value ?? '';
    const esFecha = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);
    if (!esFecha(desde) || !esFecha(hasta)) {
        fichadaFiltro = { ...fichadaFiltro, error: FICHADA_FILTRO_FECHA_VACIA_MSG };
        renderFichadasMount();
        return;
    }
    if (desde > hasta) {
        fichadaFiltro = { ...fichadaFiltro, error: FICHADA_FILTRO_RANGO_INVALIDO_MSG };
        renderFichadasMount();
        return;
    }
    fichadaFiltro = { ...fichadaFiltro, desde, hasta, error: null };
    void refreshFichadas();
}

/**
 * Tilda/destilda "Todo el historial" (owner request 2026-09-02). Tildarlo
 * dispara `refreshFichadaHistorialCompleto` (una query nueva, `sinRango:
 * true`) SOLO si no hay ya un caché válido para ese empleado — destildarlo
 * no dispara ninguna query: el rango por defecto ya está cargado en
 * `fichadasViewModel.fichadas` (`fichadasParaMostrar` vuelve a leerlo solo
 * con re-renderizar).
 * @param {Event} e
 */
function handleFichadaFiltroTodoHistorialChange(e) {
    const todoHistorial = e.currentTarget.checked;
    fichadaFiltro = { ...fichadaFiltro, todoHistorial };

    if (!todoHistorial || !fichadaFiltro.empleadoId) {
        renderFichadasMount();
        return;
    }
    if (fichadaHistorialCompleto?.empleadoId === fichadaFiltro.empleadoId) {
        renderFichadasMount();
        return;
    }
    void refreshFichadaHistorialCompleto(fichadaFiltro.empleadoId);
}

/**
 * Carga TODAS las fichadas de un empleado (`loadFichadas({ empleadoId,
 * sinRango: true })`, owner request 2026-09-02) y las cachea en
 * `fichadaHistorialCompleto` junto con `truncado` (review-6d2bc4aaef1ed2ce
 * finding 2 — si el servicio reporta que el tope de 2000 filas se
 * alcanzó). Nunca toca `fichadasViewModel.anomalies` — la lista de
 * anomalías sigue siendo la del rango por defecto para TODOS los empleados,
 * esta carga es solo para el LISTADO + resumen de un empleado puntual.
 *
 * `keepFormDraft` (default `true`, mismo criterio que `refreshFichadas`) lo
 * usa `refreshFichadasAfterMutation` para poder vaciar el form de alta
 * manual tras un alta exitosa, incluso cuando "Todo el historial" está
 * activo y esta función (no `refreshFichadas`) es la que corre después.
 * @param {string} empleadoId
 * @param {{ keepFormDraft?: boolean }} [options]
 */
async function refreshFichadaHistorialCompleto(empleadoId, { keepFormDraft = true } = {}) {
    const seq = ++refreshFichadaHistorialSeq;
    renderFichadasMount({ keepFormDraft });
    try {
        const { fichadas, truncado } = await loadFichadas({ empleadoId, sinRango: true });
        if (seq !== refreshFichadaHistorialSeq || !getFichadasMount()) return;
        fichadaHistorialCompleto = { empleadoId, fichadas, truncado };
    } catch (err) {
        if (seq !== refreshFichadaHistorialSeq || !getFichadasMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshFichadaHistorialCompleto: no se pudo cargar el historial completo', err);
        // Revierte el checkbox: mostrar "tildado" sobre un caché vacío
        // mentiría sobre qué se está viendo (el rango por defecto sigue
        // siendo lo único disponible).
        if (fichadaFiltro.empleadoId === empleadoId) fichadaFiltro = { ...fichadaFiltro, todoHistorial: false };
        showToast('No pudimos cargar el historial completo de ese empleado.', { level: 'error' });
    }
    renderFichadasMount();
}

/**
 * Refresca las fichadas mostradas después de una mutación exitosa
 * (crear/editar/cerrar — review-6d2bc4aaef1ed2ce finding 1). Antes,
 * `handleFichadaCrearSave`/`handleFichadaDialogGuardar` siempre llamaban a
 * `refreshFichadas` (el rango por defecto) sin importar el filtro, así que
 * el caché de `fichadaHistorialCompleto` quedaba desactualizado mientras
 * "Todo el historial" seguía tildado para ese empleado: la fichada recién
 * creada/corregida no aparecía hasta destildar y re-tildar el checkbox a
 * mano. Ahora, si "Todo el historial" está activo para un empleado, se
 * vuelve a pedir ESE historial (`refreshFichadaHistorialCompleto`, mismo
 * seq guard que ya tenía); si no, se refresca el rango por defecto como
 * antes.
 * @param {{ keepFormDraft?: boolean }} [options]
 */
async function refreshFichadasAfterMutation({ keepFormDraft = true } = {}) {
    // review-7ff0f203512701ce (CRITICAL): the Anomalías list lives in
    // fichadasViewModel and is only recomputed by refreshFichadas, so the
    // default-range refresh must ALWAYS run after a mutation; the full
    // history cache is refreshed on top of it when "Todo el historial" is on.
    // F-B: una corrección puede cerrar (o abrir) una jornada del banner.
    void refreshJornadasAbiertasBanner();
    await refreshFichadas({ keepFormDraft });
    if (fichadaFiltro.todoHistorial && fichadaFiltro.empleadoId) {
        await refreshFichadaHistorialCompleto(fichadaFiltro.empleadoId, { keepFormDraft });
    }
}

/**
 * Abre el diálogo de corrección para la anomalía elegida (`editar` u `cerrar`
 * según el tipo — ver `FichadasView#renderAnomalyRow`), prellenado con los
 * horarios actuales de esa fichada.
 * @param {MouseEvent} e
 */
function handleFichadaCorregirClick(e) {
    const { id, accion } = e.currentTarget.dataset;
    const anomaly = (fichadasViewModel?.anomalies ?? []).find((a) => a.fichadaId === id);
    fichadaDialog = {
        fichadaId: id,
        accion,
        empleadoId: anomaly?.empleadoId ?? null,
        empleadoNombre: anomaly?.empleadoNombre ?? '',
        entrada_at: anomaly?.entrada_at ?? null,
        salida_at: anomaly?.salida_at ?? null,
    };
    renderFichadasMount();
}

/**
 * Abre el diálogo para una fila del LISTADO (F-C/F-D, owner decisions
 * 2026-09-27): `editar`/`cerrar`/`anular` sobre CUALQUIER fichada no anulada
 * del conjunto mostrado, no solo anomalías — mismo diálogo, mismo servicio.
 * La fila se busca en lo que se está mostrando (rango por defecto o "Todo el
 * historial"), que es de donde salió el botón.
 * @param {MouseEvent} e
 */
function handleFichadaFilaClick(e) {
    const { id, accion } = e.currentTarget.dataset;
    const fichada = fichadasParaMostrar().fichadas.find((f) => f.id === id);
    if (!fichada || fichada.anulada) return;
    fichadaDialog = {
        fichadaId: id,
        accion,
        empleadoId: fichada.empleado_id,
        empleadoNombre: fichada.empleados?.nombre ?? '',
        entrada_at: fichada.entrada_at ?? null,
        salida_at: fichada.salida_at ?? null,
    };
    renderFichadasMount();
}

function handleFichadaDialogCancelar() {
    fichadaDialog = null;
    renderFichadasMount();
}

/**
 * Envía la corrección (`editar`/`cerrar`) al servicio. `motivo` vacío nunca
 * llega hasta acá: el `<textarea required>` de `FichadaEditDialog` bloquea el
 * submit nativo antes de que este listener corra (Req: "Edit without a
 * reason is rejected"). Un rechazo de negocio (incluido `cambios: 0` — "no se
 * modificó ningún campo") reabre el diálogo con el error inline, igual que
 * el diálogo de PIN de Empleados.
 * @param {SubmitEvent} e
 */
async function handleFichadaDialogGuardar(e) {
    e.preventDefault();
    if (fichadaMutationInFlight) return;
    if (!fichadaDialog) return;

    const form = e.currentTarget;
    // Se captura el diálogo ENTERO, no solo el id: al resolver hay que saber si
    // el que está abierto sigue siendo ESTE. Sin eso, un rechazo que llega
    // tarde se pega sobre el diálogo de otro empleado (mostrándole un error que
    // no es suyo) o sobre uno ya cancelado, donde no se renderiza nada y el
    // rechazo se pierde sin aviso. La sección de Empleados ya lo hace bien.
    const dialogoPropio = fichadaDialog;
    const { fichadaId, accion } = dialogoPropio;
    const sigueSiendoMiDialogo = () => fichadaDialog?.fichadaId === fichadaId && fichadaDialog?.accion === accion;

    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    const salida_at = localDatetimeToIso(form.querySelector('[name="salida_at"]')?.value);
    // El input datetime-local NO tiene segundos, y el kiosco estampa con
    // fracciones de segundo: mandar siempre entrada_at reescribiría un valor
    // que el admin no tocó y dejaría una fila de auditoría afirmando que
    // editó ese campo. Solo se manda si REALMENTE cambió respecto de lo que
    // el diálogo mostraba; si no, se omite la clave y la RPC ni la mira.
    const entradaTipeada = form.querySelector('[name="entrada_at"]')?.value ?? '';
    const entradaCambio = entradaTipeada !== toLocalDatetimeInputValue(dialogoPropio.entrada_at);
    const entrada_at = entradaCambio ? localDatetimeToIso(entradaTipeada) : undefined;

    fichadaMutationInFlight = true;
    try {
        // F-E: la fila tal como la mostraba el diálogo, para la guarda de día liquidado.
        const actual = {
            empleado_id: dialogoPropio.empleadoId,
            empleadoNombre: dialogoPropio.empleadoNombre,
            entrada_at: dialogoPropio.entrada_at,
            salida_at: dialogoPropio.salida_at ?? null,
        };
        let result;
        if (accion === 'anular') result = await anularFichada({ fichada_id: fichadaId, motivo, actual });
        else if (accion === 'cerrar') result = await cerrarFichada({ fichada_id: fichadaId, salida_at, motivo, actual });
        else result = await editarFichada({ fichada_id: fichadaId, entrada_at, salida_at, motivo, actual });
        if (!getFichadasMount()) return;
        if (!result.ok) {
            // R5: una corrección que no respondió a tiempo puede haberse
            // aplicado — aviso (no error) en el diálogo, y la lista se recarga
            // para mostrar el estado real.
            if (result.incierto) {
                if (sigueSiendoMiDialogo()) fichadaDialog = { ...dialogoPropio, error: null, aviso: result.error };
                await refreshFichadasAfterMutation();
                return;
            }
            // Solo reabre el diálogo si sigue siendo el de esta corrección.
            if (!sigueSiendoMiDialogo()) return;
            fichadaDialog = { ...dialogoPropio, error: result.error };
            renderFichadasMount();
            return;
        }
        // El éxito refresca siempre (la corrección SÍ se escribió), pero solo
        // cierra el diálogo si todavía es el propio: si no, le estaríamos
        // cerrando la pantalla y borrando lo tipeado a otra persona.
        if (sigueSiendoMiDialogo()) fichadaDialog = null;
        showToast(accion === 'anular' ? FICHADA_ANULAR_SUCCESS_MSG : FICHADA_GUARDAR_SUCCESS_MSG, { level: 'success' });
        await refreshFichadasAfterMutation();
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleFichadaDialogGuardar: no se pudo guardar la fichada', err);
        if (!getFichadasMount()) return;
        showToast(FICHADA_GENERIC_ERROR_MSG, { level: 'error' });
    } finally {
        fichadaMutationInFlight = false;
    }
}

// ---------------------------------------------------------------------------
// Liquidación — Control de Horarios y Liquidación de Sueldos (slice 5). El
// mount principal (`#admin-liquidacion-mount`) lo crea
// `mountLiquidacionSection`; el `ConfirmDialog` vive en su PROPIO mount
// (`#admin-liquidacion-confirm-mount`, mismo patrón que
// `admin-block-confirm-mount`) para que abrirlo/cerrarlo nunca repinte el
// selector ni el preview.
// ---------------------------------------------------------------------------

/** @returns {HTMLElement | null} */
function getLiquidacionMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-mount') ?? null;
}

/** @returns {HTMLElement | null} */
function getLiquidacionConfirmMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-confirm-mount') ?? null;
}

/** @returns {HTMLElement | null} */
function getLiquidacionAnularMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-anular-mount') ?? null;
}

/** @returns {HTMLElement | null} */
function getLiquidacionComprobanteMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-comprobante-mount') ?? null;
}

/** Unit S7 (design.md AD16) — mount PARALELO al del comprobante individual, para el print en lote. @returns {HTMLElement | null} */
function getTableroComprobantesLoteMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-comprobantes-lote-mount') ?? null;
}

/** Unit S2a — @returns {HTMLElement | null} */
function getTableroMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-tablero-mount') ?? null;
}

/** Unit S4 (design.md AD7) — @returns {HTMLElement | null} */
function getTableroDrawerMount() {
    return DOM.adminContainer?.querySelector('#admin-liquidacion-drawer-mount') ?? null;
}

/**
 * F1 — ventana acotada para el fetch del historial: liquidaciones cuyo
 * período arranca en los últimos `LIQUIDACION_HISTORIAL_MESES` meses (fecha
 * LOCAL — un período de sueldos es una fecha-calendario, no un instante).
 *
 * L1: SIN cota superior. Antes la ventana cortaba en `periodo_hasta <= hoy`,
 * así que una liquidación cuyo período terminaba en el futuro (el confirm la
 * aceptaba hasta L1) quedaba guardada pero invisible: no se podía anular ni
 * pagar, y el siguiente preview chocaba con `periodo_solapado` sin que el
 * admin pudiera verla. Hoy el servidor ya no acepta esas liquidaciones, pero
 * las viejas tienen que seguir apareciendo.
 * @param {Date} [now]
 * @returns {{ inicioDesde: string }} filtro de `listLiquidaciones`
 */
function liquidacionHistorialRangoDefault(now = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    const toFecha = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const desdeMes = new Date(now.getFullYear(), now.getMonth() - (LIQUIDACION_HISTORIAL_MESES - 1), 1);
    return { inicioDesde: toFecha(desdeMes) };
}

/** Resetea el estado local de Liquidación y dispara la primera carga (listado de empleados para el selector). */
function mountLiquidacionPanel() {
    // Invalida cualquier preview/confirm/carga o guardado del tope/historial
    // dejados en vuelo en una visita anterior a esta pestaña — ver los
    // comentarios de cabecera de `liquidacionPreviewSeq`/`liquidacionConfirmSeq`/
    // `liquidacionCapConfigSeq`/`liquidacionCapSaveSeq`/`liquidacionHistorialSeq`.
    liquidacionPreviewSeq += 1;
    liquidacionConfirmSeq += 1;
    liquidacionAnularSeq += 1;
    liquidacionCapConfigSeq += 1;
    liquidacionCapSaveSeq += 1;
    liquidacionHistorialSeq += 1;
    // Unit A2 (design.md AD9): un comprobante que quedó abierto cuando el
    // admin deja esta pestaña y vuelve — o cambia a otra pestaña — no debe
    // filtrar su `document.title`, su clase `comprobante-abierto` en el body,
    // ni su listener de Escape a nivel documento hacia la sesión nueva. No
    // hay mounts que resetear acá: `mountLiquidacionSection` ya le dio a esta
    // llamada mounts nuevos y vacíos.
    teardownLiquidacionComprobante();
    // Unit S7 (design.md AD16): mismo criterio que la línea de arriba — un
    // print en lote dejado abierto en una visita anterior no debe filtrar su
    // clase de body hacia la sesión nueva. No hay listener/título que
    // restaurar acá (AD16: no es un panel interactivo, no hay "Volver").
    teardownLiquidacionComprobantesLote();
    liquidacionViewModel = null;
    liquidacionUiState = { status: 'loading' };
    liquidacionPreview = null;
    liquidacionConfirmResult = null;
    liquidacionUltimoRango = null;
    pendingLiquidacionConfirm = null;
    pendingLiquidacionAnular = null;
    liquidacionMutationInFlight = false;
    liquidacionAnularInFlight = false;
    liquidacionAnularIncierto = null;
    liquidacionCapConfig = { cargando: true, configurado: false, tope: null, error: null, updatedAt: null, updatedBy: null };
    liquidacionCapDraft = null;
    liquidacionCapSaveInFlight = false;
    liquidacionHistorial = { cargando: true, error: null, liquidaciones: [] };
    liquidacionHistorialPagos = null;
    liquidacionHistorialPagosDesconocidos = new Set();
    liquidacionHistorialFilasMostradas = LIQUIDACION_HISTORIAL_PAGINA;
    // Owner UX batch (2026-09-02): closed by default on every fresh mount —
    // `refreshLiquidacionCapConfig` re-opens `liquidacionConfigAbierta` if
    // the load resolves to "tope not configured".
    liquidacionConfigAbierta = false;
    liquidacionConfigTocadoPorAdmin = false;
    liquidacionMostrarAnuladas = false;
    renderLiquidacionMount({ keepRangoDraft: false });
    renderLiquidacionConfirmMount(null);
    renderLiquidacionAnularMount(null);
    void refreshLiquidacionEmpleados();
    void refreshLiquidacionCapConfig();
    void refreshLiquidacionHistorial();

    // Unit S2a — tablero de período (design.md AD1/AD5): namespace de estado
    // PROPIO, se resetea acá igual que el resto de esta función. `tableroSeq`
    // sube SOLO acá (al montar) — ver su comentario de cabecera para por qué
    // NO sube en cada recompute.
    tableroSeq += 1;
    tableroSeleccion = null;
    tableroCargando = true;
    tableroRefrescando = false;
    tableroMostrarInactivos = false;
    tableroCache = null;
    tableroPersonalizadoDraft = null;
    // S8: "Exportar CSV" — descarta un export dejado en vuelo en una visita anterior.
    tableroExportSeq += 1;
    tableroExportDraft = null;
    tableroExportMensaje = null;
    tableroExportInFlight = false;
    renderTableroMount();
    void initTablero();

    // Unit S4 (design.md AD7): un cajón dejado abierto en una visita anterior
    // nunca sobrevive a un remonte — mismo criterio que
    // `pendingLiquidacionAnular`. `renderTableroDrawerMount()` con
    // `tableroDrawer = null` vacía el mount (ya vacío de todos modos, ver
    // `mountLiquidacionSection`), pero mantiene el estado en JS coherente.
    tableroDrawerSeq += 1;
    tableroDrawerSubmitting = false;
    tableroDrawer = null;

    // Unit S6 — mismo criterio: el área de Configuración nunca sobrevive a un remonte.
    tableroConfigMes = null;
    tableroFeriados = { cargando: true, error: null, lista: [] };
    tableroFeriadosSeq += 1;
    tableroFeriadoDraft = null;
    tableroFeriadoSubmitting = false;
    tableroFeriadoQuitandoFecha = null;
    tableroPeriodoPorDefectoActual = null;
    tableroPeriodoPorDefectoDraft = null;
    tableroPeriodoPorDefectoSaving = false;
    tableroPeriodoPorDefectoError = null;
}

// ---------------------------------------------------------------------------
// Tablero de período de Liquidación (Unit S2a, design.md AD1/AD3/AD4/AD5/AD17)
// ---------------------------------------------------------------------------

/**
 * Resuelve el período por defecto (spec "Default Period From Config", AD17)
 * y dispara el primer recompute. `loadPayrollConfig` expone
 * `periodoPorDefecto` desde S4 (migración 28 agrega la columna,
 * `admin-payroll-config.service.js` la devuelve) — HASTA que S6 agregue el
 * selector "Período por defecto" en la configuración, la columna solo se
 * puede setear a mano en la base, así que en la práctica `config
 * .periodoPorDefecto` sigue siendo `null`/ausente para casi todos y el
 * default sigue cayendo en `'mes'`. Cero cambio de código necesario acá
 * cuando S6 aterrice el selector.
 *
 * S6: esta MISMA llamada a `loadPayrollConfig()` también resuelve
 * `tableroPeriodoPorDefectoActual` (el valor que el área de Configuración
 * muestra preseleccionado en el `<select>`) — un solo round-trip, nunca uno
 * segundo cuando el admin abre esa área más tarde en la misma visita.
 */
async function initTablero() {
    const seq = tableroSeq;
    let kind = 'mes';
    try {
        const config = await loadPayrollConfig();
        if (PERIODO_KINDS.includes(config?.periodoPorDefecto)) kind = config.periodoPorDefecto;
        tableroPeriodoPorDefectoActual = PERIODO_KINDS.includes(config?.periodoPorDefecto) ? config.periodoPorDefecto : null;
    } catch (err) {
        // AD17: una falla acá SOLO degrada el default (queda 'mes'), nunca
        // bloquea el tablero — mismo criterio que el resto de este archivo
        // para lecturas de sólo-pantalla.
        window.Sentry?.captureException(err);
    }
    if (seq !== tableroSeq || !getTableroMount()) return;
    tableroSeleccion = periodoPorDefecto(kind, new Date());
    // `cargando` sólo cubre "todavía no sabemos qué período mostrar" — apenas
    // se resuelve la selección el toolbar ya puede pintarse, aunque el
    // recompute de las cifras (`recomputeTablero`, `refrescando`) siga en
    // vuelo. Sin este corte, el primer preview lento dejaría a el admin sin
    // ningún control (ni "‹ anterior · siguiente ›", ni presets) hasta que
    // resolviera, sin necesidad real: la tabla de filas es la única parte que
    // depende de esa respuesta.
    tableroCargando = false;
    renderTableroMount();
    await recomputeTablero();
}

/**
 * S2b follow-up c (review-5a2ac077e1b2c3d4): `splitTableroRoster`/
 * `applyPreviewToFilas`/`computeTableroTotales` moved to the pure module
 * `js/shared/liquidacion-tablero-policy.js` (imported above) — no DOM, no
 * I/O, no `Date.now()`, unit-tested directly in
 * `tests/unit/liquidacion-tablero-policy.test.js`. This file only calls them.
 */

/** review-982449c9e9c27e08 (findings 1/2/3) — `tableroCache` degradada, `filas` vacío a propósito: nunca una tabla vacía indistinguible de "no hay empleados". */
function tableroErrorCache(rango, kind, mensaje) {
    return {
        desde: rango.desde,
        hasta: rango.hasta,
        calculadoAt: new Date().toISOString(),
        estado: 'error',
        error: { kind, mensaje },
        filas: new Map(),
    };
}

/**
 * Fichadas abiertas de cualquier antigüedad para la nota F-F del tablero
 * (ver `recomputeTablero`). Nunca rechaza: una falla se loguea y devuelve
 * `[]`. `recomputeTablero` SÍ la espera antes de clasificar, así que una
 * lectura colgada lo dejaría "refrescando" para siempre — L9b: va con el
 * mismo tope que el resto de las cargas de sección (`conTimeoutDeCarga`); al
 * vencer se trata igual que una falla y el tablero sigue con la lectura
 * acotada de fichadas.
 * @returns {Promise<Array<object>>}
 */
async function leerAbiertasParaTablero() {
    try {
        const result = await conTimeoutDeCarga(loadJornadasAbiertasOlvidadas());
        return result?.abiertas ?? [];
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('recomputeTablero: no se pudieron leer las jornadas abiertas', err);
        return [];
    }
}

/**
 * Orquesta un recompute completo del tablero (Data Flow de design.md):
 * `rangoDelPeriodo(tableroSeleccion)` -> `loadEmpleados` + `loadFichadas`
 * (padeado, AD3) + `liquidacionHistorial` (ya cargado por
 * `refreshLiquidacionHistorial`, sin fetch propio) + las fichadas abiertas
 * de cualquier antigüedad (`leerAbiertasParaTablero`, F-F) ->
 * `splitTableroRoster` -> UN `previewLiquidacion` para los ids "Listo" ->
 * `tableroCache`.
 *
 * Disparado por: el primer montaje (`initTablero`), un cambio de período
 * (presets/navegación/rango personalizado) y "Actualizar" — TODOS pasan por
 * esta misma función (spec: "Actualizar forces a fresh recompute").
 *
 * GUARD DOBLE post-await (design.md AD5), a propósito DOS mecanismos
 * independientes, no uno solo: `tableroSeq` sólo cambia en un RE-MONTAJE
 * (mountLiquidacionPanel), así que dos recomputes disparados dentro de la
 * MISMA visita a la pestaña (p.ej. "siguiente" clickeado mientras el preview
 * anterior sigue en vuelo) comparten el mismo `tableroSeq` — por eso el
 * re-chequeo de RANGO (recalcular `rangoDelPeriodo(tableroSeleccion)` recién
 * al final y compararlo contra el `rango` con el que arrancó ESTA llamada)
 * es quien realmente descarta una respuesta abandonada dentro de esa misma
 * visita. Sin el re-chequeo de rango, una respuesta lenta pintaría la plata
 * del período anterior bajo el encabezado del nuevo (spec S2a.8).
 */
async function recomputeTablero() {
    if (!tableroSeleccion) return;
    const rango = rangoDelPeriodo(tableroSeleccion);
    if (!rango) return; // selección todavía incompleta (p.ej. "Otro" sin aplicar)

    const mountSeqAtStart = tableroSeq;
    tableroRefrescando = true;
    renderTableroMount();

    // review-5a2ac077e1b2c3d4: los caminos de FALLO necesitan el mismo re-chequeo
    // de rango que el camino exitoso — un fallo tardío de un período abandonado
    // no debe pisar con un banner las cifras vigentes del período actual.
    const sigueVigente = () => {
        if (mountSeqAtStart !== tableroSeq || !getTableroMount()) return false;
        const rangoVigente = rangoDelPeriodo(tableroSeleccion);
        return Boolean(rangoVigente && rangoVigente.desde === rango.desde && rangoVigente.hasta === rango.hasta);
    };

    // review-982449c9e9c27e08: `bail` commits the degraded banner (findings 1/2/3) — never a silent empty table.
    const bail = (kind, mensaje) => {
        if (!sigueVigente()) return;
        tableroCache = tableroErrorCache(rango, kind, mensaje);
        tableroCargando = false;
        tableroRefrescando = false;
        renderTableroMount();
    };

    // finding 3 (AD4): refuse to classify BEFORE reading anything if `desde` predates the loaded historial window.
    const ventanaHistorial = liquidacionHistorialRangoDefault();
    if (rango.desde < ventanaHistorial.inicioDesde) {
        bail(TABLERO_ERROR_KINDS.FUERA_DE_VENTANA, TABLERO_FUERA_DE_VENTANA_MSG);
        return;
    }

    // F-F: la ventana de `fichadas` arranca en desde - TABLERO_QUERY_PAD_DAYS,
    // así que un turno abierto más viejo no está ahí. La MISMA lectura del
    // banner (`listFichadasAbiertas`, a lo sumo una abierta por empleado) los
    // trae, en paralelo; `splitTableroRoster` decide el solape con el mismo
    // helper que el servidor. Es sólo un aviso: si falla o no responde a
    // tiempo (`conTimeoutDeCarga`, L9b — se espera antes de clasificar, así
    // que SÍ puede demorar el tablero hasta ese tope), se loguea y el tablero
    // sigue con la lectura acotada (el confirm del servidor igual rechaza con
    // 409 jornadas_abiertas).
    const abiertasPromise = leerAbiertasParaTablero();

    let empleados;
    try {
        empleados = await loadEmpleados();
    } catch (err) {
        window.Sentry?.captureException(err);
        // finding 1: a load failure must never fall through to an empty-but-'ok' cache.
        if (mountSeqAtStart === tableroSeq && getTableroMount()) bail(TABLERO_ERROR_KINDS.EMPLEADOS, TABLERO_LOAD_ERROR_MSG);
        return;
    }
    if (mountSeqAtStart !== tableroSeq || !getTableroMount()) return;

    let fichadas;
    try {
        const result = await loadFichadas(ventanaLecturaFichadas(rango, TABLERO_QUERY_PAD_DAYS));
        fichadas = result?.fichadas ?? [];
    } catch (err) {
        window.Sentry?.captureException(err);
        if (mountSeqAtStart === tableroSeq && getTableroMount()) bail(TABLERO_ERROR_KINDS.FICHADAS, TABLERO_LOAD_ERROR_MSG);
        return;
    }
    if (mountSeqAtStart !== tableroSeq || !getTableroMount()) return;

    // finding 2: await the in-flight historial load (fired in parallel from mountLiquidacionPanel) before classifying;
    // it never rejects, so a failure surfaces via `liquidacionHistorial.error` below.
    if (liquidacionHistorialPromise) await liquidacionHistorialPromise;
    if (mountSeqAtStart !== tableroSeq || !getTableroMount()) return;
    if (liquidacionHistorial?.error) {
        bail(TABLERO_ERROR_KINDS.HISTORIAL, TABLERO_LOAD_ERROR_MSG);
        return;
    }

    const abiertas = await abiertasPromise;
    if (mountSeqAtStart !== tableroSeq || !getTableroMount()) return;

    // S2b (spec "Row Status Presentation"/"Worked Time Column"): `now` is
    // this file's responsibility to supply — `splitTableroRoster` is a pure
    // function and never reads the clock itself.
    const { filas, listoIds } = splitTableroRoster({
        empleados,
        fichadas,
        abiertas,
        historial: liquidacionHistorial?.liquidaciones ?? [],
        desde: rango.desde,
        hasta: rango.hasta,
        mostrarInactivos: tableroMostrarInactivos,
        now: new Date(),
    });

    let previewResult = { ok: true, liquidaciones: [] };
    if (listoIds.length > 0) {
        try {
            previewResult = await previewLiquidacion({ empleadoIds: listoIds, desde: rango.desde, hasta: rango.hasta });
            // Defensivo: un `previewLiquidacion` mockeado sin implementación
            // (otros archivos de test que entran a esta pestaña sin razón
            // para mockear el servicio de liquidación) devuelve `undefined`,
            // no un envelope — normalizarlo acá evita que se propague hasta
            // el `.ok` de más abajo. Un servicio REAL nunca resuelve así.
            if (!previewResult || typeof previewResult !== 'object') {
                previewResult = { ok: false, kind: 'error', mensaje: LIQUIDACION_GENERIC_ERROR_MSG };
            }
        } catch (err) {
            window.Sentry?.captureException(err);
            previewResult = { ok: false, kind: 'error', mensaje: LIQUIDACION_GENERIC_ERROR_MSG };
        }
    }

    // Guard doble — ver el docstring de esta función.
    if (mountSeqAtStart !== tableroSeq) return;
    const rangoVigente = rangoDelPeriodo(tableroSeleccion);
    if (!rangoVigente || rangoVigente.desde !== rango.desde || rangoVigente.hasta !== rango.hasta) return;
    if (!getTableroMount()) return;

    applyPreviewToFilas(filas, previewResult);

    // S5b (design.md AD14/AD18): UN solo listPagos batcheado cubre tanto la
    // pill de pago de las filas "Liquidada" como el aviso de pagos previos
    // bajo una liquidación anulada solapada — nunca dos lecturas separadas.
    // El id set junta las liquidaciones "Liquidada" del período (para la
    // pill) MÁS las `anulada`s del historial que solapan el período (AD18) —
    // MISMO predicado de overlap que AD4, nunca containment.
    const idsLiquidacionesPago = new Set();
    for (const fila of filas.values()) {
        if (fila.bucket === 'liquidada' && fila.liquidacionRow) idsLiquidacionesPago.add(fila.liquidacionRow.id);
    }
    const historialRows = liquidacionHistorial?.liquidaciones ?? [];
    for (const l of historialRows) {
        if (l.estado === 'anulada' && l.periodo_desde <= rango.hasta && l.periodo_hasta >= rango.desde) {
            idsLiquidacionesPago.add(l.id);
        }
    }

    // review-982449c9e9c27e08's lesson aplicada acá también: una falla de
    // ESTA lectura nunca debe tirar abajo las cifras de plata ya resueltas —
    // sólo degrada la pill/el aviso a ausentes (AD19-style: "—" para lo que
    // no se pudo leer, nunca para lo que sí).
    //
    // U4: a "Sin horas" row cannot be settled, so the advances dated in the
    // period would never be deducted — ONE batched read (only when there is
    // at least one such row) feeds a warning under its status. Money math is
    // untouched. It runs in PARALLEL with the pagos read (neither depends on
    // the other); a failure of either never drops the recompute.
    const sinHorasIds = [...filas.values()].filter((f) => f.bucket === 'sin_horas').map((f) => f.id);
    // Spec liquidacion-otros-descuentos (B5): the same warning for the otros
    // descuentos of those rows, read in the same parallel batch.
    const [pagosLectura, adelantosLectura, otrosDescuentosLectura] = await Promise.allSettled([
        idsLiquidacionesPago.size > 0 ? (async () => loadPagos({ liquidacionIds: [...idsLiquidacionesPago] }))() : Promise.resolve([]),
        sinHorasIds.length > 0 ? (async () => loadAdelantos({ empleadoIds: sinHorasIds, desde: rango.desde, hasta: rango.hasta }))() : Promise.resolve([]),
        sinHorasIds.length > 0 ? (async () => loadOtrosDescuentos({ empleadoIds: sinHorasIds, desde: rango.desde, hasta: rango.hasta }))() : Promise.resolve([]),
    ]);

    // Otro punto de await — mismo re-chequeo que el guard doble de arriba.
    if (!sigueVigente()) return;

    let pagos = [];
    if (pagosLectura.status === 'fulfilled') {
        pagos = pagosLectura.value;
    } else {
        window.Sentry?.captureException(pagosLectura.reason);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('recomputeTablero: no se pudo leer pagos', pagosLectura.reason);
        pagos = null; // degradado: pill/aviso quedan ausentes, el resto del recompute sigue
    }

    if (pagos !== null) {
        const pagosPorLiquidacion = new Map();
        for (const p of pagos) {
            const arr = pagosPorLiquidacion.get(p.liquidacion_id);
            if (arr) arr.push(p);
            else pagosPorLiquidacion.set(p.liquidacion_id, [p]);
        }
        const avisos = pagosPreviosDeAnuladas(historialRows, pagos, { desde: rango.desde, hasta: rango.hasta });
        for (const fila of filas.values()) {
            // WARNING review-a57c3fe2ce2f2297, finding 2: una fila con datos
            // corruptos (p.ej. un `pago.monto` no numérico) nunca debe tirar
            // abajo el recompute entero — se degrada SÓLO esa pill (dash) y
            // el loop sigue, mismo criterio que la degradación de `pagos === null`.
            try {
                if (fila.bucket === 'liquidada' && fila.liquidacionRow && fila.figuras) {
                    const neto = Number(fila.figuras?.neto) || 0;
                    fila.pill = estadoPago({ neto, pagos: pagosPorLiquidacion.get(fila.liquidacionRow.id) ?? [] }).pill;
                }
                fila.avisoPagoPrevio = avisos.get(fila.id) ?? null;
            } catch (err) {
                window.Sentry?.captureException(err);
                // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
                console.error('recomputeTablero: no se pudo derivar la pill de pago de una fila', err);
                fila.pill = null;
            }
        }
    }

    // U4 (read above, in parallel with pagos). A failed read never looks like
    // "no advances": those rows get a neutral note instead of the warning.
    if (sinHorasIds.length > 0) {
        if (adelantosLectura.status === 'fulfilled') {
            const totales = adelantosActivosPorEmpleado(adelantosLectura.value);
            for (const id of sinHorasIds) {
                const fila = filas.get(id);
                if (fila) fila.adelantosSinDescontar = totales.get(id) ?? null;
            }
        } else {
            window.Sentry?.captureException(adelantosLectura.reason);
            // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
            console.error('recomputeTablero: no se pudieron leer los adelantos de las filas "Sin horas"', adelantosLectura.reason);
            for (const id of sinHorasIds) {
                const fila = filas.get(id);
                if (fila) fila.adelantosSinVerificar = true;
            }
        }
        if (otrosDescuentosLectura.status === 'fulfilled') {
            const totales = adelantosActivosPorEmpleado(otrosDescuentosLectura.value);
            for (const id of sinHorasIds) {
                const fila = filas.get(id);
                if (fila) fila.otrosDescuentosSinDescontar = totales.get(id) ?? null;
            }
        } else {
            window.Sentry?.captureException(otrosDescuentosLectura.reason);
            // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
            console.error('recomputeTablero: no se pudieron leer los otros descuentos de las filas "Sin horas"', otrosDescuentosLectura.reason);
            for (const id of sinHorasIds) {
                const fila = filas.get(id);
                if (fila) fila.otrosDescuentosSinVerificar = true;
            }
        }
    }

    tableroCache = {
        desde: rango.desde,
        hasta: rango.hasta,
        calculadoAt: new Date().toISOString(),
        estado: previewResult.ok ? 'ok' : 'error',
        // `kind: 'anomalias'` (whole-batch 409 anomalias_pendientes) is the
        // ONE service envelope whose `mensaje` is intentionally empty
        // elsewhere (the legacy anomalies gate renders the list instead) —
        // AD19's banner always needs text, so a falsy `mensaje` falls back
        // to the same generic copy this file already uses for a network
        // failure, never a new literal minted for this one kind.
        error: previewResult.ok ? null : { kind: previewResult.kind, mensaje: previewResult.mensaje || LIQUIDACION_GENERIC_ERROR_MSG },
        filas,
    };
    // Un feriado agregado/quitado mientras ESTE recompute todavía estaba en
    // vuelo (`tableroCache` era `null` cuando `markTableroCacheStale` corrió)
    // no debe perder el aviso: se aplica acá, sobre la cache recién escrita —
    // SÓLO si el rango pendiente coincide con el de ESTE recompute (finding 2,
    // review-130d19515f9f2340): un cambio de período de por medio descarta el aviso heredado.
    if (tableroCacheStalePendiente) {
        if (tableroCacheStalePendiente.desde === rango.desde && tableroCacheStalePendiente.hasta === rango.hasta) {
            tableroCache = { ...tableroCache, stale: true };
        }
        tableroCacheStalePendiente = null;
    }
    tableroCargando = false;
    tableroRefrescando = false;
    renderTableroMount();
}

/**
 * Guarda lo tipeado en el form de rango personalizado ("Otro") antes de un
 * repintado — MISMA TRAMPA, misma solución que `captureLiquidacionRangoDraft`
 * (spec S2a.9). El form está SIEMPRE en el DOM (oculto vía clase cuando el
 * preset activo no es "personalizado"), así que esto no depende de qué
 * preset esté seleccionado.
 * @param {HTMLElement} mount
 */
function captureTableroPersonalizadoDraft(mount) {
    const form = mount.querySelector('form[data-action-form="tablero-personalizado"]');
    if (!form) return;
    tableroPersonalizadoDraft = {
        desde: form.querySelector('[name="desde"]')?.value ?? '',
        hasta: form.querySelector('[name="hasta"]')?.value ?? '',
    };
}

/** @param {HTMLElement} mount */
function applyTableroPersonalizadoDraft(mount) {
    if (!tableroPersonalizadoDraft) return;
    const form = mount.querySelector('form[data-action-form="tablero-personalizado"]');
    if (!form) return;
    const desdeInput = form.querySelector('[name="desde"]');
    if (desdeInput) desdeInput.value = tableroPersonalizadoDraft.desde ?? '';
    const hastaInput = form.querySelector('[name="hasta"]');
    if (hastaInput) hastaInput.value = tableroPersonalizadoDraft.hasta ?? '';
}

/**
 * S8 — guarda lo tipeado en el mini-form "Exportar CSV" de la barra en lote
 * antes de un repintado. MISMA TRAMPA que `captureTableroPersonalizadoDraft`.
 * @param {HTMLElement} mount
 */
function captureTableroExportDraft(mount) {
    const form = mount.querySelector('form[data-action-form="tablero-export"]');
    if (!form) return;
    tableroExportDraft = {
        mes: form.querySelector('[name="mes-export"]')?.value ?? '',
        incluirAnuladas: form.querySelector('[name="incluir-anuladas-export"]')?.checked ?? false,
    };
}

/** S8 — re-puebla el mini-form "Exportar CSV" con el borrador guardado, si hay uno. @param {HTMLElement} mount */
function applyTableroExportDraft(mount) {
    if (!tableroExportDraft) return;
    const form = mount.querySelector('form[data-action-form="tablero-export"]');
    if (!form) return;
    const mesInput = form.querySelector('[name="mes-export"]');
    if (mesInput) mesInput.value = tableroExportDraft.mes ?? '';
    const checkbox = form.querySelector('[name="incluir-anuladas-export"]');
    if (checkbox) checkbox.checked = Boolean(tableroExportDraft.incluirAnuladas);
}

/** Re-renderiza `#admin-liquidacion-tablero-mount` y re-cablea sus listeners — mismo patrón `capture -> innerHTML -> apply -> bind` que `renderLiquidacionMount`. */
function renderTableroMount() {
    const mount = getTableroMount();
    if (!mount) return;

    captureTableroPersonalizadoDraft(mount);
    captureTableroExportDraft(mount);

    const rangoActual = tableroSeleccion ? rangoDelPeriodo(tableroSeleccion) : null;
    mount.innerHTML = LiquidacionTableroView({
        seleccion: tableroSeleccion,
        cargando: tableroCargando,
        refrescando: tableroRefrescando,
        // review-62957fd6e8912f8e: `liquidacionMutationInFlight` es dueño de
        // este flag (`runLiquidacionPreview`/confirm) — el tablero solo lo
        // LEE para deshabilitar sus propios botones "Liquidar".
        liquidando: liquidacionMutationInFlight,
        // L1/D2: un período que termina hoy (ART) o después se puede mirar
        // pero no liquidar — el servidor rechaza el confirm con
        // periodo_no_terminado, con la MISMA regla (`periodoTerminado`).
        periodoNoTerminado: periodoNoTerminadoDe(rangoActual),
        mostrarInactivos: tableroMostrarInactivos,
        desde: tableroCache?.desde ?? rangoActual?.desde ?? null,
        hasta: tableroCache?.hasta ?? rangoActual?.hasta ?? null,
        filas: tableroCache ? [...tableroCache.filas.values()] : [],
        totales: computeTableroTotales(tableroCache?.filas ?? null),
        calculadoAt: tableroCache?.calculadoAt ?? null,
        estado: tableroCache?.estado ?? null,
        error: tableroCache?.error ?? null,
        draft: tableroPersonalizadoDraft,
        stale: Boolean(tableroCache?.stale),
        // S8 (spec "Row and Bulk Actions" — "Exportar CSV"): mientras no haya
        // borrador todavía, pinta el default (`mesLiquidacionPorDefecto()`,
        // checkbox destildado) directamente acá — mismo criterio que el
        // toolbar legacy del que se mudó.
        exportState: {
            mes: tableroExportDraft?.mes ?? mesLiquidacionPorDefecto(),
            incluirAnuladas: Boolean(tableroExportDraft?.incluirAnuladas),
            exportando: tableroExportInFlight,
            mensaje: tableroExportMensaje,
        },
    });

    applyTableroPersonalizadoDraft(mount);
    applyTableroExportDraft(mount);
    bindTableroMountListeners(mount);
}

/**
 * Click en un preset del toolbar. `quincena-1`/`quincena-2` y `mes` reusan
 * el `mes` de referencia de la selección vigente (o el mes-por-defecto si
 * todavía no había uno) — sólo cambian `kind`/`mitad`, preservando
 * continuidad razonable. "Otro" no dispara recompute todavía: sólo revela
 * el form de rango personalizado (`handleTableroPersonalizadoSubmit` es
 * quien realmente cambia el período).
 * NO se guarda detrás de `tableroRefrescando` a propósito (spec S2a.8: "el
 * admin cambia de período antes de que llegue la primera respuesta" tiene
 * que poder pasar) — la respuesta abandonada del período anterior la
 * descarta el re-chequeo de rango post-await de `recomputeTablero`, no un
 * guard que bloquee el click acá.
 * @param {string} kind uno de `PRESETS[].kind` en `LiquidacionTableroView.js`
 */
function handleTableroPresetClick(kind) {
    liquidacionConfirmResult = null; // review-e2599502b715d72e-r2: un cambio de período invalida un rechazo de Liquidar ya pintado
    const now = new Date();
    const mesReferencia = tableroSeleccion?.mes ?? periodoPorDefecto('mes', now).mes;

    if (kind === 'mes') {
        tableroSeleccion = { kind: 'mes', mes: mesReferencia };
    } else if (kind === 'quincena-1' || kind === 'quincena-2') {
        tableroSeleccion = { kind: 'quincena', mes: mesReferencia, mitad: kind === 'quincena-2' ? '2' : '1' };
    } else if (kind === 'semana') {
        tableroSeleccion = periodoPorDefecto('semana', now);
    } else if (kind === 'personalizado') {
        tableroSeleccion = { kind: 'personalizado', desde: '', hasta: '' };
        renderTableroMount(); // solo revela el form; el recompute espera al submit
        return;
    } else {
        return;
    }
    void recomputeTablero();
}

/** Mismo criterio de `handleTableroPresetClick`: no se guarda detrás de `tableroRefrescando`. */
function handleTableroAnteriorClick() {
    const prev = periodoAnterior(tableroSeleccion);
    if (!prev) return;
    liquidacionConfirmResult = null; // review-e2599502b715d72e-r2
    tableroSeleccion = prev;
    void recomputeTablero();
}

/** Mismo criterio de `handleTableroPresetClick`: no se guarda detrás de `tableroRefrescando`. */
function handleTableroSiguienteClick() {
    const next = periodoSiguiente(tableroSeleccion);
    if (!next) return;
    liquidacionConfirmResult = null; // review-e2599502b715d72e-r2
    tableroSeleccion = next;
    void recomputeTablero();
}

/** Mismo criterio de `handleTableroPresetClick`: no se guarda detrás de `tableroRefrescando`. @param {SubmitEvent} e */
function handleTableroPersonalizadoSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const desde = form.querySelector('[name="desde"]')?.value ?? '';
    const hasta = form.querySelector('[name="hasta"]')?.value ?? '';
    const rango = rangoPersonalizado(desde, hasta);
    if (!rango) {
        showToast('El rango de fechas no es válido.', { level: 'error' });
        return;
    }
    liquidacionConfirmResult = null; // review-e2599502b715d72e-r2
    tableroSeleccion = { kind: 'personalizado', desde: rango.desde, hasta: rango.hasta };
    tableroPersonalizadoDraft = null;
    void recomputeTablero();
}

/** @param {HTMLElement} mount */
function bindTableroMountListeners(mount) {
    for (const btn of mount.querySelectorAll('[data-action="tablero-preset"]')) {
        btn.addEventListener('click', () => handleTableroPresetClick(btn.dataset.kind));
    }
    const anteriorBtn = mount.querySelector('[data-action="tablero-anterior"]');
    if (anteriorBtn) anteriorBtn.addEventListener('click', handleTableroAnteriorClick);
    const siguienteBtn = mount.querySelector('[data-action="tablero-siguiente"]');
    if (siguienteBtn) siguienteBtn.addEventListener('click', handleTableroSiguienteClick);
    const actualizarBtn = mount.querySelector('[data-action="tablero-actualizar"]');
    if (actualizarBtn) actualizarBtn.addEventListener('click', () => void recomputeTablero());
    // review-982449c9e9c27e08 (AD19): "Reintentar" es el mismo camino que "Actualizar".
    const reintentarBtn = mount.querySelector('[data-action="tablero-reintentar"]');
    if (reintentarBtn) reintentarBtn.addEventListener('click', () => void recomputeTablero());
    const inactivosToggle = mount.querySelector('[data-action="tablero-mostrar-inactivos"]');
    if (inactivosToggle) {
        inactivosToggle.addEventListener('change', () => {
            tableroMostrarInactivos = inactivosToggle.checked;
            void recomputeTablero();
        });
    }
    const personalizadoForm = mount.querySelector('form[data-action-form="tablero-personalizado"]');
    if (personalizadoForm) personalizadoForm.addEventListener('submit', handleTableroPersonalizadoSubmit);
    // Unit S6 (design.md AD7 extendido): abre el cajón "Configuración".
    const configAbrirBtn = mount.querySelector('[data-action="tablero-config-abrir"]');
    if (configAbrirBtn) configAbrirBtn.addEventListener('click', handleTableroConfigAbrirClick);
    // Unit S4 (design.md AD7): abre el cajón "Adelanto" para la fila clicada.
    for (const btn of mount.querySelectorAll('[data-action="tablero-adelanto"]')) {
        btn.addEventListener('click', handleTableroAdelantoClick);
    }
    // Spec liquidacion-otros-descuentos (B1): abre el cajón "Otros descuentos".
    for (const btn of mount.querySelectorAll('[data-action="tablero-descontar"]')) {
        btn.addEventListener('click', handleTableroDescontarClick);
    }
    // Unit S5b (design.md AD7): abre el cajón "Registrar pago" para la fila "Liquidada" clicada.
    for (const btn of mount.querySelectorAll('[data-action="tablero-pago"]')) {
        btn.addEventListener('click', handleTableroPagoClick);
    }
    // Unit S3 (design.md AD7): abre el cajón de anomalías — el `data-action`
    // ya lo renderiza `LiquidacionTableroView.js#renderStatusPill` sobre la
    // pill "N anomalías" misma (S2a), sin cablear todavía hasta esta unidad.
    for (const pill of mount.querySelectorAll('[data-action="tablero-abrir-anomalias"]')) {
        pill.addEventListener('click', handleTableroAbrirAnomaliasClick);
    }
    // Unit S7 (design.md AD6/AD15): "Liquidar" de una fila y "Liquidar N
    // listos" comparten el MISMO flujo de re-preview + confirm — ver
    // `iniciarLiquidarDesdeTablero`.
    for (const btn of mount.querySelectorAll('[data-action="tablero-liquidar"]')) {
        btn.addEventListener('click', handleTableroLiquidarClick);
    }
    const liquidarLoteBtn = mount.querySelector('[data-action="tablero-liquidar-lote"]');
    if (liquidarLoteBtn) liquidarLoteBtn.addEventListener('click', handleTableroLiquidarLoteClick);
    // Unit S7 (design.md AD16): print en lote de las filas "Liquidada".
    const imprimirLoteBtn = mount.querySelector('[data-action="tablero-imprimir-lote"]');
    if (imprimirLoteBtn) imprimirLoteBtn.addEventListener('click', handleTableroImprimirLoteClick);
    // S8 (spec "Row and Bulk Actions" — "Exportar CSV"): mes/checkbox SIN
    // listener propio (mismo criterio AD16 original: ningún cambio repinta
    // el mount) — se leen del DOM recién al clickear.
    const exportarBtn = mount.querySelector('[data-action="tablero-exportar"]');
    if (exportarBtn) exportarBtn.addEventListener('click', handleTableroExportarClick);
}

// ---------------------------------------------------------------------------
// Liquidar desde el tablero (Unit S7, design.md AD6/AD15) — fila individual
// o "Liquidar N listos" en lote. Reusa VERBATIM la máquina legacy
// (`runLiquidacionPreview`/`liquidacionPreview`/`handleLiquidacionConfirmarAbrir`/
// `pendingLiquidacionConfirm`/`handleLiquidacionConfirmConfirmar`) per el
// "Technical Approach" de design.md — es la MISMA máquina que ya resuelve
// anomalías/tope/rechazo parcial, así que el tablero no duplica ese árbol de
// estados. Efecto secundario aceptado y documentado (ver apply-progress.md):
// el ConfirmDialog abre en su propio mount (`#admin-liquidacion-confirm-mount`,
// sin tocar el tablero), pero el preview intermedio SÍ repinta el panel
// legacy de abajo (`#admin-liquidacion-mount`) — el mismo mount donde,
// dado un rechazo parcial, `renderRechazoParcial` termina apareciendo.
// ---------------------------------------------------------------------------

/**
 * Ids del lote de "Liquidar N listos" del período vigente: `loteLiquidable`,
 * la misma función con la que la vista cuenta el botón. Son las filas
 * "Listo" (nunca "Sin horas"/"N anomalías"/"Liquidada") MENOS, U3, los
 * sueldos fijos cuando el período no es un mes calendario completo. Ese es
 * el conjunto que el batch `preview` + `confirm` en lote liquida; no es
 * "todas las filas Listo" (un fijo excluido sigue "Listo" y se liquida por
 * su propio "Liquidar").
 * @returns {string[]}
 */
function getTableroLoteIds() {
    if (!tableroCache?.filas) return [];
    return loteLiquidable(tableroCache.filas.values(), { desde: tableroCache.desde, hasta: tableroCache.hasta }).ids;
}

/**
 * AD6: SIEMPRE re-corre `preview` server-side antes de ofrecer el
 * `ConfirmDialog` — nunca confirma desde `tableroCache`. `status:'ready'` es
 * el único caso que abre el diálogo; cualquier otro rechazo se traduce acá
 * (`mapLiquidacionPreviewRechazo`, correction review-e2599502b715d72e) al
 * MISMO panel de confirm que `handleLiquidacionConfirmConfirmar` — antes
 * quedaba silencioso (`renderPreviewSection` está retirado desde S8).
 * @param {string[]} empleadoIds
 */
async function iniciarLiquidarDesdeTablero(empleadoIds) {
    if (liquidacionMutationInFlight) return;
    // review-62957fd6e8912f8e: un recompute en vuelo (Actualizar/preset/
    // navegación) todavía no tiene un `tableroCache` confiable — nunca
    // arrancar un preview de liquidar sobre datos a mitad de refrescar.
    if (tableroRefrescando) return;
    if (!tableroSeleccion) return;
    const rango = rangoDelPeriodo(tableroSeleccion);
    if (!rango) return;
    await runLiquidacionPreview({ empleadoIds, desde: rango.desde, hasta: rango.hasta });
    // review-e2599502b715d72e-r2: mismo re-chequeo de rango que `sigueVigente`
    // (recomputeTablero) — un preview lento de un período ya abandonado nunca
    // debe pintar su rechazo bajo el período vigente.
    const rangoVigente = getTableroMount() ? rangoDelPeriodo(tableroSeleccion) : null;
    if (!rangoVigente || rangoVigente.desde !== rango.desde || rangoVigente.hasta !== rango.hasta) return;
    if (liquidacionPreview?.status === 'ready') {
        handleLiquidacionConfirmarAbrir();
        return;
    }
    if (liquidacionPreview && liquidacionPreview.status !== 'loading') {
        liquidacionConfirmResult = mapLiquidacionPreviewRechazo(liquidacionPreview);
        if (getLiquidacionMount()) renderLiquidacionMount();
    }
    if (getTableroMount()) renderTableroMount();
}

/**
 * Traduce un `liquidacionPreview` NO `ready` al shape de
 * `liquidacionConfirmResult` que `renderConfirmResultSection` ya sabe
 * pintar. `bloqueado` (Fudo) no tiene status propio ahí: cae al genérico
 * `error`, mismo criterio que `handleLiquidacionConfirmConfirmar`.
 * @param {object} preview
 * @returns {object}
 */
function mapLiquidacionPreviewRechazo(preview) {
    if (preview.status === 'anomalias') {
        const nombrePorId = new Map((liquidacionViewModel?.empleados ?? []).map((e) => [e.id, e.nombre]));
        return { status: 'anomalias', nombres: (preview.anomalias ?? []).map((a) => nombrePorId.get(a.empleado_id) ?? 'Empleado') };
    }
    if (preview.status === 'tope_no_configurado' || preview.status === 'precondicion_bloqueante') {
        return { status: preview.status, mensaje: preview.mensaje, detalle: preview.detalle };
    }
    return { status: 'error', mensaje: preview.mensaje || LIQUIDACION_GENERIC_ERROR_MSG };
}

/** "Liquidar" de UNA fila "Listo" — lee `data-id` del botón clicado. @param {MouseEvent} e */
function handleTableroLiquidarClick(e) {
    const { id } = e.currentTarget.dataset;
    if (!id) return;
    void iniciarLiquidarDesdeTablero([id]);
}

/** "Liquidar N listos" (AD15: UN batch `confirm`, nunca N confirms secuenciales). */
function handleTableroLiquidarLoteClick() {
    const ids = getTableroLoteIds();
    if (ids.length === 0) return;
    void iniciarLiquidarDesdeTablero(ids);
}

/**
 * "Imprimir comprobantes (N)" (design.md AD16): un documento por fila
 * "Liquidada" del período vigente, vía `construirComprobante(fila.liquidacionRow)`
 * — CERO lectura nueva, `liquidacionRow` ya viene de `liquidacionHistorial`
 * (cargado por `refreshLiquidacionHistorial`). Mount y clase de body
 * PARALELOS a los del comprobante individual — ese gate no se toca acá.
 */
function handleTableroImprimirLoteClick() {
    const mount = getTableroComprobantesLoteMount();
    if (!mount) return;
    // review-62957fd6e8912f8e: una pasada anterior cuyo `afterprint` nunca
    // disparó (impresión cancelada) no debe dejar clase/mount colgados para
    // esta pasada nueva.
    teardownLiquidacionComprobantesLote();
    mount.innerHTML = '';

    const filas = tableroCache ? [...tableroCache.filas.values()] : [];
    const liquidadas = filas.filter((f) => f.bucket === 'liquidada' && f.liquidacionRow);
    if (liquidadas.length === 0) return;

    let html;
    try {
        html = liquidadas.map((f) => ComprobanteLiquidacionView(construirComprobante(f.liquidacionRow), { firma: true })).join('');
    } catch (err) {
        window.Sentry?.captureException(err);
        showToast('No se pudieron generar los comprobantes.', { level: 'error' });
        return;
    }

    mount.innerHTML = html;
    document.body.classList.add('comprobantes-lote-abierto');

    // review-62957fd6e8912f8e: `afterprint` es cross-browser (a diferencia
    // del valor de retorno de `print()`, que no lo expone) — un solo disparo
    // que limpia clase y mount apenas el diálogo se cierra, cancelado o no.
    window.addEventListener(
        'afterprint',
        () => {
            teardownLiquidacionComprobantesLote();
            mount.innerHTML = '';
        },
        { once: true },
    );

    try {
        window.print();
    } catch (err) {
        // Mismo criterio que `openLiquidacionComprobante`: algunos navegadores
        // embebidos bloquean `print()` — decirlo en vez de fallar en silencio.
        window.Sentry?.captureException(err);
        showToast('No se pudo abrir el diálogo de impresión. Probá con Ctrl+P.', { level: 'error' });
    }
}

// ---------------------------------------------------------------------------
// Cajón "Adelanto" (Unit S4, design.md AD7) — comparte DrawerShell.js /
// #admin-liquidacion-drawer-mount con el cajón de anomalías que S3 agrega
// después (ver el comentario de cabecera de `mountLiquidacionSection`).
// ---------------------------------------------------------------------------

/**
 * Configuración de los dos cajones de movimientos de sueldo que comparten
 * handlers: "Adelanto" (S4) y "Otros descuentos" (spec
 * liquidacion-otros-descuentos). Lo único que cambia entre los dos es lo que
 * está acá: el form, el campo de texto (`nota` opcional o `concepto`
 * obligatorio), el servicio y las copias. El flujo (borradores, `incierto`,
 * recompute, foco) es uno solo.
 */
const CAJON_MOVIMIENTO = {
    adelanto: {
        titulo: 'Adelanto',
        vista: AdelantoDrawerView,
        formCrear: 'adelanto-crear',
        campoTexto: 'nota',
        textoObligatorio: false,
        errorIncompleto: 'Completá una fecha y un monto válido.',
        motivoVacio: 'Ingresá un motivo.',
        accionActualizar: 'tablero-adelanto-actualizar',
        accionAnularAbrir: 'adelanto-anular-abrir',
        accionAnularCancelar: 'adelanto-anular-cancelar',
        formAnular: 'adelanto-anular-confirmar',
        toastCreado: 'Adelanto registrado.',
        toastAnulado: 'Adelanto anulado.',
        cargar: (params) => loadAdelantos(params),
        crear: ({ empleadoId, fecha, monto, texto }) => crearAdelanto({ empleadoId, fecha, monto, nota: texto || null }),
        anular: ({ id, motivo }) => anularAdelanto({ adelantoId: id, motivo }),
    },
    otro_descuento: {
        titulo: 'Otros descuentos',
        vista: OtrosDescuentosDrawerView,
        formCrear: 'otro-descuento-crear',
        campoTexto: 'concepto',
        textoObligatorio: true,
        errorIncompleto: 'Completá una fecha, un monto válido y el concepto.',
        motivoVacio: 'Ingresá un motivo para anular el otro descuento.',
        accionActualizar: 'tablero-otro-descuento-actualizar',
        accionAnularAbrir: 'otro-descuento-anular-abrir',
        accionAnularCancelar: 'otro-descuento-anular-cancelar',
        formAnular: 'otro-descuento-anular-confirmar',
        toastCreado: 'Otro descuento registrado.',
        toastAnulado: 'Otro descuento anulado.',
        cargar: (params) => loadOtrosDescuentos(params),
        crear: ({ empleadoId, fecha, monto, texto }) => crearOtroDescuento({ empleadoId, fecha, monto, concepto: texto.trim() }),
        anular: ({ id, motivo }) => anularOtroDescuento({ descuentoId: id, motivo }),
    },
};

/** @param {string|undefined|null} tipo @returns {object|null} la configuración del cajón de movimiento, o `null` si `tipo` no es uno. */
function cajonMovimiento(tipo) {
    return Object.hasOwn(CAJON_MOVIMIENTO, tipo ?? '') ? CAJON_MOVIMIENTO[tipo] : null;
}

/**
 * Abre el cajón de un movimiento ("Adelanto" u "Otros descuentos") para la
 * fila clicada — lee `data-id` (`empleadoId`) y el nombre desde
 * `tableroCache.filas` (ya cargado, mismo criterio que
 * `handleLiquidacionAnularClick` leyendo del historial). Dispara la carga
 * async de la lista activa/anulada; el form de alta se puede usar de
 * inmediato, sin esperar esa carga (`cargando: true` sólo tapa la lista).
 * @param {MouseEvent} e
 * @param {'adelanto'|'otro_descuento'} tipo
 */
function abrirCajonMovimiento(e, tipo) {
    const { id } = e.currentTarget.dataset;
    const cfg = cajonMovimiento(tipo);
    if (!id || !cfg) return;
    tableroDrawerTrigger = e.currentTarget;
    const fila = tableroCache?.filas?.get(id);
    tableroDrawer = {
        tipo,
        empleadoId: id,
        empleadoNombre: fila?.nombre ?? '',
        fecha: '',
        monto: '',
        [cfg.campoTexto]: '',
        cargando: true,
        activos: [],
        anulados: [],
        error: null,
        anulandoId: null,
        anularError: null,
    };
    renderTableroDrawerMount();
    void refreshTableroMovimientosList();
}

/** "Adelanto" en una fila del tablero. @param {MouseEvent} e */
function handleTableroAdelantoClick(e) {
    abrirCajonMovimiento(e, 'adelanto');
}

/** "Descontar" en una fila del tablero (spec liquidacion-otros-descuentos, B1). @param {MouseEvent} e */
function handleTableroDescontarClick(e) {
    abrirCajonMovimiento(e, 'otro_descuento');
}

/**
 * Trae la lista del cajón de movimientos vigente (adelantos u otros
 * descuentos, activos + anulados) del empleado y la parte en dos — la misma
 * lectura RLS que el resto del panel (`cajonMovimiento(tipo).cargar`), cero write. Guardada por `tableroDrawerListSeq` +
 * `mismoCajonTablero` (S2, ver su comentario), para que una respuesta tardía
 * de un cajón ya cerrado o reabierto para OTRO empleado, o superada por una
 * carga más nueva, nunca lo repinte, sin perder la única carga en vuelo.
 */
async function refreshTableroMovimientosList() {
    const pending = tableroDrawer;
    const cfg = cajonMovimiento(pending?.tipo);
    if (!pending || !cfg) return;
    const listSeq = ++tableroDrawerListSeq;
    const vigente = () => listSeq === tableroDrawerListSeq && mismoCajonTablero(tableroDrawer, pending) && !!getTableroDrawerMount();
    try {
        const rows = await cfg.cargar({ empleadoIds: [pending.empleadoId] });
        if (!vigente()) return;
        const activos = rows.filter((r) => !r.anulado_at);
        const anulados = rows.filter((r) => r.anulado_at);
        // Same criterion as `refreshTableroPagosList`: a refresh triggered by
        // an `incierto` closes the blocking notice and brings the form back
        // with monto/nota EMPTY — a retry must be a conscious new entry, never
        // a blind resubmit of an advance that may already exist. Any error
        // left by a failed earlier refresh is stale now that the list loaded.
        if (tableroDrawer.incierto === true) {
            tableroDrawer = { ...tableroDrawer, cargando: false, activos, anulados, monto: '', [cfg.campoTexto]: '', montoIlegible: false, error: null, incierto: false, mensaje: null };
        } else {
            // S13: same `montoIlegible`/`errorPrevio` rule as
            // `refreshTableroPagosList` — a "monto format" notice left by an
            // earlier re-render is stale once the monto holds a value again;
            // it stays while the monto is unreadable or still empty after
            // being flagged (the re-rendered input is no longer `badInput`).
            const borrador = leerBorradorMovimiento(getTableroDrawerMount());
            const montoVivo = borrador?.monto ?? tableroDrawer.monto ?? '';
            const montoInvalido = borrador?.montoIlegible === true || (tableroDrawer.montoIlegible === true && montoVivo === '');
            const errorPrevio = tableroDrawer.error === MONTO_FORMATO_INVALIDO_MSG ? null : tableroDrawer.error;
            tableroDrawer = {
                ...tableroDrawer,
                ...borrador,
                cargando: false,
                activos,
                anulados,
                montoIlegible: montoInvalido,
                error: montoInvalido ? MONTO_FORMATO_INVALIDO_MSG : errorPrevio,
            };
        }
        renderTableroDrawerMount();
    } catch (err) {
        if (!vigente()) return;
        window.Sentry?.captureException(err);
        tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), cargando: false, error: LIQUIDACION_GENERIC_ERROR_MSG };
        renderTableroDrawerMount();
    }
}

/**
 * Live values of a drawer form, read straight from the DOM. Every drawer
 * re-render rebuilds its HTML from `tableroDrawer`, and the form stays usable
 * while requests are in flight, so the rule is: every re-render of a live
 * form merges this live draft into the new model, EXCEPT the fields a
 * successful submit clears because they still equal what was sent. That is
 * why it is not done inside `renderTableroDrawerMount`: each caller decides
 * which fields a success clears. `null` when the form is not rendered (e.g.
 * the `incierto` notice replaces it).
 *
 * Only the `monto` (`type="number"`) is checked for `validity.badInput`:
 * text the browser cannot parse there (e.g. the Argentine "5.000,50") reports
 * `value === ''` with `badInput`. That is NOT an empty field: the admin typed
 * something. It is flagged `montoIlegible: true` (so no caller prefills over
 * it, e.g. the pago saldo), its value is '' (the text itself cannot be
 * written back into a number input) and `error` explains why it was cleared.
 * Any other field (e.g. a half-typed `type="date"`, which Chromium also
 * reports as `badInput`) is read as its plain `value` and never sets that
 * flag, so it can never make a success keep the monto it just sent. The flag
 * is only ever SET here, never cleared: the pago drawer keeps it in its model
 * while the monto stays empty (see `refreshTableroPagosList`), so a later
 * re-render, where the input is no longer `badInput`, still does not prefill
 * over it.
 * @param {Element|null} mount
 * @param {string} formAction `data-action-form` of the form
 * @param {string[]} campos input `name`s to read
 * @returns {(Record<string, string> & { montoIlegible?: true, error?: string })|null}
 */
function leerBorradorForm(mount, formAction, campos) {
    const form = mount?.querySelector(`form[data-action-form="${formAction}"]`);
    if (!form) return null;
    const borrador = {};
    for (const campo of campos) {
        const input = form.querySelector(`[name="${campo}"]`);
        if (!input) continue;
        if (campo === 'monto' && input.validity?.badInput) {
            borrador.monto = '';
            borrador.montoIlegible = true;
            borrador.error = MONTO_FORMATO_INVALIDO_MSG;
        } else {
            borrador[campo] = input.value;
        }
    }
    return borrador;
}

/** @param {Element|null} mount @returns {{fecha?: string, medio?: string, monto?: string, nota?: string}|null} */
function leerBorradorPago(mount) {
    return leerBorradorForm(mount, 'pago-registrar', ['fecha', 'medio', 'monto', 'nota']);
}

/**
 * Borrador vivo del form de alta del cajón de movimientos abierto (adelanto:
 * `nota`; otro descuento: `concepto`). `null` si no hay cajón de movimiento.
 * @param {Element|null} mount
 * @returns {{fecha?: string, monto?: string, nota?: string, concepto?: string}|null}
 */
function leerBorradorMovimiento(mount) {
    const cfg = cajonMovimiento(tableroDrawer?.tipo);
    return cfg ? leerBorradorForm(mount, cfg.formCrear, ['fecha', 'monto', cfg.campoTexto]) : null;
}

/**
 * Today's calendar date in ART (fixed UTC-3, `artDateKey`), never the host's
 * local date nor the UTC date of `toISOString()` (both are the next day from
 * 21:00 ART on). Default `fecha` of the "Registrar pago" form (tablero and
 * historial) and the end of the Fichadas «Desde»/«Hasta» default range
 * (`fichadasFechasDefault`).
 * @returns {string} `YYYY-MM-DD`
 */
function hoyArtISO() {
    return artDateKey(Date.now());
}

/**
 * L1/D2 — si el período del tablero todavía no terminó (`periodoTerminado`,
 * la misma regla que el confirm del servidor): `'hoy'` si termina hoy (ART),
 * `'futuro'` si termina después, `null` si ya terminó o no hay rango.
 * @param {{ hasta: string }|null|undefined} rango
 * @returns {'hoy'|'futuro'|null}
 */
function periodoNoTerminadoDe(rango) {
    if (!rango?.hasta) return null;
    const nowMs = Date.now();
    if (periodoTerminado(rango.hasta, nowMs)) return null;
    return rango.hasta === artDateKey(nowMs) ? 'hoy' : 'futuro';
}

// ---------------------------------------------------------------------------
// Cajón "Registrar pago" (Unit S5b, design.md AD7/AD13/AD14/AD18) — comparte
// DrawerShell.js / #admin-liquidacion-drawer-mount con Adelanto (S4) y
// Anomalías (S3). `tableroDrawer.tipo:'pago'` distingue cuál cuerpo renderiza
// `renderTableroDrawerMount`. A diferencia de Adelanto, un pago NUNCA
// dispara `recomputeTablero()` (spec "Payment Changes Do Not Invalidate the
// Dashboard Cache" — un pago jamás cambia el neto): sólo repinta la pill de
// ESA fila, vía el mismo `listPagos` que ya refresca la lista del cajón.
// ---------------------------------------------------------------------------

/**
 * Abre el cajón "Registrar pago" para la fila "Liquidada" clicada — lee
 * `data-id` (`empleadoId`) y `liquidacionId`/`neto` desde `tableroCache.filas`
 * (ya cargado, mismo criterio que `handleTableroAdelantoClick`). Sin
 * `liquidacionRow` (fila no "Liquidada" — no debería poder pasar, el botón
 * sólo se renderiza para ese balde) esto es un no-op defensivo.
 * @param {MouseEvent} e
 */
function handleTableroPagoClick(e) {
    const { id } = e.currentTarget.dataset;
    if (!id) return;
    const fila = tableroCache?.filas?.get(id);
    if (!fila?.liquidacionRow) return;
    tableroDrawerTrigger = e.currentTarget;
    tableroDrawer = {
        tipo: 'pago',
        empleadoId: id,
        empleadoNombre: fila.nombre ?? '',
        liquidacionId: fila.liquidacionRow.id,
        neto: fila.figuras?.neto ?? 0,
        fecha: hoyArtISO(),
        medio: 'efectivo',
        monto: '',
        nota: '',
        cargando: true,
        activos: [],
        anulados: [],
        pagadoCentavos: 0,
        saldoCentavos: 0,
        error: null,
        anulandoId: null,
        anularError: null,
    };
    renderTableroDrawerMount();
    void refreshTableroPagosList();
}

/**
 * S8 (spec `payroll-payments` "Register Payment Drawer", historial payment
 * pill) — abre el MISMO cajón "Registrar pago" que `handleTableroPagoClick`,
 * pero desde una fila del HISTORIAL (`liquidacionHistorial.liquidaciones`)
 * en vez de `tableroCache.filas`: una fila del historial puede pertenecer a
 * un período distinto del seleccionado en el tablero, así que no existe (o
 * no es la misma) en `tableroCache`. Sólo ofrecido para filas `confirmada`
 * (ver `renderHistorialRow`, `LiquidacionView.js`) — una `anulada` no acepta
 * pagos nuevos (AD13: `registrar_pago` refuses `liquidacion_anulada`).
 * @param {MouseEvent} e
 */
function handleLiquidacionHistorialPagoClick(e) {
    const { id } = e.currentTarget.dataset;
    if (!id) return;
    const row = (liquidacionHistorial?.liquidaciones ?? []).find((r) => r.id === id);
    if (!row) return;
    tableroDrawerTrigger = e.currentTarget;
    tableroDrawer = {
        tipo: 'pago',
        empleadoId: row.empleado_id,
        empleadoNombre: row.empleados?.nombre ?? '',
        liquidacionId: row.id,
        neto: row.neto ?? 0,
        fecha: hoyArtISO(),
        medio: 'efectivo',
        monto: '',
        nota: '',
        cargando: true,
        activos: [],
        anulados: [],
        pagadoCentavos: 0,
        saldoCentavos: 0,
        error: null,
        anulandoId: null,
        anularError: null,
    };
    renderTableroDrawerMount();
    void refreshTableroPagosList();
}

/**
 * L5 — "Anular" sobre un pago de una liquidación ANULADA, desde el historial
 * ("Mostrar anuladas"). Abre el MISMO cajón de pagos que
 * `handleLiquidacionHistorialPagoClick`, pero en modo `soloLectura` (sin el
 * form de alta: `registrar_pago` rechaza una liquidación anulada) y con el
 * sub-form de anulación de ESE pago ya abierto. `anular_pago` (migración 29)
 * no mira el estado de la liquidación, así que la anulación es válida.
 * @param {MouseEvent} e
 */
function handleLiquidacionHistorialPagoAnularClick(e) {
    const { id, pagoId } = e.currentTarget.dataset;
    if (!id || !pagoId) return;
    const row = (liquidacionHistorial?.liquidaciones ?? []).find((r) => r.id === id);
    if (!row) return;
    tableroDrawerTrigger = e.currentTarget;
    tableroDrawer = {
        tipo: 'pago',
        soloLectura: true,
        empleadoId: row.empleado_id,
        empleadoNombre: row.empleados?.nombre ?? '',
        liquidacionId: row.id,
        neto: row.neto ?? 0,
        fecha: hoyArtISO(),
        medio: 'efectivo',
        monto: '',
        nota: '',
        cargando: true,
        activos: [],
        anulados: [],
        pagadoCentavos: 0,
        saldoCentavos: 0,
        error: null,
        anulandoId: pagoId,
        motivo: '',
        anularError: null,
    };
    renderTableroDrawerMount();
    void refreshTableroPagosList();
}

/**
 * Trae los pagos (activos + anulados) de la liquidación del cajón vigente,
 * los parte en dos y deriva neto/pagado/saldo vía `estadoPago` — MISMO
 * `listPagos` de lectura RLS que el resto del panel, cero write. Guardada por
 * `tableroDrawerListSeq` + `mismoCajonTablero` (mismo par de guards que
 * `refreshTableroMovimientosList`), para que una respuesta tardía de un cajón
 * ya cerrado o reabierto para OTRA liquidación, o superada por una carga más
 * nueva, nunca lo repinte.
 *
 * Además de refrescar el cajón, repinta la pill de la fila EN LA TABLA
 * (`tableroCache.filas.get(pending.empleadoId).pill`) — spec "Payment
 * Changes Do Not Invalidate the Dashboard Cache": sólo esa pill cambia,
 * `previewLiquidacion` nunca se vuelve a llamar.
 */
async function refreshTableroPagosList() {
    const pending = tableroDrawer;
    if (!pending) return;
    const listSeq = ++tableroDrawerListSeq;
    const vigente = () => listSeq === tableroDrawerListSeq && mismoCajonTablero(tableroDrawer, pending) && !!getTableroDrawerMount();
    try {
        const rows = await loadPagos({ liquidacionIds: [pending.liquidacionId] });
        if (!vigente()) return;
        const activos = rows.filter((r) => !r.anulado_at);
        const anulados = rows.filter((r) => r.anulado_at);
        const resumen = estadoPago({ neto: pending.neto, pagos: activos });
        // CRITICAL review-a57c3fe2ce2f2297, finding 1: un refresh disparado
        // por un `incierto` limpia monto/nota en vez de pre-cargar el saldo —
        // un reintento debe ser una carga consciente, nunca repetir a ciegas
        // el monto anterior. También cierra el aviso bloqueante.
        const wasIncierto = tableroDrawer.incierto === true;
        // Money path: whatever the admin typed while this request was in
        // flight (first open, right after a payment, after an Anular) is
        // kept — the live form wins over the previous model. The saldo is
        // prefilled ONLY into an untouched monto: empty, or still exactly
        // the saldo this drawer prefilled last time (`montoPrefill`), so a
        // payment or a void moves an untouched monto along with the saldo.
        const borrador = wasIncierto ? null : leerBorradorPago(getTableroDrawerMount());
        const saldoPrefill = (resumen.saldoCentavos / 100).toFixed(2);
        const montoVivo = borrador?.monto ?? tableroDrawer.monto ?? '';
        // A monto the admin typed but the browser could not parse
        // (`montoIlegible`, see `leerBorradorForm`) is touched, never
        // prefilled over — and it stays that way while the field is still
        // empty, even though the re-rendered input is no longer `badInput`.
        const montoInvalido = !wasIncierto && (borrador?.montoIlegible === true || (tableroDrawer.montoIlegible === true && montoVivo === ''));
        const montoIntacto = !montoInvalido && (montoVivo === '' || montoVivo === tableroDrawer.montoPrefill);
        // After an `incierto`, any error left by a failed earlier refresh is
        // stale now that the list loaded (the manual "Actualizar lista" retry).
        const errorPrevio = wasIncierto || tableroDrawer.error === MONTO_FORMATO_INVALIDO_MSG ? null : tableroDrawer.error;
        tableroDrawer = {
            ...tableroDrawer,
            ...borrador,
            cargando: false,
            activos,
            anulados,
            pagadoCentavos: resumen.pagadoCentavos,
            saldoCentavos: resumen.saldoCentavos,
            monto: wasIncierto ? '' : montoIntacto ? saldoPrefill : montoVivo,
            montoPrefill: !wasIncierto && montoIntacto ? saldoPrefill : null,
            nota: wasIncierto ? '' : (borrador?.nota ?? tableroDrawer.nota),
            montoIlegible: montoInvalido,
            error: montoInvalido ? MONTO_FORMATO_INVALIDO_MSG : errorPrevio,
            incierto: false,
            mensaje: null,
        };
        renderTableroDrawerMount();

        // S8 guard: el cajón ahora también se abre desde una fila del
        // HISTORIAL (`handleLiquidacionHistorialPagoClick`), cuyo empleado
        // puede tener OTRA fila en `tableroCache` para el período vigente
        // (bucket distinto, u otra `liquidacionRow`) — comparar también
        // `liquidacionRow.id` evita pisar la pill de una fila que no es la
        // liquidación de este cajón.
        const fila = tableroCache?.filas?.get(pending.empleadoId);
        if (fila && fila.liquidacionRow?.id === pending.liquidacionId) {
            fila.pill = resumen.pill;
            renderTableroMount();
        }

        // S8: si esta liquidación también es una fila del HISTORIAL (pudo
        // abrirse desde ahí, o coincidir por casualidad), su pill se
        // refresca ahí también — mismo criterio "sólo esa pill cambia".
        if (liquidacionHistorialPagos && (liquidacionHistorial?.liquidaciones ?? []).some((r) => r.id === pending.liquidacionId)) {
            const proximaMap = new Map(liquidacionHistorialPagos);
            // L5: TODOS los pagos (activos y anulados) — la fila de una
            // liquidación anulada lista también los anulados; la pill
            // (`estadoPago`) ya suma sólo los activos.
            proximaMap.set(pending.liquidacionId, rows);
            liquidacionHistorialPagos = proximaMap;
            if (liquidacionHistorialPagosDesconocidos.has(pending.liquidacionId)) {
                liquidacionHistorialPagosDesconocidos = new Set(liquidacionHistorialPagosDesconocidos);
                liquidacionHistorialPagosDesconocidos.delete(pending.liquidacionId);
            }
            if (getLiquidacionMount()) renderLiquidacionMount();
        }
    } catch (err) {
        if (!vigente()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshTableroPagosList: no se pudo refrescar la lista de pagos', err);
        tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), cargando: false, error: LIQUIDACION_GENERIC_ERROR_MSG };
        renderTableroDrawerMount();
    }
}

/**
 * Alta de un pago parcial (spec `payroll-payments`, "Registering a Payment
 * Never Exceeds Neto"). A diferencia de `handleMovimientoCrearSubmit`, un
 * éxito NUNCA cierra el cajón ni dispara un recompute — sólo limpia el form
 * y refresca la lista (que a su vez repinta neto/pagado/saldo y la pill de
 * la fila). Un rechazo de negocio (p.ej. `monto_excede_neto`) se muestra
 * inline sin cerrar nada, para que el admin corrija el monto y reintente.
 * @param {SubmitEvent} e
 */
async function handleTableroPagoRegistrarSubmit(e) {
    e.preventDefault();
    if (tableroDrawerSubmitting) return;
    const pending = tableroDrawer;
    if (!pending) return;

    const form = e.currentTarget;
    const fecha = form.querySelector('[name="fecha"]')?.value ?? '';
    const medio = form.querySelector('[name="medio"]')?.value ?? '';
    const montoRaw = form.querySelector('[name="monto"]')?.value ?? '';
    const nota = form.querySelector('[name="nota"]')?.value ?? '';
    const monto = Number(montoRaw);
    if (!fecha || !['efectivo', 'transferencia'].includes(medio) || !Number.isFinite(monto) || monto <= 0) {
        tableroDrawer = { ...pending, fecha, medio, monto: montoRaw, nota, error: 'Completá fecha, medio y un monto válido.' };
        renderTableroDrawerMount();
        return;
    }

    // Mismo criterio que `handleMovimientoCrearSubmit` (review-5b6ea3ac37a82d85):
    // pre-incrementar UNA sola vez — nunca de nuevo dentro de las ramas de
    // abajo, o el guard `seq === tableroDrawerSeq` del `finally` deja de
    // matchear y `tableroDrawerSubmitting` queda pegado en `true`.
    const seq = ++tableroDrawerSeq;
    tableroDrawerSubmitting = true;
    tableroDrawer = { ...pending, fecha, medio, monto: montoRaw, nota, montoIlegible: false, error: null, submitting: true };
    renderTableroDrawerMount();
    try {
        const result = await registrarPago({ liquidacionId: pending.liquidacionId, medio, monto, fecha, nota: nota || null });
        if (seq !== tableroDrawerSeq || !getTableroDrawerMount()) return;

        if (!result.ok) {
            if (result.kind === 'auth') {
                tableroDrawer = null;
                renderTableroDrawerMount();
                showToast(result.mensaje, { level: 'error' });
                return;
            }
            if (result.kind === 'incierto') {
                // CRITICAL review-a57c3fe2ce2f2297, finding 1 + U1: un
                // resultado AMBIGUO (falla de transporte o un 5xx que no sea
                // `no_disponible`) nunca reabre el MISMO form — no sabemos si
                // el RPC corrió. El cajón pasa a un aviso bloqueante
                // (`PagoDrawerView.js`) hasta que `refreshTableroPagosList`
                // confirme el estado real; ese refresh se dispara YA, no
                // espera a que el admin clickee "Actualizar lista" (el botón
                // es el fallback manual, no el único disparador).
                tableroDrawer = { ...tableroDrawer, submitting: false, error: null, incierto: true, mensaje: result.mensaje };
                renderTableroDrawerMount();
                void refreshTableroPagosList();
                return;
            }
            // validacion | error: el cajón queda abierto con el mensaje YA
            // MAPEADO por el servicio (nunca un código crudo) — spec: "El
            // pago supera el neto de la liquidación.".
            tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), submitting: false, error: result.mensaje };
            renderTableroDrawerMount();
            return;
        }

        // Clear for the next payment, but only the fields the admin did NOT
        // touch while `registrarPago` was in flight: a live value that still
        // equals what was submitted resets (fecha to today ART, monto/nota
        // empty — `refreshTableroPagosList` then prefills the saldo into the
        // empty monto); anything typed meanwhile is kept as is. medio keeps
        // whatever the form shows (the last one used, or a new choice).
        const vivo = leerBorradorPago(getTableroDrawerMount()) ?? {};
        const tocado = (campo, enviado) => vivo[campo] !== undefined && vivo[campo] !== enviado;
        tableroDrawer = {
            ...tableroDrawer,
            ...vivo,
            fecha: tocado('fecha', fecha) ? vivo.fecha : hoyArtISO(),
            monto: tocado('monto', montoRaw) || vivo.montoIlegible ? vivo.monto : '',
            nota: tocado('nota', nota) ? vivo.nota : '',
            montoIlegible: vivo.montoIlegible === true,
            submitting: false,
            error: vivo.montoIlegible ? vivo.error : null,
        };
        renderTableroDrawerMount();
        showToast('Pago registrado.', { level: 'success' });
        void refreshTableroPagosList();
    } catch (err) {
        if (seq !== tableroDrawerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleTableroPagoRegistrarSubmit: no se pudo registrar el pago', err);
        if (getTableroDrawerMount()) {
            tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), submitting: false, error: LIQUIDACION_GENERIC_ERROR_MSG };
            renderTableroDrawerMount();
        }
    } finally {
        if (seq === tableroDrawerSeq) tableroDrawerSubmitting = false;
    }
}

/** Revela el form inline de motivo para anular ESE pago (nunca un `window.prompt`, mismo criterio que el resto del panel). @param {MouseEvent} e */
function handleTableroPagoAnularAbrir(e) {
    const { id } = e.currentTarget.dataset;
    if (!tableroDrawer || !id) return;
    tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), anulandoId: id, anularError: null, motivo: '' };
    renderTableroDrawerMount();
}

function handleTableroPagoAnularCancelar() {
    if (!tableroDrawer) return;
    tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), anulandoId: null, anularError: null, motivo: '' };
    renderTableroDrawerMount();
}

/**
 * Anula el pago cuyo form inline está abierto (spec `payroll-payments`,
 * "Voiding a Payment"). Éxito cierra el sub-form (nunca el cajón entero) y
 * refresca la lista — nunca dispara `recomputeTablero()` (un pago jamás
 * cambia el neto).
 * @param {SubmitEvent} e
 */
async function handleTableroPagoAnularConfirmarSubmit(e) {
    e.preventDefault();
    if (tableroDrawerSubmitting) return;
    const pending = tableroDrawer;
    if (!pending?.anulandoId) return;
    const form = e.currentTarget;
    const pagoId = form.dataset.id;
    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    if (!motivo.trim()) {
        tableroDrawer = { ...pending, ...leerBorradorPago(getTableroDrawerMount()), anularError: 'Ingresá un motivo.' };
        renderTableroDrawerMount();
        return;
    }

    // Mismo criterio que `handleMovimientoAnularConfirmarSubmit`.
    const seq = ++tableroDrawerSeq;
    tableroDrawerSubmitting = true;
    try {
        const result = await anularPago({ pagoId, motivo });
        if (seq !== tableroDrawerSeq || !getTableroDrawerMount()) return;
        // S2: same drawer target, not the same object — a list refresh that
        // lands meanwhile replaces the object and must not drop this result.
        if (!mismoCajonTablero(tableroDrawer, pending)) return;
        // C1: Abrir/Cancelar stay usable while this request is in flight, so
        // the Anular sub-form may now be closed or open on ANOTHER pago. The
        // list refresh and the toast still apply (same drawer), but the
        // sub-form state (`anularError`, closing it) only when it is still
        // the sub-form of the pago that was submitted.
        const mismoSubForm = tableroDrawer.anulandoId === pending.anulandoId;

        if (!result.ok) {
            if (result.kind === 'auth') {
                tableroDrawer = null;
                renderTableroDrawerMount();
                showToast(result.mensaje, { level: 'error' });
                return;
            }
            if (mismoSubForm) {
                tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), anularError: result.mensaje };
                renderTableroDrawerMount();
            } else {
                // F1: the sub-form moved on — never drop the failure silently.
                // L9a: an uncertain outcome is a warning (it may have been
                // applied), never an error.
                showToast(result.mensaje, { level: result.kind === 'incierto' ? 'warning' : 'error' });
            }
            // The anulación may have been applied: show the real list instead of a stale one.
            if (result.kind === 'incierto') void refreshTableroPagosList();
            return;
        }

        if (mismoSubForm) {
            tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), anulandoId: null, anularError: null, motivo: '' };
            renderTableroDrawerMount();
        }
        showToast('Pago anulado.', { level: 'success' });
        void refreshTableroPagosList();
    } catch (err) {
        if (seq !== tableroDrawerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleTableroPagoAnularConfirmarSubmit: no se pudo anular el pago', err);
        if (!getTableroDrawerMount() || !mismoCajonTablero(tableroDrawer, pending)) return;
        if (tableroDrawer.anulandoId === pending.anulandoId) {
            tableroDrawer = { ...tableroDrawer, ...leerBorradorPago(getTableroDrawerMount()), anularError: LIQUIDACION_GENERIC_ERROR_MSG };
            renderTableroDrawerMount();
        } else {
            // F1: the sub-form moved on — never drop the failure silently.
            showToast(LIQUIDACION_GENERIC_ERROR_MSG, { level: 'error' });
        }
    } finally {
        if (seq === tableroDrawerSeq) tableroDrawerSubmitting = false;
    }
}

// ---------------------------------------------------------------------------
// Cajón "N anomalías" (Unit S3, design.md AD7) — comparte DrawerShell.js /
// #admin-liquidacion-drawer-mount con el cajón "Adelanto" de S4 (design.md
// AD7: "Three drawers... one shared shell"). `tableroDrawer.tipo:'anomalias'`
// distingue cuál cuerpo renderiza `renderTableroDrawerMount`.
// ---------------------------------------------------------------------------

/**
 * Abre el cajón de anomalías para la fila clicada (spec "Inline Anomalies
 * Drawer"). Lee `empleadoId`/nombre de `tableroCache.filas` (ya cargado,
 * mismo criterio que `handleTableroAdelantoClick`) y dispara la carga async
 * de la lista de anomalías; el cajón muestra "Cargando..." hasta que
 * resuelve (mismo patrón que el cajón "Adelanto").
 * @param {MouseEvent} e
 */
function handleTableroAbrirAnomaliasClick(e) {
    const { id } = e.currentTarget.dataset;
    if (!id) return;
    tableroDrawerTrigger = e.currentTarget;
    const fila = tableroCache?.filas?.get(id);
    tableroDrawer = {
        tipo: 'anomalias',
        empleadoId: id,
        empleadoNombre: fila?.nombre ?? '',
        cargando: true,
        anomalias: [],
        error: null,
        corrigiendoId: null,
        corrigiendoEntrada: '',
        corrigiendoSalida: '',
        corrigiendoMotivo: '',
        corregirError: null,
    };
    renderTableroDrawerMount();
    void refreshTableroAnomaliasList();
}

/**
 * Trae las fichadas del período (MISMA ventana padeada que `recomputeTablero`,
 * AD3 — `loadFichadas` no filtra por empleado, así que se lee el mismo lote y
 * se filtra client-side vía `anomaliasDeEmpleado`) y las reduce a la lista de
 * anomalías del empleado del cajón vigente. Guardada por `tableroDrawerListSeq`
 * + `mismoCajonTablero`, mismo par de guards que
 * `refreshTableroMovimientosList`.
 */
async function refreshTableroAnomaliasList() {
    const pending = tableroDrawer;
    if (!pending) return;
    const rango = rangoDelPeriodo(tableroSeleccion);
    if (!rango) return;
    const listSeq = ++tableroDrawerListSeq;
    const vigente = () => listSeq === tableroDrawerListSeq && mismoCajonTablero(tableroDrawer, pending) && !!getTableroDrawerMount();
    try {
        const result = await loadFichadas(ventanaLecturaFichadas(rango, TABLERO_QUERY_PAD_DAYS));
        if (!vigente()) return;
        const anomalias = anomaliasDeEmpleado(result?.fichadas ?? [], pending.empleadoId, {
            now: new Date(),
            desde: rango.desde,
            hasta: rango.hasta,
        });
        tableroDrawer = { ...tableroDrawer, cargando: false, anomalias };
        renderTableroDrawerMount();
    } catch (err) {
        if (!vigente()) return;
        window.Sentry?.captureException(err);
        tableroDrawer = { ...tableroDrawer, cargando: false, error: LIQUIDACION_GENERIC_ERROR_MSG };
        renderTableroDrawerMount();
    }
}

/**
 * Revela el form inline de corrección de ESA fichada (nunca un
 * `window.prompt`, mismo criterio que "Anular" en el cajón "Adelanto").
 * review-bd6afa990ae9d874 (S3 correction, finding 1b): no-op mientras OTRA
 * card está `submitting` — sin este guard, abrir la card B mientras la card
 * A todavía está en vuelo pisaba `tableroDrawer` (nueva identidad) y el
 * `then`/`catch` tardío de A terminaba mutando el estado de B. La vista
 * (`AnomaliasDrawerView.js`) además deshabilita el botón trigger de las
 * OTRAS cards mientras `model.submitting` — defensa en profundidad, este
 * guard es el que realmente para el click.
 * @param {MouseEvent} e
 */
function handleAnomaliaAbrirClick(e) {
    if (tableroDrawerSubmitting) return;
    const { id } = e.currentTarget.dataset;
    if (!tableroDrawer || !id) return;
    const anomalia = (tableroDrawer.anomalias ?? []).find((a) => a.fichadaId === id);
    tableroDrawer = {
        ...tableroDrawer,
        corrigiendoId: id,
        corrigiendoEntrada: toLocalDatetimeInputValue(anomalia?.entrada_at),
        corrigiendoSalida: toLocalDatetimeInputValue(anomalia?.salida_at),
        corrigiendoMotivo: '',
        corregirError: null,
    };
    renderTableroDrawerMount();
}

/**
 * La fichada de una card del cajón de anomalías tal como la muestra (F-E):
 * empleado del cajón + horarios de la anomalía, para la guarda de día
 * liquidado de `admin-fichadas.service.js`.
 * @param {{ empleadoId: string, empleadoNombre?: string, anomalias?: Array<{ fichadaId: string, entrada_at: string|null, salida_at: string|null }> }} cajon
 * @param {string} fichadaId
 */
function filaActualDelCajon(cajon, fichadaId) {
    const anomalia = (cajon.anomalias ?? []).find((a) => a.fichadaId === fichadaId);
    return {
        empleado_id: cajon.empleadoId,
        empleadoNombre: cajon.empleadoNombre,
        entrada_at: anomalia?.entrada_at ?? null,
        salida_at: anomalia?.salida_at ?? null,
    };
}

function handleAnomaliaCancelar() {
    if (!tableroDrawer) return;
    // review-53a3c0de0000a001: cancelar mientras ESA tarjeta guarda crearía un
    // objeto nuevo con submitting:true y la respuesta en vuelo se descartaría
    // por la guarda de identidad, dejando las otras tarjetas deshabilitadas.
    if (tableroDrawerSubmitting) return;
    tableroDrawer = { ...tableroDrawer, corrigiendoId: null, corregirError: null };
    renderTableroDrawerMount();
}

/**
 * Cierra la jornada abierta de la anomalía `ABIERTA` en edición (spec
 * `admin-fichadas.service.js#cerrarFichada`). Un éxito saca esa card de la
 * lista y — si era la única — cierra el cajón entero; en cualquier caso
 * dispara un recompute completo (design.md AD5: "Fichada... closed...  ->
 * Full recompute"), igual que el cajón "Adelanto" lo hace para sus propias
 * mutaciones. Un rechazo de negocio (p.ej. `jornada_ya_cerrada`) se muestra
 * inline en la card, sin cerrar nada, y los valores tipeados se preservan.
 * @param {SubmitEvent} e
 */
async function handleAnomaliaCerrarSubmit(e) {
    e.preventDefault();
    if (tableroDrawerSubmitting) return;
    const pending = tableroDrawer;
    if (!pending?.corrigiendoId) return;

    const form = e.currentTarget;
    const fichadaId = form.dataset.id;
    const salidaRaw = form.querySelector('[name="salida_at"]')?.value ?? '';
    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    if (!motivo.trim()) {
        tableroDrawer = { ...pending, corrigiendoSalida: salidaRaw, corrigiendoMotivo: motivo, corregirError: 'Ingresá un motivo.' };
        renderTableroDrawerMount();
        return;
    }

    // Mismo criterio review-5b6ea3ac37a82d85 (S4): pre-incrementar UNA vez,
    // nunca de nuevo dentro de las ramas de abajo.
    const seq = ++tableroDrawerSeq;
    tableroDrawerSubmitting = true;
    tableroDrawer = { ...pending, corrigiendoSalida: salidaRaw, corrigiendoMotivo: motivo, corregirError: null, submitting: true };
    // review-bd6afa990ae9d874 (S3 correction, finding 1a): identity captured
    // AFTER the submitting-state write (this handler, unlike
    // `handleMovimientoAnularConfirmarSubmit`, mutates `tableroDrawer` BEFORE
    // its await) — same guard purpose as that sibling's `tableroDrawer ===
    // pending` check, adapted so it doesn't misfire on the pre-await write.
    const submittingState = tableroDrawer;
    renderTableroDrawerMount();
    try {
        const result = await cerrarFichada({
            fichada_id: fichadaId,
            salida_at: localDatetimeToIso(salidaRaw),
            motivo,
            actual: filaActualDelCajon(pending, fichadaId),
        });
        if (seq !== tableroDrawerSeq || tableroDrawer !== submittingState || !getTableroDrawerMount()) return;

        if (!result.ok) {
            tableroDrawer = { ...tableroDrawer, submitting: false, corregirError: result.error };
            renderTableroDrawerMount();
            return;
        }

        const restantes = (tableroDrawer.anomalias ?? []).filter((a) => a.fichadaId !== fichadaId);
        tableroDrawer =
            restantes.length === 0
                ? null
                : { ...tableroDrawer, anomalias: restantes, corrigiendoId: null, corregirError: null, submitting: false };
        renderTableroDrawerMount();
        showToast('Fichada corregida.', { level: 'success' });
        void recomputeTablero();
        void refreshJornadasAbiertasBanner(); // F-B: la corrección puede cerrar una jornada del banner
    } catch (err) {
        if (seq !== tableroDrawerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleAnomaliaCerrarSubmit: no se pudo cerrar la jornada', err);
        if (getTableroDrawerMount() && tableroDrawer === submittingState) {
            tableroDrawer = { ...tableroDrawer, submitting: false, corregirError: LIQUIDACION_GENERIC_ERROR_MSG };
            renderTableroDrawerMount();
        }
    } finally {
        if (seq === tableroDrawerSeq) tableroDrawerSubmitting = false;
    }
}

/**
 * Corrige `entrada_at`/`salida_at` de la anomalía `DURACION_EXCESIVA`/
 * `INVERTIDA` en edición (`admin-fichadas.service.js#editarFichada`). Mismo
 * criterio de éxito/rechazo/recompute que {@link handleAnomaliaCerrarSubmit}.
 * @param {SubmitEvent} e
 */
async function handleAnomaliaCorregirSubmit(e) {
    e.preventDefault();
    if (tableroDrawerSubmitting) return;
    const pending = tableroDrawer;
    if (!pending?.corrigiendoId) return;

    const form = e.currentTarget;
    const fichadaId = form.dataset.id;
    const entradaRaw = form.querySelector('[name="entrada_at"]')?.value ?? '';
    const salidaRaw = form.querySelector('[name="salida_at"]')?.value ?? '';
    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    if (!motivo.trim()) {
        tableroDrawer = {
            ...pending,
            corrigiendoEntrada: entradaRaw,
            corrigiendoSalida: salidaRaw,
            corrigiendoMotivo: motivo,
            corregirError: 'Ingresá un motivo.',
        };
        renderTableroDrawerMount();
        return;
    }

    const seq = ++tableroDrawerSeq;
    tableroDrawerSubmitting = true;
    tableroDrawer = {
        ...pending,
        corrigiendoEntrada: entradaRaw,
        corrigiendoSalida: salidaRaw,
        corrigiendoMotivo: motivo,
        corregirError: null,
        submitting: true,
    };
    // review-bd6afa990ae9d874 (S3 correction, finding 1a): see the identical
    // comment in `handleAnomaliaCerrarSubmit` just above.
    const submittingState = tableroDrawer;
    renderTableroDrawerMount();
    try {
        const result = await editarFichada({
            fichada_id: fichadaId,
            entrada_at: localDatetimeToIso(entradaRaw),
            salida_at: localDatetimeToIso(salidaRaw),
            motivo,
            actual: filaActualDelCajon(pending, fichadaId),
        });
        if (seq !== tableroDrawerSeq || tableroDrawer !== submittingState || !getTableroDrawerMount()) return;

        if (!result.ok) {
            tableroDrawer = { ...tableroDrawer, submitting: false, corregirError: result.error };
            renderTableroDrawerMount();
            return;
        }

        const restantes = (tableroDrawer.anomalias ?? []).filter((a) => a.fichadaId !== fichadaId);
        tableroDrawer =
            restantes.length === 0
                ? null
                : { ...tableroDrawer, anomalias: restantes, corrigiendoId: null, corregirError: null, submitting: false };
        renderTableroDrawerMount();
        showToast('Fichada corregida.', { level: 'success' });
        void recomputeTablero();
        void refreshJornadasAbiertasBanner(); // F-B: la corrección puede cerrar una jornada del banner
    } catch (err) {
        if (seq !== tableroDrawerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleAnomaliaCorregirSubmit: no se pudo corregir la fichada', err);
        if (getTableroDrawerMount() && tableroDrawer === submittingState) {
            tableroDrawer = { ...tableroDrawer, submitting: false, corregirError: LIQUIDACION_GENERIC_ERROR_MSG };
            renderTableroDrawerMount();
        }
    } finally {
        if (seq === tableroDrawerSeq) tableroDrawerSubmitting = false;
    }
}

// ---------------------------------------------------------------------------
// Cajón "Configuración" (Unit S6, design.md AD5/AD7/AD17, spec
// `payroll-holidays-config`) — cuarto `tableroDrawer.tipo`, comparte
// `DrawerShell.js`/`#admin-liquidacion-drawer-mount` con Adelanto (S4) y
// Anomalías (S3). Feriados: browser-directo vía `admin-feriados.service.js`
// (AD8 — la ÚNICA escritura de este slice que NO pasa por
// `api/payroll-movimientos.js`). Tope: reusa `renderCapEditor`/
// `refreshLiquidacionCapConfig`/`handleLiquidacionCapSaveSubmit`, sin
// duplicar. Período por defecto: `savePeriodoPorDefecto` (ya expuesto desde
// S4).
// ---------------------------------------------------------------------------

/**
 * AD5 — invalidación SIN recompute inmediato: agregar/quitar un feriado
 * cambia el multiplicador ×2 que aplica el PRÓXIMO `preview`, así que toda
 * cifra cacheada queda potencialmente vieja — pero el spec ("Feriado Effect
 * on the Next Recompute") exige explícitamente que esto NO fuerce un
 * recompute inmediato. Sólo marca `stale`; el próximo cambio de período o
 * click en "Actualizar" (`recomputeTablero`) escribe un `tableroCache`
 * NUEVO sin este flag, limpiándolo por construcción.
 */
function markTableroCacheStale() {
    if (!tableroCache) {
        // Un feriado agregado/quitado ANTES de que el primer `recomputeTablero`
        // termine de escribir `tableroCache` (todavía `null`) no debe perder
        // el aviso "· desactualizadas" — PERO sólo si ese `recomputeTablero`
        // sigue siendo del MISMO período (finding 2, review-130d19515f9f2340):
        // se guarda el rango vigente ahora, `recomputeTablero` lo descarta si
        // el rango de la cache que escribe no coincide.
        tableroCacheStalePendiente = tableroSeleccion ? rangoDelPeriodo(tableroSeleccion) : null;
        return;
    }
    tableroCache = { ...tableroCache, stale: true };
}

/**
 * Abre el área de Configuración. El mes del calendario se resuelve al mes
 * del período vigente del tablero la PRIMERA vez que se abre en esta
 * visita; reaperturas posteriores conservan el mes que el admin haya
 * navegado.
 */
function handleTableroConfigAbrirClick(e) {
    tableroDrawerTrigger = e?.currentTarget ?? null;
    tableroDrawer = { tipo: 'configuracion' };
    if (!tableroConfigMes) {
        const rango = tableroCache ?? rangoDelPeriodo(tableroSeleccion);
        tableroConfigMes = (rango?.hasta ?? todayLocalISO()).slice(0, 7);
    }
    // S3 (acceptance run): the reload starts BEFORE the first render.
    // `refreshTableroFeriados` sets `cargando: true` synchronously (before its
    // first await), so this render already shows "Cargando feriados..." with
    // the days disabled. Rendering first showed enabled-looking days whose
    // clicks were silently ignored (the handlers return early on `cargando`).
    void refreshTableroFeriados();
    renderTableroDrawerMount();
}

/** Carga (o recarga) la lista completa de feriados. */
async function refreshTableroFeriados() {
    const seq = ++tableroFeriadosSeq;
    tableroFeriados = { ...tableroFeriados, cargando: true, error: null };
    const result = await loadFeriados();
    if (seq !== tableroFeriadosSeq || !getTableroDrawerMount()) return;
    tableroFeriados = result.ok
        ? { cargando: false, error: null, lista: result.feriados }
        : { cargando: false, error: result.mensaje, lista: tableroFeriados.lista };
    renderTableroDrawerMount();
}

function handleTableroConfigMesAnteriorClick() {
    const prev = feriadoMesAnterior(tableroConfigMes);
    if (!prev) return;
    tableroConfigMes = prev;
    tableroFeriadoDraft = null;
    renderTableroDrawerMount();
}

function handleTableroConfigMesSiguienteClick() {
    const next = feriadoMesSiguiente(tableroConfigMes);
    if (!next) return;
    tableroConfigMes = next;
    tableroFeriadoDraft = null;
    renderTableroDrawerMount();
}

/**
 * Abre el form inline "Descripción del feriado" sobre el día clicado (spec
 * "Adding a feriado"). review-1fa036d7d40a06c8 (finding 1): también bloqueada
 * mientras `tableroFeriados.cargando` — la carga inicial todavía no volvió.
 * @param {MouseEvent} e
 */
function handleTableroFeriadoAbrirClick(e) {
    if (tableroFeriadoSubmitting || tableroFeriados.cargando) return;
    const fecha = e.currentTarget.dataset.fecha;
    if (!fecha) return;
    tableroFeriadoDraft = { fecha, descripcion: '', error: null };
    renderTableroDrawerMount();
}

function handleTableroFeriadoCancelarClick() {
    if (tableroFeriadoSubmitting) return;
    tableroFeriadoDraft = null;
    renderTableroDrawerMount();
}

/**
 * Guarda lo tipeado en el input de descripción ANTES de un repintado —
 * MISMA TRAMPA que `captureTableroPersonalizadoDraft`/`captureLiquidacionCapDraft`:
 * un repintado del cajón disparado por OTRA acción (p.ej. la lista de
 * feriados terminando de cargar) nunca debe borrar lo que el admin ya
 * escribió acá.
 * @param {HTMLElement} mount
 */
function captureTableroFeriadoDraft(mount) {
    if (!tableroFeriadoDraft) return;
    const form = mount.querySelector('form[data-action-form="config-feriado-agregar"]');
    if (!form) return;
    tableroFeriadoDraft = { ...tableroFeriadoDraft, descripcion: form.querySelector('[name="descripcion"]')?.value ?? tableroFeriadoDraft.descripcion };
}

/** @param {HTMLElement} mount */
function applyTableroFeriadoDraft(mount) {
    if (!tableroFeriadoDraft) return;
    const form = mount.querySelector('form[data-action-form="config-feriado-agregar"]');
    if (!form) return;
    const input = form.querySelector('[name="descripcion"]');
    if (input) input.value = tableroFeriadoDraft.descripcion ?? '';
}

/** Mismo patrón capture/apply, para el `<select>` "Período por defecto" (sobrevive a un repintado con una elección todavía sin "Guardar"). @param {HTMLElement} mount */
function captureTableroPeriodoPorDefectoDraft(mount) {
    const select = mount.querySelector('select[name="periodo_por_defecto"]');
    if (!select) return;
    tableroPeriodoPorDefectoDraft = select.value;
}

/** @param {HTMLElement} mount */
function applyTableroPeriodoPorDefectoDraft(mount) {
    if (tableroPeriodoPorDefectoDraft === null) return;
    const select = mount.querySelector('select[name="periodo_por_defecto"]');
    if (select) select.value = tableroPeriodoPorDefectoDraft;
}

/**
 * Alta de un feriado (spec "Adding a feriado"). Éxito NUNCA dispara un
 * recompute (AD5) — sólo marca `tableroCache` `stale` y repinta el tablero
 * de fondo para que el hint "· desactualizadas" aparezca de inmediato.
 *
 * review-1fa036d7d40a06c8: (finding 1) bloqueada mientras `tableroFeriados.cargando`
 * — sin esto, un alta que resuelve antes que la carga inicial pisaba
 * `tableroFeriadosSeq` y hacía que `refreshTableroFeriados` descartara su
 * propia lista, borrando los feriados preexistentes. (finding 2) el clear de
 * `tableroFeriadoSubmitting` vive en un `finally` guardado por `seq`, para
 * que resolver un alta VIEJA (sesión remontada) no libere una NUEVA en vuelo.
 * @param {SubmitEvent} e
 */
async function handleTableroFeriadoAgregarSubmit(e) {
    e.preventDefault();
    if (tableroFeriadoSubmitting || tableroFeriados.cargando) return;
    const pending = tableroFeriadoDraft;
    if (!pending) return;

    const form = e.currentTarget;
    const descripcion = form.querySelector('[name="descripcion"]')?.value ?? '';
    if (!descripcion.trim()) {
        tableroFeriadoDraft = { ...pending, descripcion, error: 'Ingresá una descripción para el feriado.' };
        renderTableroDrawerMount();
        return;
    }

    const seq = ++tableroFeriadosSeq;
    tableroFeriadoSubmitting = true;
    tableroFeriadoDraft = { ...pending, descripcion, error: null };
    renderTableroDrawerMount();
    try {
        const result = await crearFeriado({ fecha: pending.fecha, descripcion });
        if (seq !== tableroFeriadosSeq || !getTableroDrawerMount()) return;

        if (!result.ok) {
            tableroFeriadoDraft = { ...pending, descripcion, error: result.mensaje };
            renderTableroDrawerMount();
            return;
        }

        tableroFeriadoDraft = null;
        tableroFeriados = { ...tableroFeriados, error: null, lista: [...tableroFeriados.lista, result.feriado] };
        markTableroCacheStale();
        renderTableroDrawerMount();
        renderTableroMount();
        showToast('Feriado agregado.', { level: 'success' });
    } finally {
        if (seq === tableroFeriadosSeq) tableroFeriadoSubmitting = false;
    }
}

/**
 * Baja de un feriado (spec "Removing a feriado"). Mismo criterio de
 * invalidación que el alta (AD5): nunca recomputa, sólo marca `stale`.
 * review-1fa036d7d40a06c8: mismos dos fixes que {@link handleTableroFeriadoAgregarSubmit}
 * (bloqueo por `cargando` + clears en `finally` guardado por `seq`).
 * @param {MouseEvent} e
 */
async function handleTableroFeriadoQuitarClick(e) {
    if (tableroFeriadoSubmitting || tableroFeriados.cargando) return;
    const fecha = e.currentTarget.dataset.fecha;
    if (!fecha) return;

    const seq = ++tableroFeriadosSeq;
    tableroFeriadoSubmitting = true;
    tableroFeriadoQuitandoFecha = fecha;
    renderTableroDrawerMount();
    try {
        const result = await eliminarFeriado(fecha);
        if (seq !== tableroFeriadosSeq || !getTableroDrawerMount()) return;

        if (!result.ok) {
            // Inline (no sólo un toast, que vive fuera de este cajón) — mismo
            // slot `feriadosError` que una falla de CARGA de la lista, la
            // vista lo renderiza igual como banner rojo sobre el calendario.
            tableroFeriados = { ...tableroFeriados, error: result.mensaje };
            renderTableroDrawerMount();
            return;
        }

        tableroFeriados = { ...tableroFeriados, error: null, lista: tableroFeriados.lista.filter((f) => f.fecha !== fecha) };
        markTableroCacheStale();
        renderTableroDrawerMount();
        renderTableroMount();
        showToast('Feriado eliminado.', { level: 'success' });
    } finally {
        if (seq === tableroFeriadosSeq) {
            tableroFeriadoSubmitting = false;
            tableroFeriadoQuitandoFecha = null;
        }
    }
}

/**
 * Guarda "Período por defecto" (spec `payroll-holidays-config`, design.md
 * AD17) — vía el MISMO servicio que S4 ya expone (`savePeriodoPorDefecto`),
 * nunca duplicado acá. Éxito NO dispara un recompute: sólo cambia lo que se
 * preselecciona en la PRÓXIMA visita (`initTablero`), el período YA elegido
 * en esta visita no se toca.
 * review-1fa036d7d40a06c8 (finding 3): el único guard post-await era
 * `getTableroDrawerMount()`, que sigue devolviendo el mismo `<div>` tras un
 * remonte completo del panel — un remonte a mitad de un guardado dejaba que
 * la respuesta VIEJA pisara `tableroPeriodoPorDefectoActual` y disparara un
 * toast sobre una sesión ya abandonada. `pending`/`seq` are captured BEFORE
 * the await. Unlike the adelanto/pago/anomalías handlers, this one keeps the
 * object-identity check (`tableroDrawer !== pending`) instead of
 * `mismoCajonTablero`, on purpose: the Configuración drawer object
 * (`{ tipo: 'configuracion' }`) is created once on open and never replaced
 * while it stays open (its drafts live in their own variables, not in
 * `tableroDrawer`), and closing it is blocked while this save is in flight,
 * so a different object can only mean a remount or a reopen, whose response
 * must be dropped.
 * @param {SubmitEvent} e
 */
async function handleTableroPeriodoPorDefectoSubmit(e) {
    e.preventDefault();
    if (tableroPeriodoPorDefectoSaving) return;
    const form = e.currentTarget;
    const value = form.querySelector('select[name="periodo_por_defecto"]')?.value ?? '';
    if (!PERIODO_KINDS.includes(value)) {
        tableroPeriodoPorDefectoError = 'Elegí un período válido.';
        renderTableroDrawerMount();
        return;
    }

    const pending = tableroDrawer;
    const seq = ++tableroDrawerSeq;
    tableroPeriodoPorDefectoSaving = true;
    tableroPeriodoPorDefectoError = null;
    renderTableroDrawerMount();
    try {
        const session = await getCurrentSession();
        const result = await savePeriodoPorDefecto({ periodoPorDefecto: value, updatedBy: session?.user?.id ?? null });
        if (seq !== tableroDrawerSeq || tableroDrawer !== pending || !getTableroDrawerMount()) return;
        if (!result.ok) {
            tableroPeriodoPorDefectoError = result.error;
            return;
        }
        tableroPeriodoPorDefectoActual = result.periodoPorDefecto ?? null;
        tableroPeriodoPorDefectoDraft = null;
        showToast('Período por defecto guardado.', { level: 'success' });
    } catch (err) {
        window.Sentry?.captureException(err);
        if (seq !== tableroDrawerSeq || tableroDrawer !== pending || !getTableroDrawerMount()) return;
        tableroPeriodoPorDefectoError = TABLERO_CONFIG_GENERIC_ERROR_MSG;
    } finally {
        if (seq === tableroDrawerSeq && tableroDrawer === pending) {
            tableroPeriodoPorDefectoSaving = false;
            if (getTableroDrawerMount()) renderTableroDrawerMount();
        }
    }
}

// Bug reportado por el owner (2026-09-05): los 31 botones de día del
// calendario de feriados comparten `data-action="config-feriado-abrir"`
// (y las tarjetas de anomalías comparten `data-action` entre sí por
// `data-id`) — sin un discriminante, `restaurarFocoDrawerRepintado` caía
// sobre el PRIMER match en vez del elemento realmente enfocado (día 8
// clickeado -> foco/borde verde reaparecía en el día 1). Estos son los
// atributos `data-*` que ya se usan para distinguir elementos con el mismo
// `data-action` dentro de un cajón (ver `ConfiguracionLiquidacionView.js`,
// `AnomaliasDrawerView.js`, `AdelantoDrawerView.js`, `PagoDrawerView.js`).
const DRAWER_FOCUS_DISCRIMINANTES = ['fecha', 'id'];

/**
 * review-130d19515f9f2340 (finding 1): captura foco + selección de texto de
 * un elemento DENTRO del cajón antes de un repintado que reconstruye el HTML
 * vía `innerHTML` — el nodo en sí no sobrevive, así que se identifica por
 * `name` (inputs de un form) o `data-action` (botones) para reencontrar su
 * equivalente después. Cuando varios elementos comparten ese `name`/
 * `data-action` (p.ej. los botones de día del calendario) se suma al
 * selector el primer atributo discriminante presente
 * (`DRAWER_FOCUS_DISCRIMINANTES`, p.ej. `data-fecha`/`data-id`); si no hay
 * ninguno y el `name`/`data-action` no es único dentro del cajón, la captura
 * se descarta (cae al fallback del botón de cerrar) en vez de arriesgarse a
 * reencontrar el elemento equivocado. Devuelve `null` si no hay foco dentro
 * del cajón o el elemento enfocado no es identificable así.
 * @param {HTMLElement} mount
 */
function capturarFocoDrawerRepintado(mount) {
    const el = document.activeElement;
    if (!el || !mount.contains(el)) return null;
    const name = el.getAttribute?.('name');
    const dataAction = el.dataset?.action;
    if (!name && !dataAction) return null;
    const base = name ? `[name="${name}"]` : `[data-action="${dataAction}"]`;
    const discriminante = DRAWER_FOCUS_DISCRIMINANTES.map((attr) => el.dataset?.[attr] && `[data-${attr}="${el.dataset[attr]}"]`).find(Boolean);
    if (!discriminante && mount.querySelectorAll(base).length > 1) return null;
    return {
        selector: discriminante ? `${base}${discriminante}` : base,
        selectionStart: typeof el.selectionStart === 'number' ? el.selectionStart : null,
        selectionEnd: typeof el.selectionEnd === 'number' ? el.selectionEnd : null,
    };
}

/**
 * Aplica lo capturado por {@link capturarFocoDrawerRepintado} sobre el HTML
 * recién reconstruido. Devuelve `true` si encontró y enfocó el equivalente.
 * @param {HTMLElement} mount
 * @param {{selector:string, selectionStart:number|null, selectionEnd:number|null}|null} captura
 */
function restaurarFocoDrawerRepintado(mount, captura) {
    if (!captura) return false;
    const el = mount.querySelector(captura.selector);
    if (!el) return false;
    el.focus();
    if (captura.selectionStart !== null && typeof el.setSelectionRange === 'function') {
        el.setSelectionRange(captura.selectionStart, captura.selectionEnd);
    }
    return true;
}

/**
 * Renderiza (o limpia) el mount SEPARADO del cajón compartido (design.md
 * AD7) — mismo criterio que `renderLiquidacionAnularMount`: abrir/cerrar,
 * o repintar con un error inline, nunca toca el mount del tablero. El
 * Escape se cablea sobre el `[role="dialog"]` recién creado, no sobre
 * `document` (así no se acumulan listeners entre aperturas sucesivas).
 * Despacha entre `AdelantoDrawerView`/`AnomaliasDrawerView`/
 * `ConfiguracionLiquidacionView` según `tableroDrawer.tipo` (AD7: "one
 * shared shell").
 */
function renderTableroDrawerMount() {
    const mount = getTableroDrawerMount();
    if (!mount) return;

    // S6: captura los borradores en vuelo ANTES de sobrescribir el HTML —
    // MISMA TRAMPA que `captureTableroPersonalizadoDraft`/`captureLiquidacionCapDraft`.
    if (tableroDrawer?.tipo === 'configuracion') {
        captureLiquidacionCapDraft(mount);
        captureTableroFeriadoDraft(mount);
        captureTableroPeriodoPorDefectoDraft(mount);
    }

    // C2: same trap for the Anular motivo of the adelanto/pago drawers, which
    // lives only in the DOM until it is sent. Unlike the alta/pago form (see
    // `leerBorradorForm`), no caller has to decide which fields a success
    // clears: the motivo is carried into the model only while this render
    // keeps the sub-form open for the SAME item; Abrir, Cancelar and a
    // successful Anular reset `motivo` to ''.
    if (cajonMovimiento(tableroDrawer?.tipo) || tableroDrawer?.tipo === 'pago') {
        const formAnularAction = cajonMovimiento(tableroDrawer.tipo)?.formAnular ?? 'pago-anular-confirmar';
        const formAnular = mount.querySelector(`form[data-action-form="${formAnularAction}"]`);
        const motivoVivo = formAnular?.querySelector('[name="motivo"]');
        if (motivoVivo && tableroDrawer.anulandoId && formAnular.dataset.id === tableroDrawer.anulandoId) {
            tableroDrawer = { ...tableroDrawer, motivo: motivoVivo.value };
        }
    }

    if (!tableroDrawer) {
        mount.innerHTML = '';
        tableroDrawerRenderedTipo = null;
        // Diferido un microtask: un cierre programático tras un éxito (p.ej.
        // `handleMovimientoCrearSubmit`) dispara `void recomputeTablero()`
        // JUSTO después de este render — su prefijo síncrono (`renderTableroMount()`)
        // puede destruir el trigger ANTES de que este microtask corra, así
        // que para ese momento `document.contains(trigger)` ya refleja la
        // verdad y el fallback al mount del tablero aplica correctamente.
        queueMicrotask(restoreTableroDrawerFocus);
        return;
    }

    const esAnomalias = tableroDrawer.tipo === 'anomalias';
    const esConfiguracion = tableroDrawer.tipo === 'configuracion';
    const esPago = tableroDrawer.tipo === 'pago';
    const titulo = esAnomalias
        ? tableroDrawer.cargando
            ? tableroDrawer.empleadoNombre
            : `${tableroDrawer.empleadoNombre} · ${tableroDrawer.anomalias.length} anomalías`
        : esConfiguracion
          ? 'Configuración'
          : esPago
            ? tableroDrawer.soloLectura
                ? 'Pagos de la liquidación anulada'
                : 'Registrar pago'
            : (cajonMovimiento(tableroDrawer.tipo)?.titulo ?? 'Adelanto');
    const cuerpo = esAnomalias
        ? AnomaliasDrawerView(tableroDrawer)
        : esConfiguracion
          ? ConfiguracionLiquidacionView({
                mes: tableroConfigMes,
                feriadosCargando: tableroFeriados.cargando,
                feriadosError: tableroFeriados.error,
                feriados: tableroFeriados.lista,
                feriadoDraft: tableroFeriadoDraft,
                feriadoSubmitting: tableroFeriadoSubmitting,
                quitandoFecha: tableroFeriadoQuitandoFecha,
                capConfig: liquidacionCapConfig
                    ? { ...liquidacionCapConfig, saving: liquidacionCapSaveInFlight, draft: liquidacionCapDraft }
                    : null,
                periodoPorDefecto: tableroPeriodoPorDefectoDraft ?? tableroPeriodoPorDefectoActual ?? 'mes',
                periodoPorDefectoSaving: tableroPeriodoPorDefectoSaving,
                periodoPorDefectoError: tableroPeriodoPorDefectoError,
            })
          : esPago
            ? PagoDrawerView(tableroDrawer)
            : (cajonMovimiento(tableroDrawer.tipo)?.vista ?? AdelantoDrawerView)(tableroDrawer);
    // review-130d19515f9f2340 (finding 1): un repintado del MISMO cajón ya
    // abierto (mismo `tipo` que el último render) nunca debe robarle el foco
    // a lo que el admin esté editando — se captura ANTES de que `innerHTML`
    // destruya el nodo enfocado, para restaurarlo después. Una apertura o un
    // cambio de tipo sigue atrapando el foco en el botón de cerrar, igual que antes.
    const esRepintado = tableroDrawerRenderedTipo === tableroDrawer.tipo;
    const focoCapturado = esRepintado ? capturarFocoDrawerRepintado(mount) : null;
    mount.innerHTML = DrawerShell({ id: 'tablero-drawer', titulo, cuerpo });
    tableroDrawerRenderedTipo = tableroDrawer.tipo;

    const dialogEl = mount.querySelector('[role="dialog"]');
    if (dialogEl) dialogEl.addEventListener('keydown', handleTableroDrawerKeydown);
    const overlay = mount.querySelector('[data-action="drawer-overlay"]');
    if (overlay) overlay.addEventListener('click', handleTableroDrawerCerrar);
    const closeBtn = mount.querySelector('[data-action="drawer-cerrar"]');
    if (closeBtn) closeBtn.addEventListener('click', handleTableroDrawerCerrar);

    // Foco DENTRO del cajón (AD10, mismo criterio que `openLiquidacionComprobante`):
    // `mount.innerHTML` de arriba acaba de destruir el nodo enfocado anterior
    // (si había uno) — `aria-modal="true"` de `DrawerShell` promete un cajón
    // modal, así que CADA render mientras está abierto (primero o repintado
    // por una carga async/error/draft) lo re-atrapa adentro; nunca deja el
    // foco caer a `<body>`. Un repintado restaura el equivalente capturado
    // arriba cuando existe; si no, cae al mismo fallback de siempre.
    if (dialogEl && !dialogEl.contains(document.activeElement)) {
        if (!restaurarFocoDrawerRepintado(mount, focoCapturado)) closeBtn?.focus();
    }

    if (esAnomalias) {
        for (const btn of mount.querySelectorAll('[data-action="tablero-anomalia-cerrar"], [data-action="tablero-anomalia-corregir"]')) {
            btn.addEventListener('click', handleAnomaliaAbrirClick);
        }
        const cancelarBtn = mount.querySelector('[data-action="tablero-anomalia-cancelar"]');
        if (cancelarBtn) cancelarBtn.addEventListener('click', handleAnomaliaCancelar);
        const cerrarForm = mount.querySelector('form[data-action-form="tablero-anomalia-cerrar-confirmar"]');
        if (cerrarForm) cerrarForm.addEventListener('submit', handleAnomaliaCerrarSubmit);
        const corregirForm = mount.querySelector('form[data-action-form="tablero-anomalia-corregir-confirmar"]');
        if (corregirForm) corregirForm.addEventListener('submit', handleAnomaliaCorregirSubmit);
        return;
    }

    if (esConfiguracion) {
        applyLiquidacionCapDraft(mount);
        applyTableroFeriadoDraft(mount);
        applyTableroPeriodoPorDefectoDraft(mount);
        const capForm = mount.querySelector('form[data-action-form="payroll-config-tope"]');
        if (capForm) capForm.addEventListener('submit', handleLiquidacionCapSaveSubmit);
        const mesAnteriorBtn = mount.querySelector('[data-action="config-mes-anterior"]');
        if (mesAnteriorBtn) mesAnteriorBtn.addEventListener('click', handleTableroConfigMesAnteriorClick);
        const mesSiguienteBtn = mount.querySelector('[data-action="config-mes-siguiente"]');
        if (mesSiguienteBtn) mesSiguienteBtn.addEventListener('click', handleTableroConfigMesSiguienteClick);
        for (const btn of mount.querySelectorAll('[data-action="config-feriado-abrir"]')) {
            btn.addEventListener('click', handleTableroFeriadoAbrirClick);
        }
        for (const btn of mount.querySelectorAll('[data-action="config-feriado-quitar"]')) {
            btn.addEventListener('click', handleTableroFeriadoQuitarClick);
        }
        const feriadoCancelarBtn = mount.querySelector('[data-action="config-feriado-cancelar"]');
        if (feriadoCancelarBtn) feriadoCancelarBtn.addEventListener('click', handleTableroFeriadoCancelarClick);
        const feriadoAgregarForm = mount.querySelector('form[data-action-form="config-feriado-agregar"]');
        if (feriadoAgregarForm) feriadoAgregarForm.addEventListener('submit', handleTableroFeriadoAgregarSubmit);
        const periodoForm = mount.querySelector('form[data-action-form="config-periodo-por-defecto"]');
        if (periodoForm) periodoForm.addEventListener('submit', handleTableroPeriodoPorDefectoSubmit);
        return;
    }

    if (esPago) {
        const registrarForm = mount.querySelector('form[data-action-form="pago-registrar"]');
        if (registrarForm) registrarForm.addEventListener('submit', handleTableroPagoRegistrarSubmit);
        // CRITICAL review-a57c3fe2ce2f2297, finding 1: fallback manual del aviso `incierto` — el refresh ya se dispara solo, este botón es para reintentar si algo lo interrumpió.
        const pagoActualizarBtn = mount.querySelector('[data-action="tablero-pago-actualizar"]');
        if (pagoActualizarBtn) pagoActualizarBtn.addEventListener('click', () => void refreshTableroPagosList());
        for (const btn of mount.querySelectorAll('[data-action="pago-anular-abrir"]')) {
            btn.addEventListener('click', handleTableroPagoAnularAbrir);
        }
        const pagoCancelarBtn = mount.querySelector('[data-action="pago-anular-cancelar"]');
        if (pagoCancelarBtn) pagoCancelarBtn.addEventListener('click', handleTableroPagoAnularCancelar);
        const pagoAnularForm = mount.querySelector('form[data-action-form="pago-anular-confirmar"]');
        if (pagoAnularForm) pagoAnularForm.addEventListener('submit', handleTableroPagoAnularConfirmarSubmit);
        return;
    }

    const cfg = cajonMovimiento(tableroDrawer.tipo);
    if (!cfg) return;
    const crearForm = mount.querySelector(`form[data-action-form="${cfg.formCrear}"]`);
    if (crearForm) crearForm.addEventListener('submit', handleMovimientoCrearSubmit);
    // Manual fallback of the `incierto` notice — the refresh already fires on its own.
    const actualizarBtn = mount.querySelector(`[data-action="${cfg.accionActualizar}"]`);
    if (actualizarBtn) actualizarBtn.addEventListener('click', () => void refreshTableroMovimientosList());
    for (const btn of mount.querySelectorAll(`[data-action="${cfg.accionAnularAbrir}"]`)) {
        btn.addEventListener('click', handleMovimientoAnularAbrir);
    }
    const cancelarBtn = mount.querySelector(`[data-action="${cfg.accionAnularCancelar}"]`);
    if (cancelarBtn) cancelarBtn.addEventListener('click', handleMovimientoAnularCancelar);
    const anularForm = mount.querySelector(`form[data-action-form="${cfg.formAnular}"]`);
    if (anularForm) anularForm.addEventListener('submit', handleMovimientoAnularConfirmarSubmit);
}

/**
 * Devuelve el foco al elemento que abrió el cajón (mismo criterio que
 * `closeLiquidacionComprobante`) — al trigger SI sigue en el documento
 * (puede haber desaparecido: un refresh de fondo sacó la fila), si no al
 * mount del tablero. Se llama desde `renderTableroDrawerMount` apenas
 * `tableroDrawer` pasa a `null`, así que cubre TODO cierre (botón, overlay,
 * Escape, o un cierre programático tras un submit exitoso) sin cablear cada
 * sitio de cierre por separado.
 */
function restoreTableroDrawerFocus() {
    const trigger = tableroDrawerTrigger;
    tableroDrawerTrigger = null;
    if (trigger && document.contains(trigger)) {
        trigger.focus();
        return;
    }
    const mount = getTableroMount();
    if (!mount) return;
    mount.setAttribute('tabindex', '-1');
    mount.focus();
}

/** Escape cierra el cajón, igual que el botón/overlay. @param {KeyboardEvent} e */
function handleTableroDrawerKeydown(e) {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    handleTableroDrawerCerrar();
}

function handleTableroDrawerCerrar() {
    // S6: mismo criterio de "no cerrar en medio de un submit" que Adelanto/
    // Anomalías (S3/S4), extendido a las dos escrituras propias de esta
    // unidad (feriados, período por defecto).
    if (tableroDrawerSubmitting || tableroFeriadoSubmitting || tableroPeriodoPorDefectoSaving) return;
    // Mismo criterio que `handleLiquidacionAnularCancelar`: bump del seq
    // para que un `refreshTableroMovimientosList`/submit todavía en vuelo no
    // reabra este cajón al resolver tarde.
    tableroDrawerSeq += 1;
    tableroDrawer = null;
    // S6: un cajón cerrado con un form/select a medio tipear nunca debe
    // reaparecer así al reabrir (el mismo criterio que el resto de esta
    // sección aplica a `tableroDrawer` en `mountLiquidacionPanel`).
    tableroFeriadosSeq += 1;
    tableroFeriadoDraft = null;
    tableroPeriodoPorDefectoDraft = null;
    tableroPeriodoPorDefectoError = null;
    renderTableroDrawerMount();
}

/**
 * Alta de un movimiento del cajón abierto — adelanto (spec `payroll-advances`,
 * "Creating an Advance") u otro descuento (spec liquidacion-otros-descuentos,
 * B1); lo que cambia entre los dos sale de `CAJON_MOVIMIENTO`. Un
 * éxito dispara un recompute completo del tablero (design.md AD5: "Adelanto
 * recorded or voided · ... Full recompute") — el adelanto recién cargado
 * puede cambiar la columna Adelantos/Neto de esa fila — y cierra el cajón,
 * SALVO que el admin haya tipeado algo mientras el alta estaba en vuelo (U2):
 * ahí el cajón queda abierto con lo tipeado y refresca su lista. Un resultado
 * `incierto` (falla de transporte o 5xx: el alta pudo haberse aplicado)
 * reemplaza el form por un aviso bloqueante hasta que la lista refrescada
 * muestre el estado real. Un rechazo de negocio (p.ej.
 * `periodo_ya_liquidado`) o un `error` limpio deja el cajón abierto con el
 * mensaje YA MAPEADO por el servicio (nunca el código crudo), para que el
 * admin pueda corregir y reintentar.
 * @param {SubmitEvent} e
 */
async function handleMovimientoCrearSubmit(e) {
    e.preventDefault();
    if (tableroDrawerSubmitting) return;
    const pending = tableroDrawer;
    const cfg = cajonMovimiento(pending?.tipo);
    if (!pending || !cfg) return;

    const form = e.currentTarget;
    const fecha = form.querySelector('[name="fecha"]')?.value ?? '';
    const montoRaw = form.querySelector('[name="monto"]')?.value ?? '';
    const texto = form.querySelector(`[name="${cfg.campoTexto}"]`)?.value ?? '';
    const monto = Number(montoRaw);
    if (!fecha || !Number.isFinite(monto) || monto <= 0 || (cfg.textoObligatorio && !texto.trim())) {
        tableroDrawer = { ...pending, fecha, monto: montoRaw, [cfg.campoTexto]: texto, error: cfg.errorIncompleto };
        renderTableroDrawerMount();
        return;
    }

    // review-5b6ea3ac37a82d85: pre-increment ONCE, mirroring
    // `handleLiquidacionAnularSubmit`'s `const seq = ++liquidacionAnularSeq`
    // — never bump `tableroDrawerSeq` again inside the branches below, or the
    // `finally` guard's `seq === tableroDrawerSeq` check never matches again
    // and `tableroDrawerSubmitting` gets stuck `true` forever.
    const seq = ++tableroDrawerSeq;
    tableroDrawerSubmitting = true;
    tableroDrawer = { ...pending, fecha, monto: montoRaw, [cfg.campoTexto]: texto, montoIlegible: false, error: null, submitting: true };
    renderTableroDrawerMount();
    try {
        const result = await cfg.crear({ empleadoId: pending.empleadoId, fecha, monto, texto });
        if (seq !== tableroDrawerSeq || !getTableroDrawerMount()) return;

        if (!result.ok) {
            if (result.kind === 'auth') {
                // review-5b6ea3ac37a82d85: no seq bump here (already done above).
                tableroDrawer = null;
                renderTableroDrawerMount();
                showToast(result.mensaje, { level: 'error' });
                return;
            }
            if (result.kind === 'incierto') {
                // Same criterion as `handleTableroPagoRegistrarSubmit`: an
                // AMBIGUOUS outcome (transport failure or 5xx) never re-arms
                // the SAME form — the RPC (crear_adelanto/crear_otro_descuento) may have committed. Blocking
                // notice until `refreshTableroMovimientosList` shows the real
                // list; the tablero is recomputed too, so its Adelantos/Neto
                // columns reflect whatever actually happened.
                tableroDrawer = { ...tableroDrawer, submitting: false, error: null, incierto: true, mensaje: result.mensaje };
                renderTableroDrawerMount();
                void refreshTableroMovimientosList();
                void recomputeTablero();
                return;
            }
            // validacion | error: el cajón queda abierto con el mensaje YA
            // MAPEADO por el servicio (nunca un código crudo) — ver el error
            // taxonomy de admin-adelantos.service.js. The live form wins over
            // what was submitted (same criterion as the pago drawer).
            tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), submitting: false, error: result.mensaje };
            renderTableroDrawerMount();
            return;
        }

        // If the admin typed anything while `crearAdelanto` was in flight
        // (a live value that differs from what was submitted), closing the
        // drawer would throw it away: keep it open with those values
        // (untouched fields cleared for the next entry) and refresh its list.
        // Otherwise close it, as always.
        const vivo = leerBorradorMovimiento(getTableroDrawerMount()) ?? {};
        const enviado = { fecha, monto: montoRaw, [cfg.campoTexto]: texto };
        const tocados = ['fecha', 'monto', cfg.campoTexto].filter((campo) => vivo[campo] !== undefined && vivo[campo] !== enviado[campo]);
        showToast(cfg.toastCreado, { level: 'success' });
        if (tocados.length > 0 || vivo.montoIlegible) {
            tableroDrawer = {
                ...tableroDrawer,
                ...vivo,
                fecha: tocados.includes('fecha') ? vivo.fecha : '',
                monto: tocados.includes('monto') || vivo.montoIlegible ? vivo.monto : '',
                [cfg.campoTexto]: tocados.includes(cfg.campoTexto) ? vivo[cfg.campoTexto] : '',
                submitting: false,
                error: vivo.montoIlegible ? vivo.error : null,
            };
            renderTableroDrawerMount();
            void refreshTableroMovimientosList();
            void recomputeTablero();
            return;
        }
        // review-5b6ea3ac37a82d85: no seq bump here (already done above).
        tableroDrawer = null;
        renderTableroDrawerMount();
        void recomputeTablero();
    } catch (err) {
        if (seq !== tableroDrawerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error(`handleMovimientoCrearSubmit: no se pudo registrar (${cfg.titulo})`, err);
        if (getTableroDrawerMount()) {
            tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), submitting: false, error: LIQUIDACION_GENERIC_ERROR_MSG };
            renderTableroDrawerMount();
        }
    } finally {
        if (seq === tableroDrawerSeq) tableroDrawerSubmitting = false;
    }
}

/** Revela el form inline de motivo para anular ESE movimiento (adelanto u otro descuento; nunca un `window.prompt`, mismo criterio que el resto del panel). @param {MouseEvent} e */
function handleMovimientoAnularAbrir(e) {
    const { id } = e.currentTarget.dataset;
    if (!tableroDrawer || !id) return;
    tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), anulandoId: id, anularError: null, motivo: '' };
    renderTableroDrawerMount();
}

function handleMovimientoAnularCancelar() {
    if (!tableroDrawer) return;
    tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), anulandoId: null, anularError: null, motivo: '' };
    renderTableroDrawerMount();
}

/**
 * Anula el movimiento (adelanto u otro descuento) cuyo form inline está
 * abierto (spec `payroll-advances`, "Voiding an Advance"; spec
 * liquidacion-otros-descuentos, B2). Igual que el alta: éxito cierra el
 * sub-form y dispara un recompute completo (design.md AD5, "Adelanto ...
 * voided -> Full recompute"); un rechazo (p.ej. `adelanto_liquidado` o
 * `descuento_liquidado`) se muestra inline sin cerrar nada.
 * @param {SubmitEvent} e
 */
async function handleMovimientoAnularConfirmarSubmit(e) {
    e.preventDefault();
    if (tableroDrawerSubmitting) return;
    const pending = tableroDrawer;
    const cfg = cajonMovimiento(pending?.tipo);
    if (!pending?.anulandoId || !cfg) return;
    const form = e.currentTarget;
    const id = form.dataset.id;
    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    if (!motivo.trim()) {
        tableroDrawer = { ...pending, ...leerBorradorMovimiento(getTableroDrawerMount()), anularError: cfg.motivoVacio };
        renderTableroDrawerMount();
        return;
    }

    // review-5b6ea3ac37a82d85: same pre-increment-once fix as
    // `handleMovimientoCrearSubmit` above — see its comment for why.
    const seq = ++tableroDrawerSeq;
    tableroDrawerSubmitting = true;
    try {
        const result = await cfg.anular({ id, motivo });
        if (seq !== tableroDrawerSeq || !getTableroDrawerMount()) return;
        // S2: same drawer target, not the same object (see `anularPago` above).
        if (!mismoCajonTablero(tableroDrawer, pending)) return;
        // C1: same sub-form too (see `handleTableroPagoAnularConfirmarSubmit`).
        const mismoSubForm = tableroDrawer.anulandoId === pending.anulandoId;

        if (!result.ok) {
            if (result.kind === 'auth') {
                // review-5b6ea3ac37a82d85: no seq bump here (already done above).
                tableroDrawer = null;
                renderTableroDrawerMount();
                showToast(result.mensaje, { level: 'error' });
                return;
            }
            if (mismoSubForm) {
                tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), anularError: result.mensaje };
                renderTableroDrawerMount();
            } else {
                // F1: the sub-form moved on — never drop the failure silently.
                // L9a: an uncertain outcome is a warning (it may have been
                // applied), never an error.
                showToast(result.mensaje, { level: result.kind === 'incierto' ? 'warning' : 'error' });
            }
            if (result.kind === 'incierto') {
                // The anulación may have been applied: show the real list
                // (and tablero figures) instead of leaving a stale one.
                void refreshTableroMovimientosList();
                void recomputeTablero();
            }
            return;
        }

        if (mismoSubForm) {
            tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), anulandoId: null, anularError: null, motivo: '' };
            renderTableroDrawerMount();
        }
        showToast(cfg.toastAnulado, { level: 'success' });
        void refreshTableroMovimientosList();
        void recomputeTablero();
    } catch (err) {
        if (seq !== tableroDrawerSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error(`handleMovimientoAnularConfirmarSubmit: no se pudo anular (${cfg.titulo})`, err);
        if (!getTableroDrawerMount() || !mismoCajonTablero(tableroDrawer, pending)) return;
        if (tableroDrawer.anulandoId === pending.anulandoId) {
            tableroDrawer = { ...tableroDrawer, ...leerBorradorMovimiento(getTableroDrawerMount()), anularError: LIQUIDACION_GENERIC_ERROR_MSG };
            renderTableroDrawerMount();
        } else {
            // F1: the sub-form moved on — never drop the failure silently.
            showToast(LIQUIDACION_GENERIC_ERROR_MSG, { level: 'error' });
        }
    } finally {
        if (seq === tableroDrawerSeq) tableroDrawerSubmitting = false;
    }
}

/**
 * Re-renderiza el mount principal de Liquidación (resultado del confirm +
 * historial + configuración) y re-cablea sus listeners.
 *
 * LA TRAMPA que ya mordió en Empleados, el kiosco y Fichadas: el mount es
 * uno solo, así que un preview/confirm iniciado desde el TABLERO (S7,
 * `iniciarLiquidarDesdeTablero`) también repinta este panel legacy debajo —
 * efecto secundario aceptado, ver apply-progress.md "Unit S7". El editor de
 * tope deriva lo tipeado del DOM, así que cualquier repintado lo borraría —
 * por eso se captura en `liquidacionCapDraft` ANTES de reemplazar el HTML y
 * se re-puebla desde ese borrador después. Mismo problema, misma solución
 * que `renderFichadasMount`/`renderEmpleadosMount` — no un patrón nuevo.
 *
 * S8: el selector de rango/empleados (`renderRangoForm`) y el preview
 * (`renderPreviewSection`) se retiraron — el tablero (S2-S7) es el único
 * camino para liquidar. `keepRangoDraft` conserva su nombre histórico (el
 * primer form que cubrió) pero hoy sólo gobierna el borrador del editor de
 * tope, que sigue viviendo acá dentro del área de Configuración.
 * @param {{ keepRangoDraft?: boolean }} [options]
 */
function renderLiquidacionMount({ keepRangoDraft = true } = {}) {
    const mount = getLiquidacionMount();
    if (!mount) return;

    if (keepRangoDraft) {
        captureLiquidacionCapDraft(mount);
    } else {
        liquidacionCapDraft = null;
    }

    mount.innerHTML = LiquidacionView(liquidacionViewModel, {
        ...liquidacionUiState,
        confirmResult: liquidacionConfirmResult,
        anularIncierto: liquidacionAnularIncierto,
        capConfig: liquidacionCapConfig
            ? { ...liquidacionCapConfig, saving: liquidacionCapSaveInFlight, draft: liquidacionCapDraft }
            : null,
        historial: liquidacionHistorial,
        historialPagos: liquidacionHistorialPagos,
        historialPagosDesconocidos: liquidacionHistorialPagosDesconocidos,
        configAbierta: liquidacionConfigAbierta,
        historialMostrarAnuladas: liquidacionMostrarAnuladas,
        historialFilasMostradas: liquidacionHistorialFilasMostradas,
        // R3: "Ver más" deshabilitado mientras recarga el historial.
        historialVerMas: { ...liquidacionHistorialViejas, cargando: liquidacionHistorialViejas.cargando || liquidacionHistorialRecargando },
    });
    applyLiquidacionCapDraft(mount);
    bindLiquidacionMountListeners(mount);
}

/**
 * Guarda lo tipeado en el input del tope antes de un repintado — MISMA
 * TRAMPA que el resto de esta sección (captura antes de reemplazar el HTML,
 * re-puebla después). No hace nada si el mount todavía no tiene el form del
 * editor.
 * @param {HTMLElement} mount
 */
function captureLiquidacionCapDraft(mount) {
    const form = mount.querySelector('form[data-action-form="payroll-config-tope"]');
    if (!form) return;
    liquidacionCapDraft = form.querySelector('[name="tope_descuento_mensual"]')?.value ?? '';
}

/**
 * Re-puebla el input del tope con el borrador guardado, si hay uno.
 * @param {HTMLElement} mount
 */
function applyLiquidacionCapDraft(mount) {
    if (liquidacionCapDraft === null) return;
    const form = mount.querySelector('form[data-action-form="payroll-config-tope"]');
    if (!form) return;
    const input = form.querySelector('[name="tope_descuento_mensual"]');
    if (input) input.value = liquidacionCapDraft;
}

/**
 * Carga el listado de empleados — S8: ya no alimenta un selector (retirado
 * junto con `renderRangoForm`), pero `liquidacionViewModel.empleados` sigue
 * siendo la fuente de nombres para el historial y el resultado del confirm
 * (`renderHistorialPanel`/`renderConfirmResultSection`, ambos reusados
 * VERBATIM por el tablero — AD1).
 */
async function refreshLiquidacionEmpleados() {
    const seq = ++liquidacionEmpleadosSeq;
    liquidacionUiState = { status: 'loading' };
    renderLiquidacionMount({ keepRangoDraft: false });
    try {
        const empleados = await loadEmpleados();
        if (seq !== liquidacionEmpleadosSeq || !getLiquidacionMount()) return;
        liquidacionViewModel = { empleados };
        liquidacionUiState = { status: 'ready' };
    } catch (err) {
        if (seq !== liquidacionEmpleadosSeq || !getLiquidacionMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshLiquidacionEmpleados: no se pudo cargar el listado de empleados', err);
        liquidacionUiState = { status: 'error' };
    }
    renderLiquidacionMount();
}

/**
 * Carga el tope mensual de descuento vigente para el editor (E1). Corre en
 * PARALELO con `refreshLiquidacionEmpleados` (disparado desde
 * `mountLiquidacionPanel`) — ninguno de los dos bloquea al otro, y cada uno
 * escribe en su propia variable de módulo, así que no hay carrera de
 * escritura entre ambos (a diferencia de `refreshFudoDirectory`/
 * `refreshEmpleados`, que sí mergean en un mismo `empleadosViewModel`).
 */
async function refreshLiquidacionCapConfig() {
    const seq = ++liquidacionCapConfigSeq;
    try {
        const result = await loadPayrollConfig();
        if (seq !== liquidacionCapConfigSeq || !getLiquidacionMount()) return;
        liquidacionCapConfig = {
            cargando: false,
            configurado: result.configurado,
            tope: result.tope,
            error: null,
            // F4 (review-050ef9b16341fee9): forwarded so el editor pueda
            // mostrar quién/cuándo cambió el tope por última vez.
            updatedAt: result.updatedAt ?? null,
            updatedBy: result.updatedBy ?? null,
        };
        // Owner UX batch (2026-09-02): auto-abre el área de Configuración
        // cuando el tope todavía NO está cargado — mismo gate que
        // `renderTopeNoConfigurado` ya usa en preview/confirm, para que el
        // admin vea de entrada qué hay que hacer, sin tener que descubrir el
        // botón engranaje primero. Nunca la CIERRA acá: si ya estaba abierta
        // a mano, o el tope YA está configurado, este load no toca el flag.
        // Resilience fix (review-c978b1e13b357306): tampoco la vuelve a
        // ABRIR si el admin ya tocó el engranaje durante este montaje — ver
        // `liquidacionConfigTocadoPorAdmin`.
        if (!result.configurado && !liquidacionConfigTocadoPorAdmin) liquidacionConfigAbierta = true;
    } catch (err) {
        if (seq !== liquidacionCapConfigSeq || !getLiquidacionMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshLiquidacionCapConfig: no se pudo cargar el tope', err);
        liquidacionCapConfig = {
            cargando: false,
            configurado: false,
            tope: null,
            error: 'No pudimos cargar el tope vigente.',
            updatedAt: null,
            updatedBy: null,
        };
        // Same auto-open criterion as the success branch above — an
        // unreachable cap config is just as much "not configured, the admin
        // needs to see this" as a genuinely empty one (AD11 already
        // collapses both cases at the service layer). Same resilience fix
        // (review-c978b1e13b357306) applies here too.
        if (!liquidacionConfigTocadoPorAdmin) liquidacionConfigAbierta = true;
    }
    renderLiquidacionMount();
}

/**
 * E2 — carga el historial de liquidaciones para el panel informativo (AD9).
 * Mismo patrón que `refreshLiquidacionCapConfig`: corre en paralelo desde
 * `mountLiquidacionPanel`, sin bloquear preview/confirm, y una falla acá
 * SOLO degrada este panel — nunca toca `liquidacionPreview`/
 * `liquidacionConfirmResult`. F1: el fetch se acota a
 * `liquidacionHistorialRangoDefault()` (períodos que arrancan en los últimos
 * `LIQUIDACION_HISTORIAL_MESES` meses), nunca la tabla completa. También se
 * dispara después de CUALQUIER confirm que haya persistido algo — éxito
 * total (`handleLiquidacionConfirmConfirmar`, rama `ok`) o rechazo PARCIAL
 * (rama `rechazo_parcial`, F2: ese rechazo también deja liquidaciones nuevas
 * ya escritas) — y después de anular, porque el historial que se venía
 * mostrando queda stale.
 *
 * S8/L5 (spec `payroll-payments` "Derived Payment State"): además de las
 * filas, lee los pagos de TODAS las filas cargadas — confirmadas y anuladas
 * (una anulación no anula sus pagos) — batcheado, UN solo `loadPagos`, mismo
 * criterio "no view, no per-settlement read" de `pagos.repository.js`, para
 * la pill + "Registrar pago" (confirmadas) y la lista de pagos (anuladas).
 * Lectura NUEVA (distinta del batch AD18 del tablero, acotado al período
 * vigente ahí); degrada a `liquidacionHistorialPagos = null` sola —
 * AD9-style, nunca tira abajo el resto del historial ya resuelto.
 *
 * L8/R3: la recarga trae sólo la ventana y congela su inicio como frontera
 * de "Ver más" (`liquidacionHistorialViejas.inicioAntesDe`). Mientras está en
 * vuelo "Ver más" no arranca (`liquidacionHistorialRecargando`), y al
 * resolver vuelve a reiniciar la paginación de las viejas y descarta
 * cualquier página en vuelo: nunca queda un `cargadas` de antes de la
 * recarga que haga saltear una página.
 */
function refreshLiquidacionHistorial() {
    const seq = ++liquidacionHistorialSeq;
    const ventana = liquidacionHistorialRangoDefault();
    const reiniciarViejas = () => {
        liquidacionHistorialVerMasSeq += 1;
        liquidacionHistorialViejas = { inicioAntesDe: ventana.inicioDesde, cargadas: 0, hayMas: true, cargando: false, error: null };
    };
    // L8: las páginas viejas ya leídas se descartan (y una en vuelo también),
    // la próxima "Ver más" arranca de 0.
    reiniciarViejas();
    liquidacionHistorialRecargando = true;
    // review-982449c9e9c27e08 (finding 2): publicada ANTES de cualquier `await` interno, para que `recomputeTablero` la encuentre.
    liquidacionHistorialPromise = (async () => {
        try {
            // Mismo tope que el resto de las cargas de sección: un cuelgue
            // termina en el estado de error y libera "Ver más" (R3).
            const liquidaciones = await conTimeoutDeCarga(loadLiquidacionesHistorial(ventana));
            if (seq !== liquidacionHistorialSeq || !getLiquidacionMount()) return;
            liquidacionHistorial = { cargando: false, error: null, liquidaciones };
            liquidacionHistorialPagosDesconocidos = new Set();
            // W1: el historial recargado ES el estado real — una anulación
            // `incierto` ya quedó contrastada (aplicada o no, se ve en la fila).
            liquidacionAnularIncierto = null;

            // L5: también los pagos de las liquidaciones ANULADAS — una
            // anulación no anula sus pagos (anular_pago, migración 29), y sin
            // esta lectura quedaban invisibles y sin forma de anularlos.
            const idsHistorial = liquidaciones.map((r) => r.id);
            if (idsHistorial.length === 0) {
                liquidacionHistorialPagos = new Map();
            } else {
                try {
                    const pagos = await conTimeoutDeCarga(loadPagos({ liquidacionIds: idsHistorial }));
                    if (seq !== liquidacionHistorialSeq || !getLiquidacionMount()) return;
                    liquidacionHistorialPagos = agruparPagosPorLiquidacion(pagos);
                } catch (err) {
                    if (seq !== liquidacionHistorialSeq || !getLiquidacionMount()) return;
                    window.Sentry?.captureException(err);
                    // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
                    console.error('refreshLiquidacionHistorial: no se pudo leer pagos del historial', err);
                    liquidacionHistorialPagos = null; // degradado: SOLO la pill queda ausente
                }
            }
        } catch (err) {
            if (seq !== liquidacionHistorialSeq || !getLiquidacionMount()) return;
            window.Sentry?.captureException(err);
            // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
            console.error('refreshLiquidacionHistorial: no se pudo cargar el historial', err);
            liquidacionHistorial = { cargando: false, error: 'No pudimos cargar el historial de liquidaciones.', liquidaciones: [] };
        } finally {
            if (seq === liquidacionHistorialSeq) {
                liquidacionHistorialRecargando = false;
                // R3: la recarga resolvió — la paginación de las viejas
                // vuelve a arrancar de 0 sobre las filas NUEVAS.
                reiniciarViejas();
            }
        }
        renderLiquidacionMount();
    })();
    return liquidacionHistorialPromise;
}

/**
 * `pagos` -> `Map<liquidacion_id, pago[]>` (orden de llegada).
 * @param {Array<{ liquidacion_id: string }>} pagos
 * @param {Map<string, Array<object>>} [base] mapa a extender (se copia, nunca se muta)
 * @returns {Map<string, Array<object>>}
 */
function agruparPagosPorLiquidacion(pagos, base = new Map()) {
    const porLiquidacion = new Map(base);
    for (const p of pagos ?? []) {
        const arr = porLiquidacion.get(p.liquidacion_id);
        if (arr) porLiquidacion.set(p.liquidacion_id, [...arr, p]);
        else porLiquidacion.set(p.liquidacion_id, [p]);
    }
    return porLiquidacion;
}

/**
 * L8 — "Ver más" del historial. Si quedan filas cargadas sin mostrar, sólo
 * sube el tope de filas visibles (sin leer nada). Si ya se muestra todo lo
 * cargado, lee UNA página de liquidaciones más viejas que la ventana
 * (`inicioAntesDe` = inicio de la ventana congelado en la última recarga,
 * `offset` = las viejas ya leídas, `limit` = una página), más recientes
 * primero, y la agrega al final junto con sus pagos (misma lectura batcheada
 * que `refreshLiquidacionHistorial`). Una página incompleta significa que no
 * hay nada más viejo.
 *
 * R2: las dos lecturas tienen el tope de carga de sección
 * (`conTimeoutDeCarga`). Una falla o un timeout de la PÁGINA deja lo que ya
 * se mostraba, avisa y permite reintentar. Una falla o un timeout de sus
 * PAGOS (R1) agrega las filas igual, marcadas como "pagos desconocidos"
 * (`liquidacionHistorialPagosDesconocidos`), nunca como "Pendiente".
 * R3: no arranca mientras el historial recarga.
 */
async function handleLiquidacionHistorialVerMas() {
    if (liquidacionHistorialViejas.cargando || liquidacionHistorialRecargando) return;
    const rows = liquidacionHistorial?.liquidaciones ?? [];
    const visibles = liquidacionMostrarAnuladas ? rows : rows.filter((r) => r.estado !== 'anulada');
    if (visibles.length > liquidacionHistorialFilasMostradas) {
        liquidacionHistorialFilasMostradas += LIQUIDACION_HISTORIAL_PAGINA;
        renderLiquidacionMount();
        return;
    }
    if (!liquidacionHistorialViejas.hayMas || !liquidacionHistorialViejas.inicioAntesDe) return;

    const seq = ++liquidacionHistorialVerMasSeq;
    const { inicioAntesDe, cargadas: offset } = liquidacionHistorialViejas;
    liquidacionHistorialViejas = { ...liquidacionHistorialViejas, cargando: true, error: null };
    renderLiquidacionMount();
    try {
        const pagina = await conTimeoutDeCarga(loadLiquidacionesHistorial({ inicioAntesDe, offset, limit: LIQUIDACION_HISTORIAL_PAGINA }));
        if (seq !== liquidacionHistorialVerMasSeq || !getLiquidacionMount()) return;
        const idsCargados = new Set((liquidacionHistorial?.liquidaciones ?? []).map((r) => r.id));
        const nuevas = (pagina ?? []).filter((r) => !idsCargados.has(r.id));

        let pagosNuevos = [];
        let pagosFallaron = false;
        if (nuevas.length > 0 && liquidacionHistorialPagos) {
            try {
                pagosNuevos = await conTimeoutDeCarga(loadPagos({ liquidacionIds: nuevas.map((r) => r.id) }));
            } catch (err) {
                pagosFallaron = true;
                window.Sentry?.captureException(err);
                // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
                console.error('handleLiquidacionHistorialVerMas: no se pudieron leer los pagos de la página', err);
            }
            if (seq !== liquidacionHistorialVerMasSeq || !getLiquidacionMount()) return;
        }

        liquidacionHistorial = { ...liquidacionHistorial, liquidaciones: [...(liquidacionHistorial?.liquidaciones ?? []), ...nuevas] };
        if (pagosFallaron) {
            // R1: pagos desconocidos, nunca "sin pagos".
            liquidacionHistorialPagosDesconocidos = new Set([...liquidacionHistorialPagosDesconocidos, ...nuevas.map((r) => r.id)]);
        } else if (liquidacionHistorialPagos) {
            liquidacionHistorialPagos = agruparPagosPorLiquidacion(pagosNuevos, liquidacionHistorialPagos);
        }
        liquidacionHistorialViejas = {
            inicioAntesDe,
            cargadas: offset + (pagina?.length ?? 0),
            hayMas: (pagina?.length ?? 0) === LIQUIDACION_HISTORIAL_PAGINA,
            cargando: false,
            error: null,
        };
        liquidacionHistorialFilasMostradas += LIQUIDACION_HISTORIAL_PAGINA;
    } catch (err) {
        if (seq !== liquidacionHistorialVerMasSeq || !getLiquidacionMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleLiquidacionHistorialVerMas: no se pudo cargar la página', err);
        liquidacionHistorialViejas = { ...liquidacionHistorialViejas, cargando: false, error: 'No pudimos cargar liquidaciones más viejas.' };
    }
    renderLiquidacionMount();
}

/**
 * S8 (design.md "File Changes" S8 row, spec `payroll-period-dashboard` "Row
 * and Bulk Actions": "Exportar CSV" en la barra en lote) — exporta a CSV las
 * liquidaciones del mes elegido en el mini-form de la barra en lote del
 * tablero. REUBICADO desde el toolbar legacy del historial
 * (`liquidacion-wiring.test.js`'s Unit B2, retirado con `renderRangoForm`/
 * `renderPreviewSection`) — la policy y el comportamiento de exportación NO
 * cambian, sólo el mount (`getTableroMount()` en vez de
 * `getLiquidacionMount()`).
 *
 * `mesARango` valida el mes ANTES de cualquier lectura (spec "Invalid month
 * is rejected before reading"): un mes vacío o malformado nunca dispara
 * `loadLiquidacionesHistorial` ni el seam de descarga — mismo toast
 * (`LIQUIDACION_MES_INVALIDO_MSG`) que el resto de esta sección ya usa para
 * el mismo tipo de rechazo.
 *
 * AD11 (original): SIEMPRE refetchea por su cuenta vía
 * `loadLiquidacionesHistorial`, nunca reusa `liquidacionHistorial.liquidaciones`
 * — ese estado está acotado a `LIQUIDACION_HISTORIAL_MESES` meses (más las
 * páginas que haya sumado "Ver más"), capado a
 * `liquidacionHistorialFilasMostradas` filas visibles en pantalla y filtrado
 * por el toggle "Mostrar anuladas": exportar lo que está en pantalla produciría un
 * archivo cuyo contenido depende de un toggle de UI y un tope de
 * renderizado, sin ningún aviso.
 *
 * Un resultado vacío (tras el filtro de `incluirAnuladas`) o una falla de
 * lectura se muestran como un mensaje INLINE en la propia barra en lote
 * (`tableroExportMensaje`, spec "A read failure degrades only the export
 * panel") — nunca tocan `tableroCache`/`liquidacionPreview`. Guard de
 * secuencia (`tableroExportSeq`, mismo patrón que el resto de esta sección):
 * si el mount se re-monta (cambio de pestaña) mientras un export sigue en
 * vuelo, la respuesta tardía no debe tocar un estado que ya no existe.
 */
async function handleTableroExportarClick() {
    if (tableroExportInFlight) return;
    const mount = getTableroMount();
    if (!mount) return;
    const form = mount.querySelector('form[data-action-form="tablero-export"]');
    const mes = form?.querySelector('[name="mes-export"]')?.value ?? '';
    const incluirAnuladas = form?.querySelector('[name="incluir-anuladas-export"]')?.checked ?? false;

    // Bugfix (heredado de B2 closing batch, review-4389286eba29e81b): el
    // mensaje se limpia en CADA intento, ANTES de validar el mes — si no, un
    // mensaje de un intento anterior (p.ej. "No hay liquidaciones para ese
    // mes.") queda pegado en pantalla cuando el mes nuevo es inválido y la
    // función corta más abajo antes de llegar a la limpieza que solía estar
    // acá.
    tableroExportMensaje = null;
    renderTableroMount();

    const rango = mesARango(mes);
    if (!rango) {
        showToast(LIQUIDACION_MES_INVALIDO_MSG, { level: 'error' });
        return;
    }

    const seq = ++tableroExportSeq;
    tableroExportInFlight = true;
    renderTableroMount();

    try {
        // L6 (owner decision 2026-09-27): una liquidación pertenece al mes de
        // su `periodo_desde` (`mesDeLiquidacion`, payroll-policy — la MISMA
        // regla que el gate de sueldo fijo, D1) — una semana que cruza de mes
        // va al PRIMER mes.
        // Antes se leía por contención (`periodo_desde >= inicio` y
        // `periodo_hasta <= fin`), y esa semana no salía en ningún archivo.
        const rows = await loadLiquidacionesHistorial({ inicioDesde: rango.desde, inicioHasta: rango.hasta });
        if (seq !== tableroExportSeq || !getTableroMount()) return;
        const { cabecera, filas } = construirFilas(rows, { incluirAnuladas });
        if (!filas.length) {
            tableroExportMensaje = 'No hay liquidaciones para ese mes.';
        } else {
            downloadTextFile({
                filename: nombreArchivoExport(mes),
                text: conBom(serializarCsv({ cabecera, filas })),
                mime: 'text/csv;charset=utf-8',
            });
        }
    } catch (err) {
        if (seq !== tableroExportSeq || !getTableroMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleTableroExportarClick: no se pudo cargar el historial para exportar', err);
        tableroExportMensaje = 'No se pudo cargar el historial para exportar.';
    } finally {
        if (seq === tableroExportSeq) tableroExportInFlight = false;
    }
    if (seq === tableroExportSeq && getTableroMount()) renderTableroMount();
}

/** @param {HTMLElement} mount */
function bindLiquidacionMountListeners(mount) {
    const capForm = mount.querySelector('form[data-action-form="payroll-config-tope"]');
    if (capForm) capForm.addEventListener('submit', handleLiquidacionCapSaveSubmit);

    const retryBtn = mount.querySelector('[data-action="liquidacion-retry"]');
    if (retryBtn) retryBtn.addEventListener('click', () => void refreshLiquidacionEmpleados());

    // W1: el estado de error del historial trae su propio "Reintentar".
    const historialRetryBtn = mount.querySelector('[data-action="liquidacion-historial-retry"]');
    if (historialRetryBtn) historialRetryBtn.addEventListener('click', handleLiquidacionHistorialRetry);

    // S8: `data-action="liquidacion-confirmar-abrir"` era el botón "Confirmar
    // liquidación" de `renderPreviewReady` (retirado con `renderPreviewSection`)
    // — `handleLiquidacionConfirmarAbrir` sigue viva porque el tablero (S7,
    // `iniciarLiquidarDesdeTablero`) la llama directamente, sin pasar por un
    // botón en este mount.

    const confirmResultDismissBtn = mount.querySelector('[data-action="liquidacion-confirm-result-dismiss"]');
    if (confirmResultDismissBtn) {
        confirmResultDismissBtn.addEventListener('click', () => {
            liquidacionConfirmResult = null;
            renderLiquidacionMount();
        });
    }

    // Unit B2 — un botón "Anular" por fila `confirmada` del historial.
    for (const btn of mount.querySelectorAll('[data-action="liquidacion-anular"]')) {
        btn.addEventListener('click', handleLiquidacionAnularClick);
    }

    // Unit A2 (design.md AD5, spec "Comprobante Availability") — un botón
    // "Comprobante" por fila del historial, `confirmada` O `anulada` (a
    // diferencia de "Anular", que sólo existe en filas `confirmada`).
    for (const btn of mount.querySelectorAll('[data-action="liquidacion-comprobante"]')) {
        btn.addEventListener('click', handleLiquidacionComprobanteClick);
    }

    // Owner UX batch (2026-09-02) — botón engranaje: abre/cierra el área de
    // Configuración. Reusa `renderLiquidacionMount()` (con `keepRangoDraft:
    // true` por default, nombre histórico — hoy sólo conserva el borrador
    // del editor de tope) — un toggle nunca pierde lo tipeado ahí.
    const configToggleBtn = mount.querySelector('[data-action="liquidacion-config-toggle"]');
    if (configToggleBtn) {
        configToggleBtn.addEventListener('click', () => {
            liquidacionConfigAbierta = !liquidacionConfigAbierta;
            // Resilience fix (review-c978b1e13b357306): once the admin
            // touches the gear by hand, `refreshLiquidacionCapConfig` must
            // never override their choice again for this mount.
            liquidacionConfigTocadoPorAdmin = true;
            renderLiquidacionMount();
        });
    }

    // Owner UX batch (2026-09-02) — toggle "Mostrar anuladas (N)": mismo
    // patrón de re-render con draft-preserving que el botón engranaje.
    const verMasBtn = mount.querySelector('[data-action="liquidacion-historial-ver-mas"]');
    if (verMasBtn) verMasBtn.addEventListener('click', () => void handleLiquidacionHistorialVerMas());

    const mostrarAnuladasToggle = mount.querySelector('[data-action="liquidacion-historial-mostrar-anuladas"]');
    if (mostrarAnuladasToggle) {
        mostrarAnuladasToggle.addEventListener('change', () => {
            liquidacionMostrarAnuladas = mostrarAnuladasToggle.checked;
            renderLiquidacionMount();
        });
    }

    // S8 (spec `payroll-payments` "Derived Payment State", reusa el MISMO
    // cajón "Registrar pago" que el tablero — `handleTableroPagoClick` — pero
    // sourceado desde una fila del historial, que puede no estar en
    // `tableroCache` (otro período).
    for (const btn of mount.querySelectorAll('[data-action="liquidacion-historial-pago"]')) {
        btn.addEventListener('click', handleLiquidacionHistorialPagoClick);
    }
    // L5: anular un pago de una liquidación anulada (lista del historial).
    for (const btn of mount.querySelectorAll('[data-action="liquidacion-historial-pago-anular"]')) {
        btn.addEventListener('click', handleLiquidacionHistorialPagoAnularClick);
    }
}

/**
 * Guarda el tope mensual de descuento (editor E1). Mismo contrato que
 * `handleEmpleadoSave`, MÁS el mismo guard de secuencia monotónica que el
 * resto de esta sección (F1, review-050ef9b16341fee9 — ver el comentario de
 * cabecera de `liquidacionCapSaveSeq`).
 *
 * F2 (mismo lineage): un rechazo de VALIDACIÓN (p.ej. negativo) sigue siendo
 * un `showToast(result.error, {level:'error'})` que conserva lo tipeado (el
 * draft NO se limpia). Un rechazo PERMANENTE (`result.permanente` — permiso
 * denegado o configuración inalcanzable, el servicio ya los distingue) NO
 * usa ese toast: se pinta en el bloque de estado del editor
 * (`liquidacionCapConfig.error`, mismo criterio que
 * `renderTopeNoConfigurado`), porque un toast ahí sugeriría "reintentá y
 * puede que funcione" y para esos dos casos eso nunca es cierto sin que
 * cambie algo por fuera de este formulario (el rol del usuario, o la
 * conexión). Una excepción real no clasificada (transporte cortado) sigue
 * siendo Sentry + console.error + el toast genérico. El re-render final vive
 * en el `finally`, después de bajar el guard de re-entrancy — mismo orden
 * que el resto de las mutaciones de esta sección.
 * @param {SubmitEvent} e
 */
async function handleLiquidacionCapSaveSubmit(e) {
    e.preventDefault();
    if (liquidacionCapSaveInFlight) return;

    const form = e.currentTarget;
    const topeRaw = form.querySelector('[name="tope_descuento_mensual"]')?.value ?? '';
    const submitBtn = form.querySelector('[data-action="payroll-config-guardar"]');

    const seq = ++liquidacionCapSaveSeq;
    liquidacionCapSaveInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        const session = await getCurrentSession();
        const result = await savePayrollConfig({
            topeDescuentoMensual: topeRaw,
            updatedBy: session?.user?.id ?? null,
        });
        if (seq !== liquidacionCapSaveSeq || !getLiquidacionMount()) return;
        if (!result.ok) {
            if (result.permanente) {
                liquidacionCapConfig = { ...(liquidacionCapConfig ?? {}), error: result.error };
            } else {
                // E1b review follow-up B: un rechazo NO permanente (p.ej. una
                // validación ordinaria) reemplaza cualquier bloque de error
                // PERSISTENTE que hubiera quedado de un intento anterior — si
                // no se limpia acá, un mensaje de "sin permisos" viejo se
                // queda pegado en pantalla mientras el toast de abajo avisa
                // de un problema distinto y ya no vigente.
                if (liquidacionCapConfig?.error) {
                    liquidacionCapConfig = { ...liquidacionCapConfig, error: null };
                }
                showToast(result.error ?? LIQUIDACION_CAP_SAVE_ERROR_MSG, { level: 'error' });
            }
            return;
        }
        liquidacionCapConfig = {
            cargando: false,
            configurado: true,
            tope: result.tope,
            error: null,
            updatedAt: result.updatedAt ?? null,
            updatedBy: result.updatedBy ?? null,
        };
        liquidacionCapDraft = null;
        // L2: el tope cambia el descuento que calcula el PRÓXIMO preview — las
        // cifras del tablero quedan viejas. Mismo mecanismo que un feriado
        // (AD5): se marcan "desactualizadas", sin recompute inmediato.
        markTableroCacheStale();
        if (getTableroMount()) renderTableroMount();
        showToast(LIQUIDACION_CAP_SAVE_SUCCESS_MSG, { level: 'success' });
    } catch (err) {
        if (seq !== liquidacionCapSaveSeq) return; // F1: sesión vieja, no pisar la nueva
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleLiquidacionCapSaveSubmit: no se pudo guardar el tope', err);
        if (!getLiquidacionMount()) return;
        // E1b review follow-up B, mismo criterio que la rama `!result.ok`.
        if (liquidacionCapConfig?.error) {
            liquidacionCapConfig = { ...liquidacionCapConfig, error: null };
        }
        showToast(LIQUIDACION_CAP_SAVE_ERROR_MSG, { level: 'error' });
    } finally {
        if (seq === liquidacionCapSaveSeq) liquidacionCapSaveInFlight = false;
        if (submitBtn?.isConnected) submitBtn.disabled = false;
        if (seq === liquidacionCapSaveSeq && getLiquidacionMount()) renderLiquidacionMount();
        // S6: el MISMO form vive también dentro del cajón de Configuración
        // del tablero (reuso de `renderCapEditor`/esta función, AD7
        // extendido) — sin esto, guardar el tope desde ahí sólo se
        // reflejaría en la sección legacy de abajo, nunca en el cajón mismo.
        if (seq === liquidacionCapSaveSeq && getTableroDrawerMount() && tableroDrawer?.tipo === 'configuracion') {
            renderTableroDrawerMount();
        }
    }
}

/**
 * Corre un preview: descarta una respuesta vieja (`liquidacionPreviewSeq`,
 * mismo patrón que `refreshFichadasSeq`) y limpia cualquier resultado de
 * confirm anterior — un preview nuevo implica que el admin está decidiendo
 * de nuevo, así que un resultado de confirm viejo (aunque fuera un rechazo
 * parcial) ya no debería seguir tapando la pantalla.
 * @param {{ empleadoIds: string[], desde: string, hasta: string }} rango
 */
async function runLiquidacionPreview(rango) {
    const seq = ++liquidacionPreviewSeq;
    liquidacionUltimoRango = rango;
    liquidacionMutationInFlight = true;
    liquidacionPreview = { status: 'loading' };
    liquidacionConfirmResult = null;
    renderLiquidacionMount();
    // review-62957fd6e8912f8e: el tablero (si está montado) también refleja `liquidando`.
    if (getTableroMount()) renderTableroMount();

    try {
        const result = await previewLiquidacion(rango);
        if (seq !== liquidacionPreviewSeq || !getLiquidacionMount()) return;
        liquidacionPreview = mapLiquidacionResultToPreviewState(result);
    } catch (err) {
        if (seq !== liquidacionPreviewSeq || !getLiquidacionMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('runLiquidacionPreview: no se pudo calcular el preview', err);
        liquidacionPreview = { status: 'error', mensaje: LIQUIDACION_GENERIC_ERROR_MSG };
    } finally {
        if (seq === liquidacionPreviewSeq) liquidacionMutationInFlight = false;
    }
    if (seq === liquidacionPreviewSeq) {
        renderLiquidacionMount();
        if (getTableroMount()) renderTableroMount();
    }
}

/**
 * Traduce el `kind` discriminado del servicio al `status` que entiende
 * `LiquidacionView`. Requisito 1 (RF-11) y requisito 4 (cero silencioso de
 * Fudo) se resuelven ACÁ, cada uno con su propio `status` — ninguno de los
 * dos cae en el `status: 'error'` genérico, que es justo lo que un "$0 de
 * descuentos" o un "hubo un error" silencioso harían.
 * @param {object} result `LiquidacionResult` de admin-liquidacion.service.js
 * @returns {object}
 */
function mapLiquidacionResultToPreviewState(result) {
    if (result.ok) return { status: 'ready', liquidaciones: result.liquidaciones };
    if (result.kind === 'anomalias') return { status: 'anomalias', anomalias: result.anomalias };
    if (result.kind === 'fudo_bloqueado') return { status: 'bloqueado', mensaje: result.mensaje };
    // D2 correction, follow-ups C2/C3: mismo tratamiento por identidad de
    // `kind` — `LiquidacionView` ya distingue estos dos `status` del error
    // genérico (bloqueo de configuración / precondición del batch, nunca
    // "probá de nuevo").
    if (result.kind === 'tope_no_configurado' || result.kind === 'precondicion_bloqueante') {
        return { status: result.kind, mensaje: result.mensaje, detalle: result.detalle };
    }
    return { status: 'error', mensaje: result.mensaje || LIQUIDACION_GENERIC_ERROR_MSG };
}

/**
 * Abre el `ConfirmDialog` de liquidación — NUNCA llama a `confirmLiquidacion`
 * directamente desde acá (requisito 2). Unit B2: `liquidaciones` ya NO es
 * estrictamente append-only — existe una única transición auditada
 * (`confirmada -> anulada`, migración 27) — pero los MONTOS siguen sin poder
 * editarse nunca, y esta pantalla sigue exigiendo un `ConfirmDialog`
 * explícito antes de escribir cualquier fila nueva; el diálogo ya no
 * promete que la liquidación es indeshacible (`LIQUIDACION_CONFIRM_IRREVERSIBLE_MSG`,
 * B2.7). Reusa `liquidacionUltimoRango` (el mismo `empleado_ids`/`desde`/
 * `hasta` que generó el preview vigente); ningún monto viaja acá —
 * `api/liquidacion.js` recalcula todo de cero al confirmar.
 */
/**
 * `YYYY-MM-DD` -> `dd/mm`, sin año — mismo formateador string-only que
 * `LiquidacionTableroView.js#formatDiaMes` (duplicado deliberado, no
 * exportado desde un componente de vista hacia el wiring; mismo criterio que
 * `AdelantoDrawerView.js#formatFechaSql`).
 * @param {string} fechaSql
 * @returns {string}
 */
function formatDiaMesSql(fechaSql) {
    // WARNING review-a57c3fe2ce2f2297, finding 3: mismo guard que
    // `LiquidacionTableroView.js#formatDiaMes` — un `periodo_desde`/`hasta`
    // no-string (o mal formado) nunca debe tirar abajo el diálogo de confirmar.
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}`;
}

/**
 * S5b (design.md AD18): mismo texto que ya se muestra bajo la pill de la
 * fila (`LiquidacionTableroView.js#formatAvisoPagoPrevio`) — el TEXTO en sí
 * ya no está duplicado (review-a57c3fe2ce2f2297, finding 4:
 * `liquidacion-pagos-policy.js#textoAvisoPagoPrevio`); sólo el formateador de
 * fecha string-only sigue sin compartirse, misma razón que {@link formatDiaMesSql}.
 * @param {{ total: number, periodos: Array<{desde:string,hasta:string}> }} aviso
 * @returns {string}
 */
function formatAvisoPagoPrevioTexto(aviso) {
    const periodosFormateados = (aviso.periodos ?? []).map((p) => `${formatDiaMesSql(p.desde)} a ${formatDiaMesSql(p.hasta)}`);
    return textoAvisoPagoPrevio({ total: aviso.total, periodosFormateados });
}

/**
 * AD18 — mismo aviso que la fila, ahora concatenado al mensaje del
 * `ConfirmDialog` de "Liquidar" (spec: "the confirm still writes... notice,
 * never a block"; design.md: "misma copia, por empleado del batch"). Fuente:
 * `tableroCache.filas`, ya resuelto por el último `recomputeTablero` — CERO
 * lectura nueva. `tableroCache` es `null` en la pantalla legacy (sin tablero
 * montado), así que ahí esto siempre devuelve `''` — sin cambios para ese flujo.
 * @param {string[]} empleadoIds
 * @returns {string} sufijo a concatenar al mensaje del diálogo, o `''`
 */
function construirAvisoPagoPrevioParaConfirm(empleadoIds) {
    if (!tableroCache?.filas) return '';
    const avisos = [];
    for (const id of empleadoIds) {
        const fila = tableroCache.filas.get(id);
        if (!fila?.avisoPagoPrevio) continue;
        avisos.push(`${fila.nombre}: ${formatAvisoPagoPrevioTexto(fila.avisoPagoPrevio)}`);
    }
    return avisos.length ? ` ${avisos.join(' ')}` : '';
}

/**
 * Un renglón "Nombre: $neto." por cada empleado del batch (design.md AD6 —
 * el admin debe ver A QUIÉN y CUÁNTO antes de confirmar, no sólo un conteo).
 * `formatMonto` (no `formatPrice`): un `neto` de `0` es un valor real acá,
 * nunca "Consultar". Va PRIMERO en el mensaje, antes del excedente/AD18.
 * @param {string[]} empleadoIds
 * @param {Array<object>} liquidaciones
 * @returns {string}
 */
function construirEmpleadosParaConfirm(empleadoIds, liquidaciones) {
    const nombrePorId = new Map((liquidacionViewModel?.empleados ?? []).map((e) => [e.id, e.nombre]));
    const netoPorId = new Map((liquidaciones ?? []).map((r) => [r.empleado_id, r.neto]));
    const lineas = empleadoIds.map((id) => `${nombrePorId.get(id) ?? 'Empleado'}: ${formatMonto(netoPorId.get(id))}.`);
    return lineas.length ? `${lineas.join(' ')} ` : '';
}

function handleLiquidacionConfirmarAbrir() {
    if (liquidacionMutationInFlight) return;
    if (!liquidacionUltimoRango || liquidacionPreview?.status !== 'ready') return;

    const n = liquidacionUltimoRango.empleadoIds.length;
    const plural = n === 1 ? 'de 1 empleado' : `de ${n} empleados`;
    const empleadosDetalle = construirEmpleadosParaConfirm(liquidacionUltimoRango.empleadoIds, liquidacionPreview.liquidaciones);
    const excedenteWarning = construirExcedenteWarningParaConfirm(liquidacionPreview.liquidaciones);
    const fijoWarning = construirAvisoSueldoFijoParaConfirm(liquidacionUltimoRango, liquidacionPreview.liquidaciones);
    const message = `${empleadosDetalle}${excedenteWarning}${fijoWarning}${LIQUIDACION_CONFIRM_IRREVERSIBLE_MSG} ${plural} para el período ${liquidacionUltimoRango.desde} a ${liquidacionUltimoRango.hasta}?${construirAvisoPagoPrevioParaConfirm(liquidacionUltimoRango.empleadoIds)}`;

    pendingLiquidacionConfirm = { ...liquidacionUltimoRango, message };
    renderLiquidacionConfirmMount(pendingLiquidacionConfirm);
}

/**
 * U3: a `tipo_pago = 'fijo'` employee is settled the FULL monthly `valor`
 * whatever the range (`payroll-policy.js`). Settling one on a range that is
 * not exactly one calendar month stays possible (the per-row "Liquidar"),
 * but the confirm dialog says so explicitly, with the real bruto from the
 * fresh preview and the real length of the range. `''` on a whole month.
 * Warns when EITHER source says fijo: the `tableroCache` row (same source as
 * the bulk exclusion, see `loteLiquidable`) or the fresh preview's
 * `detalle.modo`. A stale cached 'hora' must never hide a fresh 'fijo'
 * (money guard: fail closed). The amount always comes from the fresh
 * preview. Never from `liquidacionViewModel` alone, which is `null` when its
 * own load failed and would silently drop the warning.
 * @param {{ empleadoIds: string[], desde: string, hasta: string }} rango
 * @param {Array<object>} liquidaciones preview rows
 * @returns {string} prefix to add to the dialog message, or `''`
 */
function construirAvisoSueldoFijoParaConfirm(rango, liquidaciones) {
    if (esMesCompleto(rango.desde, rango.hasta)) return '';
    const nombrePorId = new Map((liquidacionViewModel?.empleados ?? []).map((e) => [e.id, e.nombre]));
    const previewPorId = new Map((liquidaciones ?? []).map((r) => [r.empleado_id, r]));
    const dias = diasDelPeriodo(rango.desde, rango.hasta);
    const lineas = rango.empleadoIds
        .filter((id) => tableroCache?.filas?.get(id)?.tipoPago === 'fijo' || previewPorId.get(id)?.detalle?.modo === 'fijo')
        .map((id) => {
            const nombre = tableroCache?.filas?.get(id)?.nombre ?? nombrePorId.get(id) ?? 'Empleado';
            return `Atención: ${nombre} cobra el sueldo fijo completo (${formatMonto(previewPorId.get(id)?.bruto)}) aunque el período sea de ${dias} días.`;
        });
    return lineas.length ? `${lineas.join(' ')} ` : '';
}

/**
 * E1.3/D3.5 fix (correction review-e2599502b715d72e — restaura
 * `renderExcedenteWarning`, retirado con `renderPreviewSection`; spec
 * `employee-discount-cap`): el excedente ya está sumado a `consumos`/`neto`
 * server-side (D3) — esto sólo lo hace visible, ANTES del `ConfirmDialog`.
 * @param {Array<object>} liquidaciones
 * @returns {string} prefijo con el excedente por empleado, o `''`.
 */
function construirExcedenteWarningParaConfirm(liquidaciones) {
    const nombrePorId = new Map((liquidacionViewModel?.empleados ?? []).map((e) => [e.id, e.nombre]));
    const lineas = (liquidaciones ?? [])
        .filter((r) => (r.detalle?.descuento_consumos?.excedente_total ?? 0) > 0)
        .map((r) => `${nombrePorId.get(r.empleado_id) ?? 'Empleado'} recupera un excedente del tope de descuento de ${formatPrice(r.detalle.descuento_consumos.excedente_total)}.`);
    return lineas.length ? `${lineas.join(' ')} ` : '';
}

/**
 * Renderiza (o limpia) el diálogo de confirmación de liquidación pendiente.
 * Reutiliza el `ConfirmDialog` genérico — sus hooks fijos (`reserva-confirm`/
 * `reserva-dismiss`) se cablean acá a los handlers de liquidación, mismo
 * patrón que `renderBlockConfirmMount` para el borrado de bloqueos.
 * @param {{ message: string } | null} [pending]
 */
function renderLiquidacionConfirmMount(pending = pendingLiquidacionConfirm) {
    const mount = getLiquidacionConfirmMount();
    if (!mount) return;

    if (!pending) {
        mount.innerHTML = '';
        return;
    }

    mount.innerHTML = ConfirmDialog({ id: 'liquidacion-batch', action: 'confirmar-liquidacion', message: pending.message });
    const dismissBtn = mount.querySelector('[data-action="reserva-dismiss"]');
    if (dismissBtn) dismissBtn.addEventListener('click', handleLiquidacionConfirmDismiss);
    const confirmBtn = mount.querySelector('[data-action="reserva-confirm"]');
    if (confirmBtn) confirmBtn.addEventListener('click', () => handleLiquidacionConfirmConfirmar(confirmBtn));
}

function handleLiquidacionConfirmDismiss() {
    pendingLiquidacionConfirm = null;
    renderLiquidacionConfirmMount(null);
}

/**
 * Confirma la liquidación pendiente. Requisito 5: un rechazo parcial
 * (`kind: 'rechazo_parcial'` — confirm de varios empleados donde uno fue
 * rechazado por `create_liquidacion`, p.ej. `periodo_duplicado`) NO se
 * muestra como un `showToast` de error genérico: se guarda en
 * `liquidacionConfirmResult` con `status: 'parcial'` para que
 * `LiquidacionView` liste EXACTAMENTE qué quedó escrito (con su id) y qué
 * no — nunca un "falló" que invite a reintentar el batch entero y duplicar
 * lo que ya se guardó (api/liquidacion.js documenta esta misma limitación
 * en su propio header: el confirm de varios empleados es secuencial y sin
 * transacción cruzada). El preview se limpia siempre que la respuesta
 * llegue: ya no es válido, haya persistido todo, nada, o una parte.
 * @param {HTMLButtonElement | null} [confirmBtn]
 */
async function handleLiquidacionConfirmConfirmar(confirmBtn) {
    if (liquidacionMutationInFlight) return;
    const pending = pendingLiquidacionConfirm;
    if (!pending) return;

    // R4-002: ver el comentario de cabecera de `liquidacionConfirmSeq`.
    const seq = ++liquidacionConfirmSeq;
    liquidacionMutationInFlight = true;
    if (confirmBtn) confirmBtn.disabled = true;
    try {
        const result = await confirmLiquidacion(pending);
        if (seq !== liquidacionConfirmSeq || !getLiquidacionConfirmMount()) return;

        pendingLiquidacionConfirm = null;
        renderLiquidacionConfirmMount(null);

        if (!getLiquidacionMount()) return;
        liquidacionPreview = null;

        if (result.ok) {
            liquidacionConfirmResult = { status: 'ok', liquidaciones: result.liquidaciones };
            showToast('Liquidación confirmada.', { level: 'success' });
            void refreshLiquidacionHistorial(); // E2: la liquidación nueva vuelve stale al historial mostrado.
            // S7 (design.md AD5): "Settlement confirmed... Full recompute" —
            // vale sin importar si el confirm se disparó desde el selector
            // legacy o desde el tablero (AD1: una sola pantalla de Liquidación).
            if (getTableroMount()) void recomputeTablero();
        } else if (result.kind === 'anomalias') {
            // R2: el gate de anomalías vuelve a correr en CADA acción
            // server-side, y "abierta" depende del reloj — un turno que
            // estaba bien en el preview puede volverse anómalo para cuando
            // el admin confirma. S8 fix (coordinator instruction, closes the
            // risk flagged in apply-progress.md "Unit S8"): ya NO mapea a
            // `liquidacionPreview` — `renderPreviewSection`, lo único que
            // alguna vez lo pintaba, está retirado, así que esto quedaba sin
            // feedback visible. Usa el MISMO panel de resultado de confirm
            // que `rechazo_parcial`/`incierto`, nombrando a los empleados
            // afectados.
            const nombrePorId = new Map((liquidacionViewModel?.empleados ?? []).map((e) => [e.id, e.nombre]));
            liquidacionConfirmResult = {
                status: 'anomalias',
                nombres: (result.anomalias ?? []).map((a) => nombrePorId.get(a.empleado_id) ?? 'Empleado'),
            };
        } else if (result.kind === 'rechazo_parcial') {
            liquidacionConfirmResult = {
                status: 'parcial',
                persistidas: result.liquidacionesPersistidas ?? [],
                empleadoId: result.empleadoId,
                mensaje: result.mensaje,
                errorCode: result.errorCode, // distingue periodo_duplicado de un rechazo real
                noIntentados: result.empleadosNoIntentados ?? [],
            };
            // F2: un rechazo parcial también deja liquidaciones NUEVAS ya
            // persistidas (`liquidaciones_persistidas`) — el historial
            // mostrado queda stale igual que en la rama `ok`, no sólo ahí.
            if ((result.liquidacionesPersistidas ?? []).length > 0) {
                void refreshLiquidacionHistorial();
                // S7 (design.md AD5): mismo criterio que la rama `ok` — lo
                // que SÍ quedó escrito también invalida las cifras del tablero.
                if (getTableroMount()) void recomputeTablero();
            }
        } else if (result.kind === 'tope_no_configurado' || result.kind === 'precondicion_bloqueante') {
            // D2 correction, follow-ups C2/C3: ninguno de los dos persiste
            // nada (corren antes de fase 3) — nunca 'status: parcial', que
            // implicaría un guardado a mitad de camino que acá no ocurrió.
            liquidacionConfirmResult = { status: result.kind, mensaje: result.mensaje, detalle: result.detalle };
        } else if (result.kind === 'incierto') {
            // Transporte cortado o 5xx no reconocido en CONFIRM — nunca se
            // muestra como el error genérico de "probá de nuevo". Owner
            // decision: pudo haber escrito, así que el estado real se trae
            // solo (historial + tablero). El diálogo ya se cerró y el preview
            // ya se limpió arriba: nada queda armado para reenviar a ciegas.
            liquidacionConfirmResult = { status: 'incierto', mensaje: result.mensaje };
            void refreshLiquidacionHistorial();
            if (getTableroMount()) void recomputeTablero();
        } else {
            liquidacionConfirmResult = { status: 'error', mensaje: result.mensaje || LIQUIDACION_GENERIC_ERROR_MSG };
        }
        renderLiquidacionMount();
    } catch (err) {
        if (seq !== liquidacionConfirmSeq) return; // R4-002: sesión vieja, no pisar la nueva
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleLiquidacionConfirmConfirmar: no se pudo confirmar la liquidación', err);
        if (getLiquidacionConfirmMount()) {
            pendingLiquidacionConfirm = null;
            renderLiquidacionConfirmMount(null);
        }
        if (getLiquidacionMount()) {
            // W2: una excepción no dice si algo llegó a escribirse
            // (requisito 5) — mismo trato que la rama `incierto`: aviso de
            // advertencia, el preview ya no vale y el estado real se trae
            // solo (historial + tablero). Nada queda armado para reenviar.
            liquidacionPreview = null;
            liquidacionConfirmResult = { status: 'incierto', mensaje: LIQUIDACION_INCIERTO_EXCEPCION_MSG };
            renderLiquidacionMount();
            void refreshLiquidacionHistorial();
            if (getTableroMount()) void recomputeTablero();
        }
    } finally {
        if (seq === liquidacionConfirmSeq) liquidacionMutationInFlight = false;
        if (confirmBtn?.isConnected) confirmBtn.disabled = false;
        // S8 fix (coordinator instruction): repinta el tablero DESPUÉS de
        // bajar `liquidacionMutationInFlight` — un rechazo en confirm que
        // no dispara `recomputeTablero` (anomalias/fudo_bloqueado/
        // tope_no_configurado/precondicion_bloqueante/error) nunca debe
        // dejar "Liquidar"/"Liquidar N listos" reflejando el `disabled` de
        // cuando el confirm seguía en vuelo. `incierto` y una excepción SÍ
        // disparan `recomputeTablero` (arriba); este repintado igual corre y
        // es inocuo. Repintado puro, NUNCA otro `previewLiquidacion` — eso
        // sigue siendo trabajo exclusivo de `recomputeTablero`.
        if (seq === liquidacionConfirmSeq && getTableroMount()) renderTableroMount();
    }
}

/**
 * Abre el `AnularLiquidacionDialog` (Unit B2, design.md AD12) para la fila
 * clicada del historial — lee `data-id` y busca la fila en
 * `liquidacionHistorial` (ya cargado) para prellenar el nombre/período que
 * el diálogo muestra. `anularLiquidacion` recién se llama al enviar el
 * form (`handleLiquidacionAnularSubmit`), nunca acá.
 * @param {MouseEvent} e
 */
function handleLiquidacionAnularClick(e) {
    if (liquidacionAnularInFlight) return;
    const { id } = e.currentTarget.dataset;
    const row = (liquidacionHistorial?.liquidaciones ?? []).find((r) => r.id === id);
    pendingLiquidacionAnular = {
        liquidacionId: id,
        empleadoNombre: row?.empleados?.nombre ?? '',
        periodo: row ? `${row.periodo_desde ?? ''} a ${row.periodo_hasta ?? ''}` : '',
    };
    renderLiquidacionAnularMount(pendingLiquidacionAnular);
}

/**
 * Renderiza (o limpia) el mount SEPARADO del `AnularLiquidacionDialog` —
 * mismo criterio que `renderLiquidacionConfirmMount`: abrir/cerrar este
 * diálogo, o repintarlo con un error de submit, nunca toca el mount
 * principal (selector + historial), así que un motivo a medio escribir en
 * OTRO lugar de la pantalla nunca se pierde por esto. Un `keydown` Escape
 * cableado sobre el `[role="dialog"]` recién creado (no sobre el mount
 * persistente — así no se acumulan listeners entre aperturas sucesivas)
 * cierra el diálogo sin llamar a `anularLiquidacion`.
 * @param {{ liquidacionId: string, empleadoNombre?: string, periodo?: string, error?: string } | null} [pending]
 */
function renderLiquidacionAnularMount(pending = pendingLiquidacionAnular) {
    const mount = getLiquidacionAnularMount();
    if (!mount) return;

    if (!pending) {
        mount.innerHTML = '';
        return;
    }

    mount.innerHTML = AnularLiquidacionDialog(pending);
    const dialogEl = mount.querySelector('[role="dialog"]');
    if (dialogEl) dialogEl.addEventListener('keydown', handleLiquidacionAnularKeydown);
    const cancelBtn = mount.querySelector('[data-action="liquidacion-anular-cancelar"]');
    if (cancelBtn) cancelBtn.addEventListener('click', handleLiquidacionAnularCancelar);
    const form = mount.querySelector('[data-action="liquidacion-anular-confirmar"]')?.closest('form');
    if (form) form.addEventListener('submit', handleLiquidacionAnularSubmit);
}

/** Escape cierra el diálogo de anulación, igual que el botón Cancelar. @param {KeyboardEvent} e */
function handleLiquidacionAnularKeydown(e) {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    handleLiquidacionAnularCancelar();
}

function handleLiquidacionAnularCancelar() {
    // review-ba2b1ddf305cb909: closing the dialog must also orphan any submit
    // still in flight, otherwise its late validacion/incierto response would
    // resurrect a dialog the admin already dismissed. Bumping the seq makes
    // the post-await guard in handleLiquidacionAnularSubmit drop it, and
    // releasing the in-flight flag lets the next open submit again.
    liquidacionAnularSeq += 1;
    liquidacionAnularInFlight = false;
    pendingLiquidacionAnular = null;
    renderLiquidacionAnularMount(null);
}

/**
 * Envía la anulación pendiente (Unit B2, design.md AD3/AD12). Un rechazo
 * `validacion`/`error` reabre el diálogo con el mensaje inline (mismo
 * patrón que `handleFichadaDialogGuardar`) — nunca lo cierra, para que el
 * admin pueda leer por qué y decidir; un rechazo `auth` sí lo cierra (la
 * sesión ya no es válida, no tiene sentido seguir mostrando un form que va
 * a volver a fallar) y avisa por toast. Un `incierto` — o una excepción,
 * igual de ambigua — cierra el diálogo (nunca queda armado para reenviar a
 * ciegas), avisa por toast de advertencia, deja un aviso fijo en el mount
 * (`liquidacionAnularIncierto`) hasta que el historial recargue bien, y
 * refresca historial + tablero como un éxito. Un éxito refresca el historial
 * (que también trae el panel de consumo del tope — ambos derivan de la
 * MISMA lectura, `renderMesConsumoPanel`/`renderHistorialPanel` en
 * `LiquidacionView.js`) — nunca hace falta un refresh separado para el
 * panel de consumo.
 * @param {SubmitEvent} e
 */
async function handleLiquidacionAnularSubmit(e) {
    e.preventDefault();
    if (liquidacionAnularInFlight) return;
    const pending = pendingLiquidacionAnular;
    if (!pending) return;

    const form = e.currentTarget;
    const motivo = form.querySelector('[name="motivo"]')?.value ?? '';
    const submitBtn = form.querySelector('[data-action="liquidacion-anular-confirmar"]');
    // The textarea is `required`, but a programmatic submit bypasses native
    // validation: never send an empty motivo to the service or the API.
    if (!motivo.trim()) {
        pendingLiquidacionAnular = { ...pending, error: 'Ingresá un motivo.' };
        renderLiquidacionAnularMount(pendingLiquidacionAnular);
        return;
    }

    const seq = ++liquidacionAnularSeq;
    liquidacionAnularInFlight = true;
    if (submitBtn) submitBtn.disabled = true;
    try {
        const result = await anularLiquidacion({ liquidacionId: pending.liquidacionId, motivo });
        // Stale if a newer submit started, the dialog was cancelled (seq bump),
        // the section was torn down, or the dialog now belongs to another row
        // (identity check, same idea as sigueSiendoMiDialogo in the fichada
        // dialog): in every case this response must not repaint anything.
        if (seq !== liquidacionAnularSeq || !getLiquidacionAnularMount()) return;
        if (pendingLiquidacionAnular !== pending) return;

        if (!result.ok) {
            if (result.kind === 'auth') {
                pendingLiquidacionAnular = null;
                renderLiquidacionAnularMount(null);
                showToast(result.mensaje, { level: 'error' });
                return;
            }
            if (result.kind === 'incierto') {
                // Owner decision: la anulación pudo haberse aplicado. El
                // diálogo se cierra (nunca queda armado para reenviar a
                // ciegas), el aviso sale como advertencia y el estado real
                // se trae solo — mismo refresco que un éxito.
                marcarAnulacionIncierta(result.mensaje);
                return;
            }
            // validacion | error: el diálogo se queda abierto con
            // el mensaje adentro — nunca se cierra ni se pierde el intento.
            pendingLiquidacionAnular = { ...pending, error: result.mensaje };
            renderLiquidacionAnularMount(pendingLiquidacionAnular);
            return;
        }

        pendingLiquidacionAnular = null;
        renderLiquidacionAnularMount(null);
        showToast(LIQUIDACION_ANULAR_SUCCESS_MSG, { level: 'success' });
        if (getLiquidacionMount()) void refreshLiquidacionHistorial();
        // L3: mismo criterio que un confirm (S7, AD5) — la fila "Liquidada"
        // del tablero deja de estarlo. `recomputeTablero` espera la carga del
        // historial recién disparada (`liquidacionHistorialPromise`) antes de
        // clasificar, así que ve la fila ya anulada.
        if (getTableroMount()) void recomputeTablero();
    } catch (err) {
        if (seq !== liquidacionAnularSeq) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleLiquidacionAnularSubmit: no se pudo anular la liquidación', err);
        // W2: una excepción es tan ambigua como un `incierto` — nunca el
        // "Probá de nuevo" con el diálogo armado.
        if (getLiquidacionAnularMount() && pendingLiquidacionAnular === pending) {
            marcarAnulacionIncierta(LIQUIDACION_INCIERTO_EXCEPCION_MSG);
        }
    } finally {
        if (seq === liquidacionAnularSeq) liquidacionAnularInFlight = false;
        if (submitBtn?.isConnected) submitBtn.disabled = false;
    }
}

/**
 * Anulación de resultado desconocido (`incierto` o excepción): cierra el
 * diálogo, avisa por toast de advertencia, deja el aviso fijo
 * `liquidacionAnularIncierto` en el mount (W1) y trae el estado real
 * (historial + tablero), igual que un éxito.
 * @param {string} mensaje
 */
function marcarAnulacionIncierta(mensaje) {
    pendingLiquidacionAnular = null;
    renderLiquidacionAnularMount(null);
    showToast(mensaje, { level: 'warning' });
    if (getLiquidacionMount()) {
        liquidacionAnularIncierto = mensaje;
        renderLiquidacionMount();
        void refreshLiquidacionHistorial();
    }
    if (getTableroMount()) void recomputeTablero();
}

/**
 * W1 — "Reintentar" del estado de error del historial: vuelve a mostrar
 * "Cargando historial..." y relanza `refreshLiquidacionHistorial` (que nunca
 * rechaza; un nuevo fallo vuelve a pintar el error con su botón).
 */
function handleLiquidacionHistorialRetry() {
    liquidacionHistorial = { cargando: true, error: null, liquidaciones: [] };
    renderLiquidacionMount();
    void refreshLiquidacionHistorial();
}

/**
 * Unit A2 (design.md AD5/AD6, spec "Comprobante Availability") — abre el
 * comprobante de la fila clicada del historial: lee `data-id`, busca la fila
 * YA CARGADA en `liquidacionHistorial` (nunca dispara un fetch — el requisito
 * explícito de la spec es "no new read path") y la pasa a
 * `openLiquidacionComprobante`. Un id que ya no está en memoria (historial
 * refrescado por otra pestaña mientras este click estaba en curso) es un
 * no-op silencioso, no un error.
 * @param {MouseEvent} e
 */
function handleLiquidacionComprobanteClick(e) {
    const { id } = e.currentTarget.dataset;
    const row = (liquidacionHistorial?.liquidaciones ?? []).find((r) => r.id === id);
    if (!row) return;
    openLiquidacionComprobante(row, id);
}

/**
 * Escape cierra el comprobante exactamente como "Volver" (spec "Open, Print
 * and Close Flow"). Listener a nivel `document` (design.md AD10 — el
 * comprobante es una vista de página completa, no un modal con foco
 * atrapado), registrado en `openLiquidacionComprobante` y removido en
 * `teardownLiquidacionComprobante` — nunca se agrega una segunda vez para la
 * misma sesión porque `openLiquidacionComprobante` siempre pasa esta MISMA
 * referencia de función a `addEventListener` (el dedup nativo del DOM hace
 * el resto).
 * @param {KeyboardEvent} e
 */
function handleLiquidacionComprobanteKeydown(e) {
    if (e.key !== 'Escape') return;
    closeLiquidacionComprobante();
}

/**
 * Escribe (o reemplaza) el único aviso de error visible dentro del mount del
 * comprobante — lo usan tanto un build que tira excepción como un Imprimir
 * fallido (review-f32bdf4ca1d3ee12): una falla repetida reemplaza el mensaje
 * anterior en vez de apilar párrafos nuevos en cada click.
 * @param {HTMLElement} mount
 * @param {string} mensaje
 */
function renderLiquidacionComprobanteAviso(mount, mensaje) {
    const html = `<p class="text-sm text-red-700" role="alert" data-comprobante-aviso data-comprobante-noprint>${mensaje}</p>`;
    const aviso = mount.querySelector('[data-comprobante-aviso]');
    if (aviso) {
        aviso.outerHTML = html;
    } else {
        mount.insertAdjacentHTML('beforeend', html);
    }
}

/**
 * Renderiza el comprobante de `row` en su mount SEPARADO (AD5), oculta (no
 * desmonta, AD6) el mount principal del historial/selector, fija
 * `document.title` (AD8) y arma el listener de Escape (AD10). Llamar a esto
 * con un comprobante YA abierto lo REEMPLAZA (otra fila clicada sin cerrar
 * antes): el título "anterior" sólo se captura la PRIMERA vez para no perder
 * el título real de la pestaña detrás de un reemplazo encadenado.
 * @param {object} row fila de `liquidacionHistorial.liquidaciones`
 * @param {string} id `row.id`, guardado para volver a ubicar el botón de origen al cerrar
 */
function openLiquidacionComprobante(row, id) {
    const mount = getLiquidacionComprobanteMount();
    if (!mount) return;

    // review-62957fd6e8912f8e: un print en lote dejado abierto (afterprint
    // no disparó, p.ej. Escape cerró el diálogo) nunca debe imprimir junto a
    // este comprobante individual.
    teardownLiquidacionComprobantesLote();
    const loteMount = getTableroComprobantesLoteMount();
    if (loteMount) loteMount.innerHTML = '';

    // review-ae437ea91679bca9: build BEFORE mutating any state, so a throwing
    // build (malformed legacy detalle) leaves title, body class, listener and
    // the "already open" flag exactly as they were.
    let vm;
    let html;
    try {
        vm = construirComprobante(row);
        html = ComprobanteLiquidacionView(vm);
    } catch (err) {
        window.Sentry?.captureException(err);
        // review-f32bdf4ca1d3ee12: si ya había uno abierto, cerrarlo primero
        // deja título/clase/mount principal consistentes y el historial
        // visible detrás del aviso, en vez de pisar el mount con un error
        // suelto y dejar el resto del estado del comprobante anterior colgado.
        if (liquidacionComprobanteAbierto !== null) closeLiquidacionComprobante();
        mount.innerHTML = '';
        renderLiquidacionComprobanteAviso(mount, 'No se pudo generar el comprobante de esta liquidación.');
        return;
    }

    const yaHabiaUnoAbierto = liquidacionComprobanteAbierto !== null;
    liquidacionComprobanteAbierto = row;
    liquidacionComprobanteOrigenId = id;
    mount.innerHTML = html;

    if (!yaHabiaUnoAbierto) liquidacionComprobantePreviousTitle = document.title;
    document.title = `Comprobante de liquidación - ${vm.empleadoNombre} - ${vm.periodoDesde} a ${vm.periodoHasta}`;
    document.body.classList.add('comprobante-abierto');
    getLiquidacionMount()?.classList.add('hidden');
    document.addEventListener('keydown', handleLiquidacionComprobanteKeydown);

    const imprimirBtn = mount.querySelector('[data-action="liquidacion-comprobante-imprimir"]');
    if (imprimirBtn) {
        imprimirBtn.addEventListener('click', () => {
            try {
                window.print();
            } catch (err) {
                // Some embedded browsers block print(); say so instead of
                // failing silently (review-ae437ea91679bca9). Shares the same
                // replaceable aviso as a throwing build (review-f32bdf4ca1d3ee12)
                // so repeated failures don't stack duplicate messages.
                window.Sentry?.captureException(err);
                renderLiquidacionComprobanteAviso(mount, 'No se pudo abrir el diálogo de impresión. Probá con Ctrl+P.');
            }
        });
    }
    const volverBtn = mount.querySelector('[data-action="liquidacion-comprobante-volver"]');
    if (volverBtn) volverBtn.addEventListener('click', closeLiquidacionComprobante);

    // AD10: no es un modal (sin `aria-modal`, sin trampa de foco), pero SÍ
    // recibe foco al abrir — `role="document"` no es enfocable por defecto,
    // así que se le agrega `tabindex` acá en vez de en `ComprobanteLiquidacionView.js`
    // (esa vista sigue siendo puro HTML, sin conocimiento de foco).
    const documentoEl = mount.querySelector('[role="document"]');
    if (documentoEl) {
        documentoEl.setAttribute('tabindex', '-1');
        documentoEl.focus();
    }
}

/**
 * Cierra el comprobante: comparte el teardown de estado con
 * `teardownLiquidacionComprobante` (también usado por `mountLiquidacionPanel`,
 * AD9) y además limpia el mount, quita el `hidden` del mount principal y
 * devuelve el foco al botón "Comprobante" de la fila de origen SI todavía
 * existe en el DOM (design.md AD6 — puede haber desaparecido si un refresh de
 * fondo lo quitó del historial mientras el comprobante estaba abierto).
 * No-op si no hay nada abierto.
 */
function closeLiquidacionComprobante() {
    if (!liquidacionComprobanteAbierto) return;
    const origenId = liquidacionComprobanteOrigenId;
    teardownLiquidacionComprobante();

    getLiquidacionMount()?.classList.remove('hidden');
    const mount = getLiquidacionComprobanteMount();
    if (mount) mount.innerHTML = '';

    // Match on dataset instead of interpolating the id into a selector
    // (review-ae437ea91679bca9: no selector-escaping needed this way).
    const origenBtn = origenId
        ? Array.from(getLiquidacionMount()?.querySelectorAll('[data-action="liquidacion-comprobante"]') ?? []).find(
              (btn) => btn.dataset.id === origenId,
          )
        : null;
    if (origenBtn) origenBtn.focus();
}

/**
 * Estado compartido por `closeLiquidacionComprobante` (Volver/Escape) y por
 * `mountLiquidacionPanel` (tab change/re-montaje, design.md AD9): restaura
 * `document.title`, quita la clase `comprobante-abierto` del body y el
 * listener de Escape a nivel `document`, y limpia `liquidacionComprobanteAbierto`/
 * `liquidacionComprobanteOrigenId`. Idempotente — no hace nada si ya estaba
 * cerrado. Deliberadamente NO toca el mount ni el foco: `mountLiquidacionPanel`
 * ya tiene mounts nuevos y vacíos en ese punto, y `closeLiquidacionComprobante`
 * maneja esa parte por su cuenta.
 */
function teardownLiquidacionComprobante() {
    document.removeEventListener('keydown', handleLiquidacionComprobanteKeydown);
    document.body.classList.remove('comprobante-abierto');
    if (liquidacionComprobantePreviousTitle !== null) {
        document.title = liquidacionComprobantePreviousTitle;
        liquidacionComprobantePreviousTitle = null;
    }
    liquidacionComprobanteAbierto = null;
    liquidacionComprobanteOrigenId = null;
}

/**
 * S7 (design.md AD16): limpia la clase de body del print en lote — mismo
 * criterio que `teardownLiquidacionComprobante`, pero sin listener de
 * Escape ni `document.title` que restaurar: el print en lote no es un panel
 * interactivo (dispara `window.print()` y termina ahí, sin "Volver"). El
 * mount queda `display: none` en pantalla siempre (styles.src.css) y se
 * limpia con `afterprint`/al abrir un comprobante individual/al empezar un
 * print en lote nuevo — no "sin botones Volver que limpiar", como decía
 * antes esta misma nota (review-62957fd6e8912f8e).
 */
function teardownLiquidacionComprobantesLote() {
    document.body.classList.remove('comprobantes-lote-abierto');
}

// ---------------------------------------------------------------------------
// Configuración — sección de la rail "Configuración" (toggle de publi)
// ---------------------------------------------------------------------------

/** @returns {HTMLElement | null} */
function getConfigMount() {
    return DOM.adminContainer?.querySelector('#admin-config-mount') ?? null;
}

/**
 * Monta la sección de configuración dentro del content mount y dispara la
 * primera carga del flag de publi.
 * @param {HTMLElement} mount
 */
function mountConfigSection(mount) {
    mount.innerHTML = '<div id="admin-config-mount"></div>';

    publiViewModel = null;
    publiToggleInFlight = false;
    publiUiState = { status: 'loading' };

    renderConfigMount();
    void refreshPubli();
}

/**
 * Renderiza el ui-state actual (loading/error/ready) dentro del mount de
 * configuración y re-cablea sus listeners.
 */
function renderConfigMount() {
    const mount = getConfigMount();
    if (!mount) return;

    if (publiUiState.status === 'loading') {
        mount.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando configuración...</p>
            </div>
        `;
        return;
    }

    if (publiUiState.status === 'error') {
        mount.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${PUBLI_LOAD_ERROR_MSG}</p>
                <button data-action="publi-retry"
                    class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">
                    Reintentar
                </button>
            </div>
        `;
        const retryBtn = mount.querySelector('[data-action="publi-retry"]');
        if (retryBtn) retryBtn.addEventListener('click', () => void refreshPubli());
        return;
    }

    const enabled = publiViewModel?.enabled ?? false;
    mount.innerHTML = `
        <section aria-label="Configuración del sitio" class="max-w-3xl">
            <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2">Configuración</span>
            <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary mb-8">Ajustes del sitio</h2>
            <h3 class="text-sm font-black uppercase tracking-widest text-text-primary mb-1">Publicidad</h3>
            <p class="text-xs text-text-muted mb-4 max-w-prose">
                Controla si el banner de publicidad se muestra en el sitio público. Los cambios se aplican de inmediato.
            </p>
            <div class="bg-surface border border-border-subtle rounded-2xl px-5">
                ${PubliToggle(enabled)}
            </div>
        </section>
    `;
    bindConfigMountListeners(mount);
}

/**
 * Cablea el listener del toggle de publi dentro del mount de configuración.
 * @param {HTMLElement} mount
 */
function bindConfigMountListeners(mount) {
    const toggleBtn = mount.querySelector('[data-action="publi-toggle"]');
    if (toggleBtn) toggleBtn.addEventListener('click', () => handlePubliToggle(toggleBtn));
}

/**
 * Recarga el flag de publi y re-renderiza: `loading` / `ready` / `error`.
 */
async function refreshPubli() {
    publiUiState = { status: 'loading' };
    renderConfigMount();

    try {
        const model = await conTimeoutDeCarga(loadPubli());
        if (!getConfigMount()) return;
        publiViewModel = model;
        publiUiState = { status: 'ready' };
    } catch (err) {
        if (!getConfigMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshPubli: no se pudo cargar la configuración', err);
        publiUiState = { status: 'error' };
    }

    renderConfigMount();
}

/**
 * Cambia el flag de publi al valor `data-next` del botón. Re-entrancy: ignora
 * el doble click y deshabilita el botón mientras dura. Guard de resolución: si
 * la sección de configuración ya no está montada, descarta los efectos.
 * @param {HTMLButtonElement | null} [toggleBtn]
 */
async function handlePubliToggle(toggleBtn) {
    if (publiToggleInFlight) return;
    const next = toggleBtn?.dataset.next === 'true';

    publiToggleInFlight = true;
    if (toggleBtn) toggleBtn.disabled = true;
    try {
        const model = await togglePubli(next);
        if (!getConfigMount()) return;
        publiViewModel = model;
        publiUiState = { status: 'ready' };
        renderConfigMount();
        showToast(next ? 'Publicidad activada.' : 'Publicidad desactivada.', { level: 'success' });
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handlePubliToggle: no se pudo actualizar la publicidad', err);
        if (!getConfigMount()) return;
        showToast(PUBLI_TOGGLE_ERROR_MSG, { level: 'error' });
    } finally {
        publiToggleInFlight = false;
    }
}

// ---------------------------------------------------------------------------
// Categorías — sección de la rail "Categorías" (reglas de orden por categoría)
// ---------------------------------------------------------------------------

/** Copy Spanish de la sección de categorías. */
const CATEGORIES_LOAD_ERROR_MSG = 'No pudimos cargar las categorías. Probá de nuevo.';
const CATEGORIES_SAVE_ERROR_MSG = 'No pudimos guardar la regla. Probá de nuevo.';
const CATEGORIES_SAVE_SUCCESS_MSG = 'Regla actualizada.';

/** @returns {HTMLElement | null} */
function getCategoriesMount() {
    return DOM.adminContainer?.querySelector('#admin-categories-mount') ?? null;
}

/**
 * Monta la sección de categorías dentro del content mount y dispara la
 * primera carga.
 * @param {HTMLElement} mount
 */
function mountCategoriesSection(mount) {
    mount.innerHTML = '<div id="admin-categories-mount"></div>';

    categoriesViewModel = null;
    categoryRulesMap = null;
    categoryRuleMutationInFlight = false;
    categoriesUiState = { status: 'loading' };

    renderCategoriesMount();
    void refreshCategories();
}

/**
 * Renderiza el ui-state actual (loading/error/ready) dentro del mount de
 * categorías y re-cablea sus listeners.
 */
function renderCategoriesMount() {
    const mount = getCategoriesMount();
    if (!mount) return;

    if (categoriesUiState.status === 'loading') {
        mount.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando categorías...</p>
            </div>
        `;
        return;
    }

    if (categoriesUiState.status === 'error') {
        mount.innerHTML = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${CATEGORIES_LOAD_ERROR_MSG}</p>
                <button data-action="cat-retry"
                    class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">
                    Reintentar
                </button>
            </div>
        `;
        const retryBtn = mount.querySelector('[data-action="cat-retry"]');
        if (retryBtn) retryBtn.addEventListener('click', () => void refreshCategories());
        return;
    }

    mount.innerHTML = CategoriesView({
        categories: categoriesViewModel ?? [],
        rules: categoryRulesMap ?? {},
    });
    bindCategoriesMountListeners(mount);
}

/**
 * Cablea los listeners de los dropdowns de cambio de regla dentro del mount.
 * @param {HTMLElement} mount
 */
function bindCategoriesMountListeners(mount) {
    for (const select of mount.querySelectorAll('[data-action="cat-rule-change"]')) {
        select.addEventListener('change', (e) => {
            void handleCategoryRuleChange(e.currentTarget);
        });
    }
}

/** Generación de carga de categorías: descarta una respuesta vieja (mismo patrón que `refreshEmpleadosSeq`). */
let refreshCategoriesSeq = 0;

/**
 * Recarga categorías (de Fudo GP) + reglas (de Supabase) y re-renderiza.
 */
async function refreshCategories() {
    const seq = ++refreshCategoriesSeq;
    categoriesUiState = { status: 'loading' };
    renderCategoriesMount();

    try {
        // C3/F2: the Supabase read races the section timeout; the Fudo GP
        // read races the longer Fudo limit, above `safeFetchClient`'s own
        // budget (see `conTimeoutDeCarga`).
        const [cats, rules] = await Promise.all([
            conTimeoutDeCarga(FudoGpRepository.getCategories(), FUDO_LOAD_TIMEOUT_MS),
            conTimeoutDeCarga(fetchCategoryRules()),
        ]);
        if (seq !== refreshCategoriesSeq || !getCategoriesMount()) return;
        categoriesViewModel = cats;
        categoryRulesMap = rules;
        categoriesUiState = { status: 'ready' };
    } catch (err) {
        if (seq !== refreshCategoriesSeq || !getCategoriesMount()) return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('refreshCategories: no se pudieron cargar las categorías', err);
        categoriesUiState = { status: 'error' };
    }

    renderCategoriesMount();
}

/**
 * Guarda un cambio de regla de categoría. Re-entrancy: ignora doble cambio.
 * @param {HTMLSelectElement} select
 */
async function handleCategoryRuleChange(select) {
    if (categoryRuleMutationInFlight) return;

    const categoryId = select.dataset.categoryId;
    const newEstado = select.value;
    const oldEstado = categoryRulesMap?.[categoryId] ?? 'both';

    if (newEstado === oldEstado) return;

    const cat = (categoriesViewModel ?? []).find(c => String(c.id) === String(categoryId));
    const nombre = cat?.name ?? categoryId;

    categoryRuleMutationInFlight = true;
    select.disabled = true;
    try {
        await upsertCategoryRule({
            fudo_category_id: categoryId,
            estado: newEstado,
            nombre,
        });
        if (!getCategoriesMount()) return;
        categoryRulesMap = { ...(categoryRulesMap ?? {}), [categoryId]: newEstado };
        renderCategoriesMount();
        showToast(CATEGORIES_SAVE_SUCCESS_MSG, { level: 'success' });
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('handleCategoryRuleChange: no se pudo guardar la regla', err);
        if (!getCategoriesMount()) return;
        showToast(CATEGORIES_SAVE_ERROR_MSG, { level: 'error' });
        // Restaurar el valor anterior en el select
        renderCategoriesMount();
    } finally {
        categoryRuleMutationInFlight = false;
        if (select.isConnected) select.disabled = false;
    }
}
